import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Briefcase,
  Building2,
  CheckCircle2,
  ClipboardList,
  Eye,
  FileStack,
  FileText,
  FileUp,
  FolderOpen,
  Landmark,
  Mail,
  Phone,
  Plus,
  Trash2,
  Upload,
  UserRound,
  X,
} from 'lucide-react';
import LaborStandaloneLayout from '../../components/labor/LaborStandaloneLayout';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import {
  claimsOptions,
  evidenceOptions,
  initialLaborIntakeData,
  laborIntakeSteps,
  noticesOptions,
  relationIndicators,
} from '../../features/labor-intake/laborIntakeSchema';
import {
  laborCommonOptionsByLanguage,
  laborOptionMapsEn,
  laborStepsEn,
  laborStepTitlesByLanguage,
} from '../../features/labor-intake/laborPortalCopy';
import {
  createLaborCase,
  getLaborIntakeDraft,
  saveLaborIntakeDraft,
  uploadLaborAttachments,
} from '../../features/labor-intake/laborIntakeService';
import { useLaborPortalLanguage } from '../../features/labor-intake/LaborPortalLanguageContext';

const iconMap = {
  client: UserRound,
  company: Building2,
  contract: FileText,
  employment: Briefcase,
  notices: Mail,
  summary: FileStack,
  attachments: Upload,
  declaration: CheckCircle2,
};

const sectionDefaults = JSON.parse(JSON.stringify(initialLaborIntakeData));
const createCustodyItem = () => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  type: '',
  otherDescription: '',
  conditionAtReceipt: 'لا أعلم',
  estimatedValue: '',
  receivedAt: '',
  hasReceipt: 'لا أعلم',
  receiptNumber: '',
  receiptAttachments: [],
  cashAmount: '',
  cashPurpose: '',
  hasRemainingCash: 'لا أعلم',
  remainingCashAmount: '',
  cashReturned: 'لا أعلم',
  cashReturnedAt: '',
  cashReceiverName: '',
  cashReceiverTitle: '',
  hasCashReturnDocument: 'لا أعلم',
  cashReturnDocumentNumber: '',
  cashReturnAttachments: [],
  returnedAtEnd: 'لا أعلم',
  returnedAt: '',
  receiverName: '',
  receiverTitle: '',
  hasReturnRecord: 'لا أعلم',
  returnRecordNumber: '',
  returnRecordAttachments: [],
  employerClaimedOrDeducted: 'لا أعلم',
  claimOrDeductionDetails: '',
});
const createAdvanceItem = () => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  amount: '',
  date: '',
  receiptMethod: '',
  receiptMethodOther: '',
  receiptBankName: '',
  transferDate: '',
  cashGiverName: '',
  cashGiverTitle: '',
  signedAdvanceReceipt: 'لا',
  repaid: 'لا أعلم',
  remainingBalance: '',
  repaymentMethod: '',
  firstDeductionDate: '',
  lastDeductionDate: '',
  repaymentTransferDate: '',
  repaymentBankName: '',
  repaymentTransferAmount: '',
  repaymentTransferAttachments: [],
  cashReceiverName: '',
  cashReceiverTitle: '',
  cashRepaymentDate: '',
  hasCashReceipt: 'لا',
  cashReceiptAttachments: [],
  employerClaimedOrDeducted: 'لا أعلم',
  claimOrDeductionDetails: '',
});

const mergeDraftWithDefaults = (draft) => ({
  ...sectionDefaults,
  ...draft,
  client: {
    ...sectionDefaults.client,
    ...(draft?.client || {}),
  },
  company: {
    ...sectionDefaults.company,
    ...(draft?.company || {}),
  },
  contract: {
    ...sectionDefaults.contract,
    ...(draft?.contract || {}),
  },
    employment: {
      ...sectionDefaults.employment,
      ...(draft?.employment || {}),
      hasCustodies: draft?.employment?.hasCustodies || (draft?.employment?.custodies?.length ? 'نعم' : 'لا'),
      custodies: draft?.employment?.custodies || [],
      hasAdvances: draft?.employment?.hasAdvances || (draft?.employment?.advances?.length ? 'نعم' : 'لا'),
      advances: draft?.employment?.advances || [],
    },
  compensation: {
    ...sectionDefaults.compensation,
    ...(draft?.compensation || {}),
    claims: (draft?.compensation?.claims || [])
      .map((claim) => (claim === 'شهادة الخبرة' ? 'شهادة الخدمة' : claim === 'عمولات أو حوافز' ? 'عمولات' : claim))
      .filter((claim) => claim !== 'نقل كفالة'),
    unpaidWagePeriods: draft?.compensation?.unpaidWagePeriods || [],
    leaveBalancePeriods: draft?.compensation?.leaveBalancePeriods || [],
    commissionClaims: draft?.compensation?.commissionClaims || [],
    damageClaim: {
      ...sectionDefaults.compensation.damageClaim,
      ...(draft?.compensation?.damageClaim || {}),
    },
  },
  benefits: {
    ...sectionDefaults.benefits,
    ...(draft?.benefits || {}),
  },
  notices: {
    ...sectionDefaults.notices,
    ...(draft?.notices || {}),
  },
  attachments: {
    ...sectionDefaults.attachments,
    ...(draft?.attachments || {}),
    items: draft?.attachments?.items || [],
  },
  summary: {
    ...sectionDefaults.summary,
    ...(draft?.summary || {}),
  },
  declaration: {
    ...sectionDefaults.declaration,
    ...(draft?.declaration || {}),
  },
  narrative: {
    ...sectionDefaults.narrative,
    ...(draft?.narrative || {}),
  },
});

const toDisplayDate = (value, locale = 'ar-SA') => {
  if (!value) return '';
  try {
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(new Date(value));
  } catch (error) {
    return '';
  }
};

const toHijriDate = (value, isArabic) => {
  if (!value) return '';
  try {
    return new Intl.DateTimeFormat(isArabic ? 'ar-SA-u-ca-islamic' : 'en-SA-u-ca-islamic', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    }).format(new Date(value));
  } catch (error) {
    return '';
  }
};

const digitsOnly = (value) => value.replace(/\D/g, '');
const toAmount = (value) => Number(String(value ?? '').replace(/,/g, '')) || 0;
const formatAmount = (value, isArabic) =>
  `${new Intl.NumberFormat(isArabic ? 'ar-SA' : 'en-US', { maximumFractionDigits: 2 }).format(toAmount(value))} ${isArabic ? 'ريال' : 'SAR'}`;
const createRowId = (prefix) => `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
const createUnpaidWagePeriod = () => ({
  id: createRowId('wage'),
  periodType: 'شهر كامل',
  month: '',
  fromDate: '',
  toDate: '',
  monthlyWage: '',
  dailyWage: '',
});
const createLeaveBalancePeriod = () => ({
  id: createRowId('leave'),
  days: '',
  dailyWage: '',
});
const createCommissionClaim = () => ({
  id: createRowId('commission'),
  description: '',
  entitlementPeriod: '',
  amount: '',
  hasEvidence: 'لا أعلم',
});
const inclusiveDays = (fromDate, toDate) => {
  if (!fromDate || !toDate) return 0;
  const difference = Math.floor((new Date(toDate) - new Date(fromDate)) / (24 * 60 * 60 * 1000));
  return difference >= 0 ? difference + 1 : 0;
};
const wagePeriodQuantity = (period) =>
  period.periodType === 'شهر كامل' ? (period.month ? 1 : 0) : inclusiveDays(period.fromDate, period.toDate);
const wagePeriodTotal = (period) =>
  wagePeriodQuantity(period) * toAmount(period.periodType === 'شهر كامل' ? period.monthlyWage : period.dailyWage);
const leavePeriodTotal = (period) => toAmount(period.days) * toAmount(period.dailyWage);

const ATTACHMENT_ALLOWED_EXTENSIONS = ['pdf', 'jpg', 'jpeg', 'png', 'docx', 'xlsx', 'zip'];
const ATTACHMENT_MAX_SIZE_BYTES = 10 * 1024 * 1024;

const attachmentLibrary = [
  {
    id: 'identity',
    labelAr: 'إثباتات الهوية',
    labelEn: 'Identity Proof',
    options: [
      { key: 'national_id', labelAr: 'الهوية الوطنية', labelEn: 'National ID' },
      { key: 'resident_id', labelAr: 'هوية مقيم', labelEn: 'Resident ID' },
      { key: 'national_address', labelAr: 'العنوان الوطني', labelEn: 'National Address' },
    ],
  },
  {
    id: 'contractual',
    labelAr: 'العلاقة التعاقدية',
    labelEn: 'Contractual Relationship',
    options: [
      { key: 'employment_contract', labelAr: 'عقد العمل', labelEn: 'Employment Contract' },
      { key: 'job_offer', labelAr: 'عرض وظيفي', labelEn: 'Job Offer' },
      { key: 'work_relationship_proof', labelAr: 'ما يثبت علاقة العمل', labelEn: 'Proof of Employment Relationship' },
    ],
  },
  {
    id: 'compensation',
    labelAr: 'الرواتب والمستحقات',
    labelEn: 'Salaries and Entitlements',
    options: [
      { key: 'bank_statement', labelAr: 'كشف حساب بنكي', labelEn: 'Bank Statement' },
      { key: 'payroll_sheet', labelAr: 'مسيرات الرواتب', labelEn: 'Payroll Sheets' },
      { key: 'gosi_wages_certificate', labelAr: 'شهادة المدد والأجور من التأمينات الاجتماعية (مشهد مدد وأجور / برنت التأمينات)', labelEn: 'GOSI Periods and Wages Certificate' },
      { key: 'working_hours_sheet', labelAr: 'كشف ساعات العمل', labelEn: 'Working Hours Record' },
    ],
  },
  {
    id: 'notices',
    labelAr: 'الإشعارات والمخاطبات',
    labelEn: 'Notices and Correspondence',
    options: [
      { key: 'termination_letter', labelAr: 'خطاب فصل', labelEn: 'Termination Letter' },
      { key: 'resignation_letter', labelAr: 'استقالة', labelEn: 'Resignation Letter' },
      { key: 'warnings', labelAr: 'إنذارات', labelEn: 'Warnings' },
      { key: 'emails', labelAr: 'إيميلات', labelEn: 'Emails' },
      { key: 'whatsapp_messages', labelAr: 'محادثات واتساب', labelEn: 'WhatsApp Messages' },
      { key: 'prior_objection_claim', labelAr: 'اعتراض أو مطالبة سابقة', labelEn: 'Prior Objection or Claim' },
      { key: 'employer_response', labelAr: 'رد جهة العمل', labelEn: 'Employer Response' },
    ],
  },
  {
    id: 'settlement',
    labelAr: 'التسوية والخلاف',
    labelEn: 'Settlement and Dispute',
    options: [
      { key: 'failed_settlement_report', labelAr: 'محضر تعذر التسوية', labelEn: 'Failed Settlement Report' },
      { key: 'amicable_settlement_form', labelAr: 'نموذج التسوية الودية', labelEn: 'Amicable Settlement Form' },
    ],
  },
  {
    id: 'general',
    labelAr: 'مرفقات عامة',
    labelEn: 'General Attachments',
    options: [
      { key: 'supporting_documents', labelAr: 'مستندات داعمة', labelEn: 'Supporting Documents' },
      { key: 'other_attachments', labelAr: 'مرفقات أخرى', labelEn: 'Other Attachments' },
    ],
  },
];

const attachmentOptionLookup = attachmentLibrary.flatMap((group) =>
  group.options.map((option) => ({
    ...option,
    categoryId: group.id,
    categoryLabelAr: group.labelAr,
    categoryLabelEn: group.labelEn,
  }))
);

const getFileExtension = (name = '') => {
  const parts = name.split('.');
  return parts.length > 1 ? parts.pop().toLowerCase() : '';
};

const formatBytes = (size = 0) => {
  if (!size) return '0 KB';
  if (size < 1024 * 1024) return `${Math.max(1, Math.round(size / 1024))} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
};

const createAttachmentItem = ({ option, file, uploadedFile, customName, isArabic }) => ({
  ...(uploadedFile || {}),
  id: uploadedFile?.id || `${file.name}-${file.size}-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  category: option.categoryId,
  categoryLabel: isArabic ? option.categoryLabelAr : option.categoryLabelEn,
  typeKey: option.key,
  typeLabel: option.labelAr,
  typeLabelEn: option.labelEn,
  displayName: customName?.trim() || (isArabic ? option.labelAr : option.labelEn),
  fileName: uploadedFile?.fileName || file.name,
  name: uploadedFile?.name || file.name,
  size: uploadedFile?.size || file.size,
  type: uploadedFile?.type || file.type || 'application/octet-stream',
  mimeType: uploadedFile?.mimeType || file.type || 'application/octet-stream',
  uploadedAt: uploadedFile?.uploadedAt || new Date().toISOString(),
  status: uploadedFile?.status || 'saved',
});

const buildFullName = (client = {}) =>
  [client.firstName, client.fatherName, client.grandfatherName, client.familyName]
    .map((part) => (part || '').trim())
    .filter(Boolean)
    .join(' ');

const createOfficialRecord = () => ({
  id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
  type: '',
  otherName: '',
  number: '',
  issuedBy: '',
  issueDate: '',
});

const officialRecordTypeOptions = ['', 'رقم وطني موحد', 'سجل تجاري'];

const getOfficialRecordLabel = (type, isArabic) => {
  switch (type) {
    case 'رقم وطني موحد':
      return isArabic ? 'رقم وطني موحد' : 'Unified national number';
    case 'سجل تجاري':
      return isArabic ? 'رقم السجل التجاري' : 'Commercial registration number';
    default:
      return isArabic ? 'الرقم' : 'Number';
  }
};

const validateIdentityDocument = (identityType, identityValue, isArabic) => {
  const value = (identityValue || '').trim();
  if (!value) {
    return isArabic ? 'رقم الهوية أو الوثيقة مطلوب.' : 'Identity / document number is required.';
  }

  if (identityType === 'هوية وطنية' || identityType === 'هوية مقيم') {
    if (!/^\d{10}$/.test(value)) {
      return isArabic
        ? 'يجب أن يتكون رقم الهوية من 10 أرقام.'
        : 'The identity number must contain 10 digits.';
    }
  }

  if (identityType === 'هوية خليجي' && value.length < 6) {
    return isArabic
      ? 'يرجى إدخال رقم هوية خليجي صحيح.'
      : 'Please enter a valid GCC identity number.';
  }

  if (identityType === 'جواز سفر' && value.length < 6) {
    return isArabic
      ? 'يرجى إدخال رقم جواز سفر صحيح.'
      : 'Please enter a valid passport number.';
  }

  return '';
};

const requiresLaborRelationAssessment = (contractType) =>
  ['عقد تعاون', 'عمل جزئي', 'عمل بالعمولة', 'Freelancer / مستقل', 'بدون عقد مكتوب', 'أخرى'].includes(contractType);

const LaborIntakePage = () => {
  const navigate = useNavigate();
  const { language, isArabic, text } = useLaborPortalLanguage();
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState(sectionDefaults);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isAttachmentDialogOpen, setIsAttachmentDialogOpen] = useState(false);
  const [attachmentForm, setAttachmentForm] = useState({ typeKey: '', displayName: '', files: [] });
  const [attachmentErrors, setAttachmentErrors] = useState({});
  const [isUploadingAttachment, setIsUploadingAttachment] = useState(false);
  const [dragActive, setDragActive] = useState(false);
  const [attachmentPreviews, setAttachmentPreviews] = useState({});
  const [draftSavedMessage, setDraftSavedMessage] = useState('');

  useEffect(() => {
    const savedDraft = getLaborIntakeDraft();
    if (savedDraft) {
      setFormData(mergeDraftWithDefaults(savedDraft));
    }
  }, []);

  useEffect(() => {
    saveLaborIntakeDraft(formData);
  }, [formData]);

  useEffect(
    () => () => {
      Object.values(attachmentPreviews).forEach((preview) => {
        if (preview?.url) {
          URL.revokeObjectURL(preview.url);
        }
      });
    },
    [attachmentPreviews]
  );

  const progressValue = useMemo(
    () => Math.round(((currentStep + 1) / laborIntakeSteps.length) * 100),
    [currentStep]
  );

  const localizedSteps = useMemo(
    () => {
      const conciseTitles = language === 'ar'
        ? {
            client: 'بيانات العميل',
            company: 'بيانات المنشأة',
            contract: 'العقد والعلاقة',
            employment: 'تحليل العلاقة والأجر',
            notices: 'انتهاء العلاقة والمطالبات',
            summary: 'ملخص الوقائع',
            attachments: 'المرفقات',
            declaration: 'الإقرار والإرسال',
          }
        : language === 'en'
          ? {
            client: 'Client Details',
            company: 'Employer Details',
            contract: 'Contract and Relationship',
            employment: 'Relationship and Wage',
            notices: 'Termination and Claims',
            summary: 'Facts Summary',
            attachments: 'Attachments',
            declaration: 'Declaration and Submit',
          }
          : laborStepTitlesByLanguage[language] || laborStepTitlesByLanguage.en || {};

      return laborIntakeSteps.map((step) => ({
        ...step,
        title: conciseTitles[step.id] || (isArabic ? step.title : laborStepsEn[step.id]?.title || step.title),
        description:
          step.id === 'client'
            ? (text('بيانات تعريفية ونظامية أساسية لإعداد ملف المطالبة العمالية.', 'Core legal and identification details for preparing the labor claim file.'))
            : step.id === 'company'
              ? (text('بيانات جهة العمل الرسمية والتنظيمية اللازمة لتحديد المنشأة محل النزاع.', 'Official and organizational employer details needed to identify the establishment in dispute.'))
              : step.id === 'employment'
                ? (text('تحليل العلاقة الفعلية، الأجر والمزايا، الإجازات والعمل الإضافي، والتغييرات الجوهرية أثناء العلاقة لاستخراج المطالبات الأولية.', 'Analyze the actual relationship, wage and benefits, leave and overtime, and material changes during the relationship to derive initial legal claims.'))
              : step.id === 'notices'
                ? (text('استكمال بيانات انتهاء العلاقة، المراسلات، والمطالبات التي أفدتم بها ضمن مسار منظم وواضح.', 'Complete the termination details, dispute correspondence, and the claims you reported through a clear structured flow.'))
              : step.id === 'summary'
                ? (text('مراجعة ملخص البيانات والوقائع والمصادقة عليه قبل الانتقال إلى رفع المرفقات.', 'Review the summary of data and facts and confirm it before moving to attachments.'))
              : step.id === 'attachments'
                ? (text('رفع المستندات المتوفرة وتنظيمها ضمن قائمة مرتبة مرتبطة بالطلب.', 'Upload and organize the available supporting documents in a structured list tied to the request.'))
              : step.id === 'declaration'
                ? (text('الإقرار النهائي بصحة البيانات والموافقة على إرسال الطلب لفريق وازن.', 'Provide the final declaration of accuracy and approve submission to the Wazin team.'))
              : isArabic
                ? step.description
                : laborStepsEn[step.id]?.description || step.description,
      }));
    },
    [isArabic, language, text]
  );

  const trOption = (value, group = 'select') => {
    if (isArabic) return value;
    if (language === 'en') return laborOptionMapsEn[group]?.[value] || laborOptionMapsEn.select[value] || value;
    return laborCommonOptionsByLanguage[language]?.[value]
      || laborOptionMapsEn[group]?.[value]
      || laborOptionMapsEn.select[value]
      || value;
  };

  const relationshipEnded = formData.client.currentEmploymentStatus === 'لا';
  const primaryOfficialRecord =
    formData.company.officialRecords?.[0] || createOfficialRecord();
  const workPatternNeedsDetails = ['دوام جزئي', 'عمل مرن', 'غير منتظم'].includes(
    formData.contract.workPattern
  );

  const attachmentOptions = useMemo(() => {
    const endedByTermination =
      ['فسخ العقد بموجب المادة 80', 'فسخ العقد بموجب المادة 81'].includes(formData.contract.endReason);
    const hasOvertimeClaim =
      formData.compensation.claims.includes('ساعات إضافية') || formData.benefits.overtime === 'نعم';
    const hasPriorObjection = formData.notices.sentObjection === 'نعم';
    const hasEmployerResponse = formData.notices.employerResponseReceived === 'نعم';
    return attachmentLibrary
      .map((group) => ({
        ...group,
        label: isArabic ? group.labelAr : group.labelEn,
        options: group.options
          .filter((option) => {
            if (option.key === 'termination_letter' || option.key === 'warnings') return endedByTermination;
            if (option.key === 'working_hours_sheet') return hasOvertimeClaim;
            if (option.key === 'prior_objection_claim') return hasPriorObjection;
            if (option.key === 'employer_response') return hasEmployerResponse;
            return true;
          })
          .map((option) => ({
            ...option,
            label: isArabic ? option.labelAr : option.labelEn,
          })),
      }))
      .filter((group) => group.options.length > 0);
  }, [
    formData.benefits.overtime,
    formData.compensation.claims,
    formData.compensation.contractWageMatchesActual,
    formData.compensation.gosiWageMatchesActual,
    formData.contract.endReason,
    formData.notices.sentObjection,
    formData.notices.employerResponseReceived,
    isArabic,
  ]);

  const availableAttachmentOptions = useMemo(
    () =>
      attachmentOptions.flatMap((group) =>
        group.options.map((option) => ({
          ...option,
          categoryId: group.id,
          categoryLabel: group.label,
        }))
      ),
    [attachmentOptions]
  );

  const contractNeedsLaborAssessment = requiresLaborRelationAssessment(formData.contract.contractType);

  const dynamicClaimsOptions = useMemo(() => {
    let visibleClaims = relationshipEnded
      ? claimsOptions
      : claimsOptions.filter(
          (item) =>
            !['مكافأة نهاية خدمة', 'تعويض عن فصل غير مشروع', 'بدل إشعار', 'إخلاء طرف', 'شهادة الخدمة'].includes(item)
        );

    if (formData.contract.experienceCertificateReceived === 'نعم') {
      visibleClaims = visibleClaims.filter((item) => item !== 'شهادة الخدمة');
    }

    if (formData.contract.clearanceCompleted === 'نعم') {
      visibleClaims = visibleClaims.filter((item) => item !== 'إخلاء طرف');
    }

    return visibleClaims;
  }, [relationshipEnded, formData.contract.experienceCertificateReceived, formData.contract.clearanceCompleted]);

  const supportEmail = 'labor.support@wazn.sa';
  const attachedTypeKeys = useMemo(
    () => new Set((formData.attachments.items || []).map((item) => item.typeKey)),
    [formData.attachments.items]
  );

  const missingAttachmentOptions = useMemo(
    () => availableAttachmentOptions.filter((option) => !attachedTypeKeys.has(option.key)),
    [attachedTypeKeys, availableAttachmentOptions]
  );

  const completionItems = useMemo(() => {
    const items = [
      {
        key: 'client',
        title: text('البيانات الأساسية', 'Basic data'),
        complete: Boolean(buildFullName(formData.client) && formData.client.nationalId && formData.client.mobile && formData.client.email),
      },
      {
        key: 'company',
        title: text('بيانات المنشأة', 'Employer data'),
        complete: Boolean(formData.company.tradeName && primaryOfficialRecord.type && primaryOfficialRecord.number),
      },
      {
        key: 'contract',
        title: text('العلاقة التعاقدية', 'Contractual relationship'),
        complete: Boolean(formData.contract.contractType && formData.contract.startDate),
      },
      {
        key: 'analysis',
        title: text('الوقائع والمطالبات', 'Facts and claims'),
        complete: Boolean(formData.compensation.claims.length),
      },
      {
        key: 'attachments',
        title: text('المستندات', 'Documents'),
        complete: (formData.attachments.items || []).length > 0,
      },
    ];
    return items;
  }, [formData, isArabic, primaryOfficialRecord]);

  const completionPercentage = useMemo(() => {
    const completeCount = completionItems.filter((item) => item.complete).length;
    return Math.round((completeCount / completionItems.length) * 100);
  }, [completionItems]);

  const financialTotals = useMemo(() => {
    const delayedWages = (formData.compensation.unpaidWagePeriods || []).reduce(
      (sum, period) => sum + wagePeriodTotal(period),
      0
    );
    const leaveCompensation = (formData.compensation.leaveBalancePeriods || []).reduce(
      (sum, period) => sum + leavePeriodTotal(period),
      0
    );
    const commissions = (formData.compensation.commissionClaims || []).reduce(
      (sum, claim) => sum + toAmount(claim.amount),
      0
    );
    return {
      delayedWages,
      leaveCompensation,
      commissions,
      approximateTotal: delayedWages + leaveCompensation + commissions,
    };
  }, [
    formData.compensation.commissionClaims,
    formData.compensation.leaveBalancePeriods,
    formData.compensation.unpaidWagePeriods,
  ]);

  const summarySections = useMemo(() => {
    const fullName = buildFullName(formData.client) || (text('غير محدد', 'Not specified'));
    const companyName = formData.company.tradeName || (text('غير محدد', 'Not specified'));
    const relationshipStatus = relationshipEnded ? (text('منتهية', 'Ended')) : (text('قائمة', 'Active'));
    const claims = formData.compensation.claims.length
      ? formData.compensation.claims.map((item) => trOption(item, 'claims'))
      : [text('لم تُحدد مطالبات في هذه المرحلة.', 'No claims were specified at this stage.')];
    if (formData.compensation.claims.includes('أجور متأخرة')) {
      claims.push(isArabic ? `إجمالي الأجور المتأخرة: ${formatAmount(financialTotals.delayedWages, true)}.` : `Total delayed wages: ${formatAmount(financialTotals.delayedWages, false)}.`);
    }
    if (formData.compensation.claims.includes('رصيد إجازات')) {
      claims.push(isArabic ? `إجمالي بدل الإجازات: ${formatAmount(financialTotals.leaveCompensation, true)}.` : `Total leave compensation: ${formatAmount(financialTotals.leaveCompensation, false)}.`);
    }
    if (formData.compensation.claims.includes('عمولات')) {
      claims.push(isArabic ? `إجمالي العمولات: ${formatAmount(financialTotals.commissions, true)}.` : `Total commissions: ${formatAmount(financialTotals.commissions, false)}.`);
    }
    if (formData.compensation.claims.some((claim) => ['أجور متأخرة', 'رصيد إجازات', 'عمولات'].includes(claim))) {
      claims.push(isArabic
        ? `إجمالي المطالبات المالية التقريبي: ${formatAmount(financialTotals.approximateTotal, true)}. هذا الإجمالي تقريبي أولي وقابل للمراجعة القانونية.`
        : `Approximate total financial claims: ${formatAmount(financialTotals.approximateTotal, false)}. This is an initial estimate subject to legal review.`);
    }
    const evidenceItems = Array.from(
      new Set([
        ...formData.contract.evidenceSources.map((item) => trOption(item, 'evidence')),
        ...formData.notices.selectedNotices.map((item) => trOption(item, 'notices')),
        ...(formData.attachments.items || []).map((item) => item.typeLabel || item.displayName),
      ].filter(Boolean))
    );

    const missingItems = [];
    if (!attachedTypeKeys.has('employment_contract') && !attachedTypeKeys.has('work_relationship_proof')) {
      missingItems.push(text('لا يظهر في الطلب مرفق لعقد العمل أو ما يثبت علاقة العمل.', 'No contract or proof of employment relationship appears in the request yet.'));
    }
    if (formData.compensation.claims.includes('أجور متأخرة') && !attachedTypeKeys.has('bank_statement')) {
      missingItems.push(text('قد تكون هناك حاجة إلى كشف حساب بنكي لدعم ما يتعلق بالأجور المتأخرة.', 'A bank statement may be needed to support delayed wage-related claims.'));
    }
    if ((formData.compensation.contractWageMatchesActual === 'لا' || formData.compensation.gosiWageMatchesActual === 'لا') && !attachedTypeKeys.has('gosi_wages_certificate')) {
      missingItems.push(text('قد يكون من المفيد إرفاق شهادة المدد والأجور من التأمينات الاجتماعية عند وجود اختلاف في بيانات الأجر.', 'A GOSI periods and wages certificate may be helpful when there is a wage discrepancy.'));
    }
    if (formData.benefits.overtime === 'نعم' && !attachedTypeKeys.has('working_hours_sheet')) {
      missingItems.push(text('لا يظهر مرفق يتعلق بساعات العمل الإضافي حتى الآن.', 'No supporting attachment for overtime work appears yet.'));
    }
    if (!missingItems.length) {
      missingItems.push(text('لا توجد نواقص ظاهرة في هذه المرحلة سوى ما قد يتبين بعد المراجعة.', 'No obvious gaps appear at this stage other than what may arise during review.'));
    }

    return [
      {
        title: text('بيانات مقدم الطلب', 'Applicant Summary'),
        items: [
          isArabic ? `أفاد مقدم الطلب بأن اسمه ${fullName}.` : `The applicant stated that their name is ${fullName}.`,
          isArabic ? `رقم الهوية أو الوثيقة المدخل هو ${formData.client.nationalId || 'غير محدد'}.` : `The entered identity or document number is ${formData.client.nationalId || 'Not specified'}.`,
          isArabic ? `وسيلة التواصل الأساسية: ${formData.client.mobile || 'غير محدد'}${formData.client.email ? `، والبريد الإلكتروني ${formData.client.email}.` : '.'}` : `Primary contact: ${formData.client.mobile || 'Not specified'}${formData.client.email ? `, and the email is ${formData.client.email}.` : '.'}`,
        ],
      },
      {
        title: text('بيانات المنشأة', 'Employer Summary'),
        items: [
          isArabic ? `أفاد مقدم الطلب بأن جهة العمل هي ${companyName}.` : `The applicant stated that the employer is ${companyName}.`,
          isArabic ? `البيانات الرسمية المتوفرة: ${primaryOfficialRecord.type ? `${trOption(primaryOfficialRecord.type)} - ${primaryOfficialRecord.number || 'غير محدد'}` : 'لم تُذكر بيانات رسمية مكتملة حتى الآن.'}` : `Available official record: ${primaryOfficialRecord.type ? `${trOption(primaryOfficialRecord.type)} - ${primaryOfficialRecord.number || 'Not specified'}` : 'No complete official record has been provided yet.'}`,
        ],
      },
      {
        title: text('العلاقة التعاقدية والوقائع الأساسية', 'Contractual Relationship and Core Facts'),
        items: [
          isArabic ? `ذكر مقدم الطلب أن نوع العلاقة أو الاتفاق هو ${trOption(formData.contract.contractType || 'لا أعلم')}.` : `The applicant stated that the type of relationship or agreement is ${trOption(formData.contract.contractType || 'لا أعلم')}.`,
          isArabic ? `تاريخ مباشرة العمل المدخل هو ${formData.contract.startDate ? toDisplayDate(formData.contract.startDate, text('ar-SA', 'en-US')) : 'غير محدد'}.` : `The entered work commencement date is ${formData.contract.startDate ? toDisplayDate(formData.contract.startDate, text('ar-SA', 'en-US')) : 'Not specified'}.`,
          isArabic ? `حالة العلاقة الحالية بحسب البيانات المدخلة هي ${relationshipStatus}${relationshipEnded && formData.client.lastWorkingDay ? `، وآخر يوم عمل هو ${toDisplayDate(formData.client.lastWorkingDay, 'ar-SA')}.` : '.'}` : `The current relationship status based on the entered data is ${relationshipStatus}${relationshipEnded && formData.client.lastWorkingDay ? `, and the last working day is ${toDisplayDate(formData.client.lastWorkingDay, 'en-US')}.` : '.'}`,
          isArabic ? `طريقة استلام الأجر المذكورة هي ${trOption(formData.contract.salaryReceiptMethod || 'لا أعلم')}.` : `The stated wage receipt method is ${trOption(formData.contract.salaryReceiptMethod || 'لا أعلم')}.`,
        ],
      },
      {
        title: text('المطالبات والبيانات المساندة', 'Claims and Supporting Information'),
        items: claims,
      },
      {
        title: text('المستندات والأدلة المذكورة', 'Mentioned Documents and Evidence'),
        items: evidenceItems.length ? evidenceItems : [text('لم تُذكر مستندات أو أدلة بشكل واضح حتى الآن.', 'No documents or evidence have been clearly identified yet.')],
      },
      {
        title: text('نواقص ظاهرة في الطلب', 'Visible Gaps in the Request'),
        items: missingItems,
      },
    ];
  }, [attachedTypeKeys, financialTotals, formData, isArabic, primaryOfficialRecord, relationshipEnded, trOption]);

  const suggestedClaims = useMemo(() => {
    const suggestions = [];

    if (formData.contract.salaryDelayRepeated === 'نعم' || Number(formData.compensation.unpaidSalaries || 0) > 0) {
      suggestions.push('أجور متأخرة');
    }
    if (formData.contract.overtimeBeyondAgreed === 'نعم' && formData.contract.overtimeCompensated === 'لا') {
      suggestions.push('ساعات إضافية');
    }
    if (relationshipEnded && formData.contract.noticeProvided === 'لا') {
      suggestions.push('بدل إشعار');
    }
    if (relationshipEnded && ['فسخ العقد بموجب المادة 80', 'فسخ العقد بموجب المادة 81'].includes(formData.contract.endReason)) {
      suggestions.push('تعويض عن فصل غير مشروع');
    }
    if (relationshipEnded) {
      suggestions.push('مكافأة نهاية خدمة');
    }
    if ((formData.benefits.vacationBalance || '').trim() && formData.benefits.leaveCompensated === 'لا') {
      suggestions.push('رصيد إجازات');
    }
    if (formData.contract.unpaidBenefitsOrCommissions === 'نعم') {
      suggestions.push('عمولات');
    }
    if (formData.contract.experienceCertificateReceived === 'لا') {
      suggestions.push('شهادة الخدمة');
    }
    if (relationshipEnded && formData.contract.clearanceCompleted === 'لا') {
      suggestions.push('إخلاء طرف');
    }
    if (formData.compensation.housingAllowance && !formData.compensation.totalSalary) {
      suggestions.push('بدل سكن');
    }
    if (formData.compensation.transportAllowance && !formData.compensation.totalSalary) {
      suggestions.push('بدل نقل');
    }

    return suggestions.filter((item, index) => suggestions.indexOf(item) === index);
  }, [
    relationshipEnded,
    formData.contract.salaryDelayRepeated,
    formData.compensation.unpaidSalaries,
    formData.contract.overtimeBeyondAgreed,
    formData.contract.overtimeCompensated,
    formData.contract.noticeProvided,
    formData.contract.endReason,
    formData.benefits.vacationBalance,
    formData.benefits.leaveCompensated,
    formData.contract.unpaidBenefitsOrCommissions,
    formData.contract.experienceCertificateReceived,
    formData.contract.clearanceCompleted,
    formData.compensation.housingAllowance,
    formData.compensation.transportAllowance,
    formData.compensation.totalSalary,
  ]);

  const updateSectionField = (section, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [section]: {
        ...prev[section],
        [field]: value,
      },
    }));
  };

  const updateNationalId = (value) => {
    const type = formData.client.identityType;
    if (type === 'هوية وطنية' || type === 'هوية مقيم') {
      updateSectionField('client', 'nationalId', digitsOnly(value).slice(0, 10));
      return;
    }
    updateSectionField('client', 'nationalId', value.toUpperCase().slice(0, 20));
  };

  const updateMobile = (value) => {
    updateSectionField('client', 'mobile', digitsOnly(value).slice(0, 10));
  };

  const updateChecklistValue = (section, field, value) => {
    const currentValues = formData[section][field];
    const nextValues = currentValues.includes(value)
      ? currentValues.filter((item) => item !== value)
      : [...currentValues, value];
    updateSectionField(section, field, nextValues);
  };

  const updateCompensationRow = (field, rowId, changes) => {
    updateSectionField(
      'compensation',
      field,
      (formData.compensation[field] || []).map((row) => (row.id === rowId ? { ...row, ...changes } : row))
    );
  };

  const addCompensationRow = (field, factory) => {
    updateSectionField('compensation', field, [...(formData.compensation[field] || []), factory()]);
  };

  const removeCompensationRow = (field, rowId) => {
    updateSectionField('compensation', field, (formData.compensation[field] || []).filter((row) => row.id !== rowId));
  };

  const updateEmploymentRow = (field, rowId, changes) => {
    updateSectionField('employment', field, (formData.employment[field] || []).map((row) => (row.id === rowId ? { ...row, ...changes } : row)));
  };

  const addEmploymentRow = (field, factory) => {
    updateSectionField('employment', field, [...(formData.employment[field] || []), factory()]);
  };

  const removeEmploymentRow = (field, rowId) => {
    updateSectionField('employment', field, (formData.employment[field] || []).filter((row) => row.id !== rowId));
  };

  const handleEmploymentRowAttachment = async (field, rowId, attachmentField, files, typeKey, typeLabel) => {
    const selectedFiles = Array.from(files || []);
    const fileError = validateAttachmentFiles(selectedFiles);
    if (fileError) {
      setErrors((prev) => ({ ...prev, employmentAttachment: fileError }));
      return;
    }

    try {
      const uploadedFiles = await uploadLaborAttachments(selectedFiles);
      const createdItems = selectedFiles.map((file, index) => ({
        ...createAttachmentItem({
          option: { key: typeKey, label: typeLabel, category: 'employment' },
          file,
          uploadedFile: uploadedFiles[index],
          customName: typeLabel,
          isArabic,
        }),
        category: 'employment',
      }));

      setAttachmentPreviews((prev) => {
        const next = { ...prev };
        createdItems.forEach((item, index) => {
          next[item.id] = { file: selectedFiles[index], url: URL.createObjectURL(selectedFiles[index]) };
        });
        return next;
      });

      updateEmploymentRow(field, rowId, { [attachmentField]: createdItems });
      updateSectionField('attachments', 'items', [...(formData.attachments.items || []), ...createdItems]);
      setErrors((prev) => ({ ...prev, employmentAttachment: '' }));
    } catch (error) {
      setErrors((prev) => ({
        ...prev,
        employmentAttachment: error.message || (text('تعذر رفع المرفق.', 'Failed to upload attachment.')),
      }));
    }
  };

  const toggleClaim = (claim) => {
    const selected = formData.compensation.claims.includes(claim);
    updateChecklistValue('compensation', 'claims', claim);
    if (!selected && claim === 'أجور متأخرة' && !formData.compensation.unpaidWagePeriods.length) {
      updateSectionField('compensation', 'unpaidWagePeriods', [createUnpaidWagePeriod()]);
    }
    if (!selected && claim === 'رصيد إجازات' && !formData.compensation.leaveBalancePeriods.length) {
      updateSectionField('compensation', 'leaveBalancePeriods', [createLeaveBalancePeriod()]);
    }
    if (!selected && claim === 'عمولات' && !formData.compensation.commissionClaims.length) {
      updateSectionField('compensation', 'commissionClaims', [createCommissionClaim()]);
    }
    if (selected && claim === 'أجور متأخرة') updateSectionField('compensation', 'unpaidWagePeriods', []);
    if (selected && claim === 'رصيد إجازات') updateSectionField('compensation', 'leaveBalancePeriods', []);
    if (selected && claim === 'عمولات') updateSectionField('compensation', 'commissionClaims', []);
    if (selected && claim === 'التعويض عن الأضرار') {
      updateSectionField('compensation', 'damageClaim', sectionDefaults.compensation.damageClaim);
    }
  };

  const updateOfficialRecord = (recordId, field, value) => {
    updateSectionField(
      'company',
      'officialRecords',
      (formData.company.officialRecords || []).map((record) =>
        record.id === recordId
          ? {
              ...record,
              [field]: value,
            }
          : record
      )
    );
  };

  const resetAttachmentDialog = () => {
    setAttachmentForm({ typeKey: '', displayName: '', files: [] });
    setAttachmentErrors({});
    setDragActive(false);
  };

  const validateAttachmentFiles = (files) => {
    if (!files.length) {
      return text('يرجى اختيار ملف واحد على الأقل.', 'Please choose at least one file.');
    }

    for (const file of files) {
      const extension = getFileExtension(file.name);
      if (!ATTACHMENT_ALLOWED_EXTENSIONS.includes(extension)) {
        return text('صيغة الملف غير مدعومة. الصيغ المتاحة: PDF / JPG / PNG / DOCX / XLSX / ZIP', 'Unsupported file format. Allowed formats: PDF / JPG / PNG / DOCX / XLSX / ZIP');
      }
      if (file.size > ATTACHMENT_MAX_SIZE_BYTES) {
        return text('حجم الملف يتجاوز الحد المسموح 10 ميجابايت.', 'The file exceeds the 10 MB size limit.');
      }
    }

    return '';
  };

  const assignAttachmentFiles = (incomingFiles) => {
    const files = Array.from(incomingFiles || []);
    const fileError = validateAttachmentFiles(files);
    if (fileError) {
      setAttachmentErrors((prev) => ({ ...prev, files: fileError }));
      return;
    }
    setAttachmentForm((prev) => ({ ...prev, files }));
    setAttachmentErrors((prev) => ({ ...prev, files: '' }));
  };

  const handleAttachmentTypeChange = (value) => {
    const option = availableAttachmentOptions.find((item) => item.key === value);
    setAttachmentForm((prev) => ({
      ...prev,
      typeKey: value,
      displayName: prev.displayName.trim() ? prev.displayName : option?.label || '',
    }));
    setAttachmentErrors((prev) => ({ ...prev, typeKey: '' }));
  };

  const handleSaveAttachment = async () => {
    const nextErrors = {};

    if (!attachmentForm.typeKey) {
      nextErrors.typeKey = text('يرجى اختيار نوع المرفق.', 'Please choose the attachment type.');
    }

    const fileError = validateAttachmentFiles(attachmentForm.files);
    if (fileError) {
      nextErrors.files = fileError;
    }

    if (Object.keys(nextErrors).length > 0) {
      setAttachmentErrors(nextErrors);
      return;
    }

    const option = attachmentOptionLookup.find((item) => item.key === attachmentForm.typeKey);
    if (!option) return;

    setIsUploadingAttachment(true);
    try {
      const uploadedFiles = await uploadLaborAttachments(attachmentForm.files);
      const createdItems = attachmentForm.files.map((file, index) =>
        createAttachmentItem({
          option,
          file,
          uploadedFile: uploadedFiles[index],
          customName: attachmentForm.displayName,
          isArabic,
        })
      );

      setAttachmentPreviews((prev) => {
        const next = { ...prev };
        createdItems.forEach((item, index) => {
          const file = attachmentForm.files[index];
          next[item.id] = { file, url: URL.createObjectURL(file) };
        });
        return next;
      });

      updateSectionField('attachments', 'items', [...(formData.attachments.items || []), ...createdItems]);
      setIsAttachmentDialogOpen(false);
      resetAttachmentDialog();
    } catch (error) {
      setAttachmentErrors((prev) => ({ ...prev, files: error.message || (text('تعذر رفع المرفق.', 'Failed to upload attachment.')) }));
    } finally {
      setIsUploadingAttachment(false);
    }
  };

  const handleRemoveAttachment = (attachmentId) => {
    setAttachmentPreviews((prev) => {
      const next = { ...prev };
      if (next[attachmentId]?.url) {
        URL.revokeObjectURL(next[attachmentId].url);
      }
      delete next[attachmentId];
      return next;
    });

    updateSectionField(
      'attachments',
      'items',
      (formData.attachments.items || []).filter((item) => item.id !== attachmentId)
    );
  };

  const handlePreviewAttachment = (attachmentId) => {
    const preview = attachmentPreviews[attachmentId];
    if (preview?.url) {
      window.open(preview.url, '_blank', 'noopener,noreferrer');
    }
  };

  const validateStep = (stepIndex) => {
    const nextErrors = {};

    if (stepIndex === 0) {
      if (!formData.client.firstName.trim()) nextErrors.clientFirstName = text('الاسم الأول مطلوب.', 'First name is required.');
      if (!formData.client.fatherName.trim()) nextErrors.clientFatherName = text('اسم الأب مطلوب.', 'Father name is required.');
      if (!formData.client.familyName.trim()) nextErrors.clientFamilyName = text('اسم العائلة مطلوب.', 'Family name is required.');
      if (!formData.client.identityType) nextErrors.clientIdentityType = text('نوع الهوية مطلوب.', 'Identity type is required.');
      if (formData.client.identityType === 'أخرى' && !formData.client.identityTypeOther.trim()) nextErrors.clientIdentityTypeOther = text('يرجى توضيح نوع الهوية.', 'Please specify the identity type.');
      const identityValidationError = validateIdentityDocument(formData.client.identityType, formData.client.nationalId, isArabic);
      if (identityValidationError) nextErrors.clientNationalId = identityValidationError;
      if (!formData.client.birthDate) nextErrors.clientBirthDate = text('تاريخ الميلاد مطلوب.', 'Date of birth is required.');
      if (!formData.client.nationality.trim()) nextErrors.clientNationality = text('الجنسية مطلوبة.', 'Nationality is required.');
      if (!formData.client.mobile.trim()) nextErrors.clientMobile = text('رقم الجوال مطلوب.', 'Mobile number is required.');
      if (formData.client.mobile && !/^05\d{8}$/.test(formData.client.mobile)) nextErrors.clientMobile = text('يرجى إدخال رقم جوال سعودي صحيح يبدأ بـ 05.', 'Please enter a valid Saudi mobile number starting with 05.');
      if (!formData.client.email.trim()) nextErrors.clientEmail = text('البريد الإلكتروني مطلوب.', 'Email address is required.');
      if (!formData.client.city.trim()) nextErrors.clientCity = text('المدينة مطلوبة.', 'City is required.');
      if (!formData.client.jobTitle.trim()) nextErrors.clientJobTitle = text('المسمى الوظيفي مطلوب.', 'Job title is required.');
      if (relationshipEnded && !formData.client.lastWorkingDay) nextErrors.clientLastWorkingDay = text('تاريخ آخر يوم عمل مطلوب.', 'Last working day is required.');
    }

    if (stepIndex === 1) {
      if (!formData.company.tradeName.trim()) nextErrors.companyTradeName = text('اسم المنشأة مطلوب.', 'Establishment name is required.');
      if (formData.company.establishmentType === 'أخرى' && !formData.company.establishmentTypeOther.trim()) nextErrors.companyEstablishmentTypeOther = text('يرجى توضيح نوع المنشأة.', 'Please specify the establishment type.');
      if (formData.company.sector === 'أخرى' && !formData.company.sectorOther.trim()) nextErrors.companySectorOther = text('يرجى توضيح القطاع.', 'Please specify the sector.');
      const primaryOfficialRecord = (formData.company.officialRecords || [createOfficialRecord()])[0];
      const hasOfficialNumber = (primaryOfficialRecord?.number || '').trim();

      if (primaryOfficialRecord?.type && !hasOfficialNumber) {
        nextErrors[`officialRecordNumber-${primaryOfficialRecord.id}`] = text('يرجى إدخال الرقم.', 'Please enter the number.');
      }
    }

    if (stepIndex === 2) {
      if (!formData.contract.startDate) nextErrors.contractStartDate = text('تاريخ مباشرة العمل مطلوب.', 'Work start date is required.');
      if (formData.contract.contractType === 'أخرى' && !formData.contract.contractTypeOther.trim()) {
        nextErrors.contractTypeOther = text('يرجى توضيح نوع العلاقة أو الاتفاق.', 'Please specify the relationship or agreement type.');
      }
      if (formData.contract.contractDurationType === 'محدد المدة' && !formData.contract.contractEndDate) {
        nextErrors.contractEndDate = text('تاريخ نهاية العقد مطلوب.', 'Contract end date is required.');
      }
      if (formData.contract.trialPeriod === 'نعم' && !formData.contract.trialPeriodLength.trim()) {
        nextErrors.contractTrialPeriodLength = text('مدة التجربة مطلوبة.', 'Trial period length is required.');
      }
      if (workPatternNeedsDetails && !formData.contract.workPatternDetails.trim()) {
        nextErrors.contractWorkPatternDetails = text('يرجى توضيح طبيعة الدوام.', 'Please explain the work pattern.');
      }
      if (formData.contract.outsideAssignments === 'نعم' && !formData.contract.outsideAssignmentsDetails.trim()) {
        nextErrors.contractOutsideAssignmentsDetails = text('يرجى توضيح طبيعة التكليفات الإضافية.', 'Please explain the additional assignments.');
      }
      if (formData.contract.dutiesDifferent === 'نعم' && !formData.contract.dutiesDifferenceDetails.trim()) {
        nextErrors.contractDutiesDifferenceDetails = text('يرجى شرح طبيعة المهام الفعلية.', 'Please explain the actual duties.');
      }
      if (formData.contract.unpaidBenefitsOrCommissions === 'نعم' && !formData.contract.unpaidBenefitsDetails.trim()) {
        nextErrors.contractUnpaidBenefitsDetails = text('يرجى توضيح العمولات أو الحوافز أو البدلات غير المصروفة.', 'Please explain the unpaid commissions, incentives, or allowances.');
      }
      if (formData.contract.threatenedOrPressured === 'نعم' && !formData.contract.threatenedOrPressuredDetails.trim()) {
        nextErrors.contractThreatenedOrPressuredDetails = text('يرجى توضيح واقعة التهديد أو الضغط.', 'Please explain the threat or pressure incident.');
      }
      if (formData.contract.signedWithoutCopy === 'نعم' && !formData.contract.signedWithoutCopyDetails.trim()) {
        nextErrors.contractSignedWithoutCopyDetails = text('يرجى توضيح المستندات التي طُلب توقيعها دون تسليم نسخة.', 'Please explain which documents you were asked to sign without receiving a copy.');
      }
      if (formData.contract.employerCommittedToContract === 'لا' && !formData.contract.employerBreachDetails.trim()) {
        nextErrors.contractEmployerBreachDetails = text('يرجى توضيح أوجه عدم الالتزام بالعقد.', 'Please explain how the employer failed to comply with the contract.');
      }
      if (formData.contract.conditionsChanged === 'نعم' && ![
        formData.contract.salaryChanged,
        formData.contract.titleChanged,
        formData.contract.hoursChanged,
        formData.contract.workplaceChanged,
        formData.contract.dutiesChanged,
        formData.contract.otherChanged,
      ].some(Boolean)) {
        nextErrors.contractConditionsChanged = text('يرجى تحديد ما الذي تغيّر في العلاقة.', 'Please specify what changed in the relationship.');
      }
      if (formData.contract.otherChanged && !formData.contract.otherChangedDetails.trim()) {
        nextErrors.contractOtherChangedDetails = text('يرجى توضيح التغيير الآخر.', 'Please specify the other change.');
      }
      if (relationshipEnded) {
        if (!formData.contract.endReason || formData.contract.endReason === 'ما زالت العلاقة قائمة') nextErrors.contractEndReason = text('سبب انتهاء العلاقة مطلوب.', 'Reason for termination is required.');
        if (formData.contract.endReason === 'أخرى' && !formData.contract.endReasonOther.trim()) {
          nextErrors.contractEndReasonOther = text('يرجى توضيح سبب انتهاء العلاقة.', 'Please specify the reason for ending the relationship.');
        }
        if (formData.contract.noticeProvided === 'نعم') {
          if (!formData.contract.noticeDeliveryMethod) nextErrors.contractNoticeDeliveryMethod = text('يرجى تحديد طريقة تسليم الإشعار.', 'Please specify how the notice was delivered.');
          if (!formData.contract.noticeDate) nextErrors.contractNoticeDate = text('تاريخ الإشعار مطلوب.', 'Notice date is required.');
        }
        if (formData.contract.releaseReceived === 'نعم') {
          if (!formData.contract.releaseDate) nextErrors.contractReleaseDate = text('تاريخ توقيع المخالصة مطلوب.', 'Release signing date is required.');
          if (!formData.contract.releaseSignedTiming) nextErrors.contractReleaseSignedTiming = text('يرجى تحديد توقيت توقيع المخالصة.', 'Please specify when the release was signed.');
          if (!formData.contract.releaseFormat) nextErrors.contractReleaseFormat = text('يرجى تحديد نوع المخالصة.', 'Please specify the release format.');
        }
        if (formData.contract.experienceCertificateReceived === 'نعم' && !formData.contract.experienceCertificateDate) {
          nextErrors.contractExperienceCertificateDate = text('تاريخ استلام شهادة الخدمة مطلوب.', 'Service certificate receipt date is required.');
        }
        if (formData.contract.clearanceCompleted === 'نعم' && !formData.contract.clearanceDate) {
          nextErrors.contractClearanceDate = text('تاريخ إخلاء الطرف مطلوب.', 'Clearance date is required.');
        }
      }
      if (formData.contract.evidenceSources.includes('أخرى') && !formData.contract.evidenceOther.trim()) {
        nextErrors.contractEvidenceOther = text('يرجى توضيح نوع الدليل الآخر.', 'Please specify the other evidence type.');
      }
      if (!formData.contract.problemSummary.trim()) {
        nextErrors.contractProblemSummary = text('يرجى شرح المشكلة الأساسية بشكل مختصر.', 'Please briefly explain the main issue.');
      }
    }

    if (stepIndex === 3) {
      if (formData.compensation.contractWageMatchesActual === 'لا' && !formData.compensation.contractWageDifferenceDetails.trim()) {
        nextErrors.compensationContractWageDifferenceDetails = text('يرجى توضيح الفرق بين الأجر الفعلي والأجر المدون في العقد.', 'Please explain the difference between the actual wage and the wage stated in the contract.');
      }
      if (formData.compensation.gosiWageMatchesActual === 'لا' && !formData.compensation.gosiWageDifferenceDetails.trim()) {
        nextErrors.compensationGosiWageDifferenceDetails = text('يرجى توضيح الفرق بين الأجر الفعلي والأجر المسجل في التأمينات الاجتماعية.', 'Please explain the difference between the actual wage and the wage registered in GOSI.');
      }
      if (formData.compensation.claims.includes('أجور متأخرة') && !(formData.compensation.unpaidWagePeriods || []).some((period) =>
        period.periodType === 'شهر كامل'
          ? period.month && toAmount(period.monthlyWage) > 0
          : period.fromDate && period.toDate && toAmount(period.dailyWage) > 0
      )) {
        nextErrors.unpaidWagePeriods = text('يرجى إدخال فترة واحدة على الأقل للأجور المتأخرة مع قيمة الأجر.', 'Please enter at least one delayed wage period with its wage value.');
      }
      if (formData.compensation.claims.includes('رصيد إجازات') && !(formData.compensation.leaveBalancePeriods || []).some((period) =>
        toAmount(period.days) > 0 && toAmount(period.dailyWage) > 0
      )) {
        nextErrors.leaveBalancePeriods = text('يرجى إدخال رصيد إجازة واحد على الأقل مع الأجر اليومي.', 'Please enter at least one leave balance with its daily wage.');
      }
      if (formData.compensation.claims.includes('عمولات') && !(formData.compensation.commissionClaims || []).some((claim) =>
        (claim.description || '').trim() && (claim.entitlementPeriod || '').trim() && toAmount(claim.amount) > 0
      )) {
        nextErrors.commissionClaims = text('يرجى إدخال عمولة واحدة على الأقل مع وصفها وفترة استحقاقها ومبلغها.', 'Please enter at least one commission with its description, entitlement period, and amount.');
      }
      if (formData.compensation.claims.includes('التعويض عن الأضرار') &&
        (!(formData.compensation.damageClaim.damageType || '').trim() || !(formData.compensation.damageClaim.description || '').trim())) {
        nextErrors.damageClaim = text('يرجى تحديد نوع الضرر ووصفه.', 'Please specify and describe the damage.');
      }
    }

    if (stepIndex === 4) {
      if (formData.notices.sentObjection === 'نعم') {
        if (!formData.notices.objectionDate) {
          nextErrors.noticesObjectionDate = text('تاريخ الإرسال أو المطالبة مطلوب.', 'The objection or claim date is required.');
        }
        if (!formData.notices.objectionMethod) {
          nextErrors.noticesObjectionMethod = text('يرجى تحديد طريقة الإرسال.', 'Please specify the submission method.');
        }
        if (formData.notices.employerResponseReceived === 'نعم' && !formData.notices.employerResponseCopyAvailable) {
          nextErrors.noticesEmployerResponseCopyAvailable = text('يرجى تحديد ما إذا كانت لديكم نسخة من الرد.', 'Please specify whether you have a copy of the employer response.');
        }
      }
    }

    if (stepIndex === 5) {
      if (!formData.summary.approvalStatus) {
        nextErrors.summaryApprovalStatus = text('يرجى اختيار حالة المصادقة على الملخص.', 'Please choose the summary confirmation status.');
      }
      if (formData.summary.approvalStatus === 'partial' && !formData.summary.approvalNote.trim()) {
        nextErrors.summaryApprovalNote = text('يرجى كتابة المعلومة التي ترغب في تعديلها أو توضيحها.', 'Please describe the information you want to correct or clarify.');
      }
      if (formData.summary.approvalStatus === 'rejected') {
        nextErrors.summaryApprovalStatus = text('لا يمكن إرسال الطلب دون المصادقة على ملخص البيانات والوقائع. إذا واجهت مشكلة في البيانات أو في عرض الملخص، يرجى التواصل مع فريق الدعم عبر البريد: labor.support@wazn.sa', 'The request cannot be submitted without confirming the summary of data and facts. If you face an issue with the data or summary display, please contact support at labor.support@wazn.sa.');
      }
    }

    if (stepIndex === 7 && !formData.declaration.accepted) {
      nextErrors.declarationAccepted = text('يلزم الإقرار بصحة البيانات قبل الإرسال.', 'You must confirm the declaration before submission.');
    }

    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleNext = () => {
    if (!validateStep(currentStep)) return;
    setCurrentStep((prev) => Math.min(prev + 1, laborIntakeSteps.length - 1));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handlePrevious = () => {
    setErrors({});
    setCurrentStep((prev) => Math.max(prev - 1, 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleManualSaveDraft = () => {
    const savedDraft = saveLaborIntakeDraft(formData);
    setDraftSavedMessage(
      isArabic
        ? `تم حفظ المسودة برقم مرجعي: ${savedDraft.draftReference}`
        : `Draft saved with reference: ${savedDraft.draftReference}`
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!validateStep(currentStep)) return;
    setIsSubmitting(true);

    const submissionPayload = {
      ...formData,
      client: {
        ...formData.client,
        fullName: buildFullName(formData.client),
      },
      contract: {
        ...formData.contract,
        currentStatus: relationshipEnded ? 'منتهية' : 'قائمة',
        lastDay: relationshipEnded ? formData.client.lastWorkingDay : '',
      },
      compensation: {
        ...formData.compensation,
        paymentMethod: formData.contract.salaryReceiptMethod,
      },
      summary: formData.summary,
      localization: {
        sourceLanguage: language,
        originalTextPreserved: true,
        translationStatus: language === 'ar' ? 'not_required' : 'pending',
      },
    };

    try {
      const createdCase = await createLaborCase(submissionPayload);
      navigate(
        `/labor-intake/success?ref=${encodeURIComponent(createdCase.referenceNumber)}&token=${encodeURIComponent(createdCase.trackingToken)}`,
        { state: { createdCase } }
      );
    } catch (error) {
      setErrors({ submit: text('تعذر إرسال الطلب إلى الخادم. يرجى المحاولة مجدداً.', 'The request could not be submitted. Please try again.') });
      setIsSubmitting(false);
    }
  };

  const CurrentIcon = iconMap[laborIntakeSteps[currentStep].id] || FileText;

  return (
    <LaborStandaloneLayout
      badgeText={text('مرحلة تعبئة الطلب', 'Request Form Stage')}
      footerNote={text('يُحفظ التقدم تلقائياً أثناء التعبئة حتى يمكن استكمال الطلب لاحقاً.', 'Progress is saved automatically during completion so the request can be resumed later.')}
    >
      <main className="pb-20 pt-8">
        <section className="px-4">
          <div className="mx-auto max-w-7xl space-y-6">
            <Card className="rounded-[22px] border-[#C8A870]/18 bg-white/95 shadow-[0_8px_28px_rgba(15,23,42,0.045)]">
              <CardContent className="p-3.5 sm:p-4">
                <div className="flex flex-col gap-3">
                  <div className="flex items-center justify-between gap-3 px-1">
                    <p className="text-xs font-bold tracking-wide text-[#1F3B64]">{text('مسار الطلب', 'Request Workflow')}</p>
                    <div className="inline-flex items-center gap-2 rounded-full border border-[#C8A870]/18 bg-[#A27849]/[0.07] px-3 py-1 text-[11px] font-bold text-[#A27849]">
                      {text('الخطوة', 'Step')} {currentStep + 1} {text('من', 'of')} {laborIntakeSteps.length}
                    </div>
                  </div>

                  <div className="h-[3px] overflow-hidden rounded-full bg-[#ece7de]">
                    <div className="h-full rounded-full bg-[linear-gradient(90deg,#1F3B64_0%,#C8A870_100%)] transition-all duration-500 ease-out" style={{ width: `${progressValue}%` }} />
                  </div>

                  <div className="flex gap-2 overflow-x-auto pb-1">
                    {localizedSteps.map((step, index) => {
                      const StepIcon = iconMap[step.id] || FileText;
                      const isActive = index === currentStep;
                      const isCompleted = index < currentStep;

                      return (
                        <button
                          key={step.id}
                          type="button"
                          onClick={() => {
                            if (index <= currentStep || validateStep(currentStep)) {
                              setCurrentStep(index);
                              setErrors({});
                            }
                          }}
                          className={`min-w-[140px] shrink-0 rounded-xl border px-3 py-2.5 text-right transition duration-300 ${isActive ? 'border-[#1F3B64] bg-[#1F3B64] text-white shadow-[0_5px_14px_rgba(31,59,100,0.14)]' : isCompleted ? 'border-[#C8A870]/25 bg-[#faf8f3] text-[#1F3B64]' : 'border-[#e8e2d7] bg-white text-slate-600 hover:border-[#C8A870]/40 hover:bg-[#fcfbf8]'}`}
                        >
                          <div className="flex items-center gap-2.5">
                            <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-lg ${isActive ? 'bg-white/10' : isCompleted ? 'bg-[#C8A870]/10 text-[#A27849]' : 'bg-slate-50'}`}>
                              {isCompleted ? <CheckCircle2 className="h-4 w-4" /> : <StepIcon className="h-4 w-4" />}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="whitespace-nowrap text-[13px] font-bold leading-5">{step.title}</p>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              </CardContent>
            </Card>

            <form onSubmit={handleSubmit}>
              <Card className="rounded-[28px] border-[#C8A870]/20 bg-white/95 shadow-[0_20px_60px_rgba(15,23,42,0.08)]">
                <CardHeader className="border-b border-[#C8A870]/15 pb-6">
                  <div className="flex flex-wrap items-center justify-between gap-4">
                    <div>
                      <div className="mb-3 inline-flex items-center gap-2 rounded-full bg-[#A27849]/10 px-3 py-1 text-xs font-bold text-[#A27849]">
                        {text('الخطوة', 'Step')} {currentStep + 1} {text('من', 'of')} {laborIntakeSteps.length}
                      </div>
                      <CardTitle className="text-3xl font-black text-[#1F3B64]">{localizedSteps[currentStep].title}</CardTitle>
                      <CardDescription className="mt-3 text-sm leading-7 text-slate-600">{localizedSteps[currentStep].description}</CardDescription>
                    </div>
                    <div className="flex h-16 w-16 items-center justify-center rounded-[22px] bg-[#1F3B64] text-white shadow-lg">
                      <CurrentIcon className="h-7 w-7" />
                    </div>
                  </div>
                </CardHeader>

                <CardContent className="space-y-8 p-6 sm:p-8">
                  {currentStep === 0 && (
                    <div className="space-y-6">
                      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
                        <Field required label={text('الاسم الأول', 'First name')} value={formData.client.firstName} onChange={(value) => updateSectionField('client', 'firstName', value)} error={errors.clientFirstName} placeholder={text('الاسم الأول', 'First name')} />
                        <Field required label={text('اسم الأب', 'Father name')} value={formData.client.fatherName} onChange={(value) => updateSectionField('client', 'fatherName', value)} error={errors.clientFatherName} placeholder={text('اسم الأب', 'Father name')} />
                        <Field label={text('اسم الجد', 'Grandfather name')} value={formData.client.grandfatherName} onChange={(value) => updateSectionField('client', 'grandfatherName', value)} placeholder={text('اسم الجد', 'Grandfather name')} />
                        <Field required label={text('اسم العائلة', 'Family name')} value={formData.client.familyName} onChange={(value) => updateSectionField('client', 'familyName', value)} error={errors.clientFamilyName} placeholder={text('اسم العائلة', 'Family name')} />
                      </div>

                      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
                        <SelectField required label={text('نوع الهوية', 'Identity type')} value={formData.client.identityType} onChange={(value) => {
                          updateSectionField('client', 'identityType', value);
                          if (value !== 'أخرى') updateSectionField('client', 'identityTypeOther', '');
                          updateSectionField('client', 'nationalId', '');
                        }} error={errors.clientIdentityType} options={['هوية وطنية', 'هوية مقيم', 'هوية خليجي', 'جواز سفر', 'أخرى']} getOptionLabel={(option) => trOption(option)} />
                        {formData.client.identityType === 'أخرى' ? (
                          <Field required label={text('يرجى التوضيح', 'Please specify')} value={formData.client.identityTypeOther} onChange={(value) => updateSectionField('client', 'identityTypeOther', value)} error={errors.clientIdentityTypeOther} placeholder={text('نوع الهوية', 'Identity type')} />
                        ) : null}
                        <Field required label={text('رقم الهوية / الوثيقة', 'Identity / document number')} value={formData.client.nationalId} onChange={updateNationalId} error={errors.clientNationalId} inputMode={formData.client.identityType === 'هوية وطنية' || formData.client.identityType === 'هوية مقيم' ? 'numeric' : 'text'} maxLength={formData.client.identityType === 'هوية وطنية' || formData.client.identityType === 'هوية مقيم' ? 10 : 20} placeholder={formData.client.identityType === 'جواز سفر' ? 'Passport Number' : '1XXXXXXXXX'} />
                        <Field required label={text('تاريخ الميلاد', 'Date of birth')} type="date" value={formData.client.birthDate} onChange={(value) => updateSectionField('client', 'birthDate', value)} error={errors.clientBirthDate} helperText={formData.client.birthDate ? `${text('هجري', 'Hijri')}: ${toHijriDate(formData.client.birthDate, isArabic)}` : ''} />
                        <Field required label={text('الجنسية', 'Nationality')} value={formData.client.nationality} onChange={(value) => updateSectionField('client', 'nationality', value)} error={errors.clientNationality} placeholder={text('مثال: سعودي', 'Example: Saudi')} />
                        <Field required label={text('رقم الجوال', 'Mobile number')} value={formData.client.mobile} onChange={updateMobile} error={errors.clientMobile} icon={Phone} inputMode="tel" maxLength={10} placeholder="05XXXXXXXX" />
                        <Field required label={text('البريد الإلكتروني', 'Email address')} type="email" value={formData.client.email} onChange={(value) => updateSectionField('client', 'email', value)} error={errors.clientEmail} icon={Mail} placeholder="name@example.com" />
                        <Field required label={text('المدينة', 'City')} value={formData.client.city} onChange={(value) => updateSectionField('client', 'city', value)} error={errors.clientCity} placeholder={text('المدينة', 'City')} />
                        <Field required label={text('المسمى الوظيفي', 'Job title')} value={formData.client.jobTitle} onChange={(value) => updateSectionField('client', 'jobTitle', value)} error={errors.clientJobTitle} placeholder={text('المسمى الوظيفي', 'Job title')} />
                        <SelectField required label={text('هل ما زلت على رأس العمل؟', 'Are you still employed?')} value={formData.client.currentEmploymentStatus} onChange={(value) => {
                          updateSectionField('client', 'currentEmploymentStatus', value);
                          if (value === 'نعم') updateSectionField('client', 'lastWorkingDay', '');
                        }} options={['نعم', 'لا']} getOptionLabel={(option) => trOption(option)} />
                        {relationshipEnded ? (
                          <Field required className="xl:col-span-2" label={text('تاريخ آخر يوم عمل', 'Last working day')} type="date" value={formData.client.lastWorkingDay} onChange={(value) => updateSectionField('client', 'lastWorkingDay', value)} error={errors.clientLastWorkingDay} helperText={formData.client.lastWorkingDay ? `${text('هجري', 'Hijri')}: ${toHijriDate(formData.client.lastWorkingDay, isArabic)}` : ''} />
                        ) : null}
                      </div>

                      <div className="rounded-[24px] border border-[#C8A870]/18 bg-[#f8f6f1] p-5">
                        <div className="mb-4">
                          <h3 className="text-sm font-black text-[#1F3B64]">{text('العنوان الوطني', 'National Address')}</h3>
                        </div>
                        <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
                          <Field required label={text('المدينة', 'City')} value={formData.client.city} onChange={(value) => updateSectionField('client', 'city', value)} error={errors.clientCity} placeholder={text('المدينة', 'City')} />
                          <Field label={text('الحي', 'District')} value={formData.client.district} onChange={(value) => updateSectionField('client', 'district', value)} placeholder={text('الحي', 'District')} />
                          <Field label={text('الشارع', 'Street')} value={formData.client.street} onChange={(value) => updateSectionField('client', 'street', value)} placeholder={text('الشارع', 'Street')} />
                          <Field label={text('رقم المبنى', 'Building number')} value={formData.client.buildingNumber} onChange={(value) => updateSectionField('client', 'buildingNumber', digitsOnly(value).slice(0, 4))} inputMode="numeric" maxLength={4} placeholder="1234" />
                          <Field label={text('الرقم الإضافي', 'Additional number')} value={formData.client.additionalNumber} onChange={(value) => updateSectionField('client', 'additionalNumber', digitsOnly(value).slice(0, 4))} inputMode="numeric" maxLength={4} placeholder="1234" />
                          <Field label={text('الرمز البريدي', 'Postal code')} value={formData.client.postalCode} onChange={(value) => updateSectionField('client', 'postalCode', digitsOnly(value).slice(0, 5))} inputMode="numeric" maxLength={5} placeholder="12345" />
                          <Field label={text('رقم الوحدة', 'Unit number')} value={formData.client.unitNumber} onChange={(value) => updateSectionField('client', 'unitNumber', digitsOnly(value).slice(0, 4))} inputMode="numeric" maxLength={4} placeholder="12" />
                        </div>
                      </div>
                    </div>
                  )}

                  {currentStep === 1 && (
                    <div className="space-y-6">
                      <Field
                        required
                        label={text('اسم المنشأة', 'Establishment name')}
                        value={formData.company.tradeName}
                        onChange={(value) => updateSectionField('company', 'tradeName', value)}
                        error={errors.companyTradeName}
                        helperText={text('كما يظهر في العقد أو السجل التجاري.', 'As shown in the contract or commercial registration.')}
                      />

                      <div className="rounded-[24px] border border-[#C8A870]/18 bg-[#f8f6f1] p-5">
                        <div className="mb-4 border-b border-[#C8A870]/14 pb-3">
                          <h3 className="text-lg font-black text-[#1F3B64] sm:text-xl">{text('البيانات الرسمية للمنشأة', 'Official establishment data')}</h3>
                          <p className="mt-2 text-sm leading-7 text-slate-600">
                            {text('حدّد نوع البيانات الرسمية المتوفرة لديك، ثم أدخل الرقم المرتبط بها.', 'Select the official data type available to you, then enter the related number.')}
                          </p>
                        </div>
                        <div className="grid gap-4 md:grid-cols-2">
                          <SelectField
                            label={text('نوع البيانات الرسمية', 'Official data type')}
                            value={primaryOfficialRecord.type}
                            onChange={(value) =>
                              updateSectionField('company', 'officialRecords', [
                                {
                                  ...primaryOfficialRecord,
                                  type: value,
                                  number: '',
                                  otherName: '',
                                  issuedBy: '',
                                  issueDate: '',
                                },
                              ])
                            }
                            options={officialRecordTypeOptions}
                            getOptionLabel={(option) => {
                              if (!option) return text('اختر النوع', 'Select type');
                              return trOption(option);
                            }}
                          />

                          {primaryOfficialRecord.type ? (
                            <Field
                              label={getOfficialRecordLabel(primaryOfficialRecord.type, isArabic)}
                              value={primaryOfficialRecord.number}
                              onChange={(value) =>
                                updateOfficialRecord(
                                  primaryOfficialRecord.id,
                                  'number',
                                  digitsOnly(value).slice(0, 15)
                                )
                              }
                              error={errors[`officialRecordNumber-${primaryOfficialRecord.id}`]}
                              inputMode="numeric"
                            />
                          ) : null}
                        </div>
                      </div>

                      <div className="rounded-[24px] border border-[#C8A870]/18 bg-[#f8f6f1] p-5">
                        <div className="mb-4 border-b border-[#C8A870]/14 pb-3">
                          <h3 className="text-lg font-black text-[#1F3B64] sm:text-xl">{text('بيانات الترخيص (إن وجدت)', 'License details (if available)')}</h3>
                          <p className="mt-2 text-sm leading-7 text-slate-600">
                            {text('هذا القسم اختياري بالكامل، ويمكن تجاوزه إذا لم تتوفر لديك بيانات الترخيص.', 'This section is entirely optional and may be skipped if license details are not available.')}
                          </p>
                        </div>
                        <div className="grid gap-4 md:grid-cols-3">
                          <Field
                            label={text('رقم الترخيص', 'License number')}
                            value={formData.company.licenseNumber}
                            onChange={(value) => updateSectionField('company', 'licenseNumber', value.toUpperCase().slice(0, 30))}
                          />
                          <Field
                            label={text('صادر من', 'Issued by')}
                            value={formData.company.licenseIssuedBy}
                            onChange={(value) => updateSectionField('company', 'licenseIssuedBy', value)}
                          />
                          <Field
                            label={text('تاريخ الترخيص', 'License date')}
                            type="date"
                            value={formData.company.licenseIssueDate}
                            onChange={(value) => updateSectionField('company', 'licenseIssueDate', value)}
                            helperText={formData.company.licenseIssueDate ? `${text('هجري', 'Hijri')}: ${toHijriDate(formData.company.licenseIssueDate, isArabic)}` : ''}
                          />
                        </div>
                      </div>

                      <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-3">
                        <SelectField label={text('نوع المنشأة', 'Establishment type')} value={formData.company.establishmentType} onChange={(value) => {
                          updateSectionField('company', 'establishmentType', value);
                          if (value !== 'أخرى') updateSectionField('company', 'establishmentTypeOther', '');
                        }} options={['شركة', 'مؤسسة', 'معهد', 'مدرسة', 'جمعية', 'مستشفى', 'متجر', 'جهة حكومية', 'أخرى']} getOptionLabel={(option) => trOption(option)} />
                        {formData.company.establishmentType === 'أخرى' ? (
                          <Field label={text('يرجى التوضيح', 'Please specify')} value={formData.company.establishmentTypeOther} onChange={(value) => updateSectionField('company', 'establishmentTypeOther', value)} error={errors.companyEstablishmentTypeOther} />
                        ) : null}
                        <Field label={text('المقر الرئيسي', 'Head office')} value={formData.company.headOffice} onChange={(value) => updateSectionField('company', 'headOffice', value)} />
                        <Field label={text('اسم المسؤول المباشر', 'Direct supervisor name')} value={formData.company.directSupervisor} onChange={(value) => updateSectionField('company', 'directSupervisor', value)} />
                        <Field label={text('مسؤول الموارد البشرية أو الشخص المسؤول', 'HR or responsible contact')} value={formData.company.hrContact} onChange={(value) => updateSectionField('company', 'hrContact', value)} />
                        <Field label={text('رقم هاتف المنشأة', 'Establishment phone')} value={formData.company.phone} onChange={(value) => updateSectionField('company', 'phone', digitsOnly(value).slice(0, 10))} inputMode="tel" maxLength={10} />
                        <Field label={text('البريد الإلكتروني الرسمي', 'Official email')} type="email" value={formData.company.email} onChange={(value) => updateSectionField('company', 'email', value)} />
                        <Field label={text('الموقع الإلكتروني', 'Website')} value={formData.company.website} onChange={(value) => updateSectionField('company', 'website', value)} placeholder="https://example.com" />
                        <SelectField label={text('القطاع', 'Sector')} value={formData.company.sector} onChange={(value) => {
                          updateSectionField('company', 'sector', value);
                          if (value !== 'أخرى') updateSectionField('company', 'sectorOther', '');
                        }} options={['تعليمي', 'طبي', 'تقني', 'تجاري', 'مقاولات', 'ضيافة', 'تجزئة', 'صناعي', 'أخرى']} getOptionLabel={(option) => trOption(option)} />
                        {formData.company.sector === 'أخرى' ? (
                          <Field label={text('يرجى التوضيح', 'Please specify')} value={formData.company.sectorOther} onChange={(value) => updateSectionField('company', 'sectorOther', value)} error={errors.companySectorOther} />
                        ) : null}
                        <SelectField label={text('حجم المنشأة', 'Establishment size')} value={formData.company.companySize} onChange={(value) => updateSectionField('company', 'companySize', value)} options={['', 'صغيرة', 'متوسطة', 'كبيرة']} getOptionLabel={(option) => {
                          if (!option) return text('اختر الحجم إن لزم', 'Select size if relevant');
                          return trOption(option);
                        }} />
                      </div>
                    </div>
                  )}

                  {currentStep === 2 && (
                    <div className="space-y-8">
                      <div className="rounded-[28px] border border-[#C8A870]/18 bg-[#f8f6f1] p-6 sm:p-7">
                        <div className="mb-6 border-b border-[#C8A870]/14 pb-4">
                          <h3 className="text-xl font-black leading-9 text-[#1F3B64] sm:text-2xl">
                            {text('تأسيس العلاقة التعاقدية', 'Contractual Relationship Foundation')}
                          </h3>
                          <p className="mt-2 text-sm leading-8 text-slate-600 sm:text-[15px]">
                            {text('يوضّح هذا القسم كيف بدأت العلاقة مع جهة العمل، وما نوع العقد أو الاتفاق، وكيف كان تنظيم العمل من البداية.', 'This section explains how the relationship started, the type of contract or agreement, and how the work was structured from the outset.')}
                          </p>
                        </div>

                        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
                          <SelectField label={text('ما نوع العلاقة أو الاتفاق بينك وبين جهة العمل؟', 'What type of relationship or agreement did you have with the employer?')} value={formData.contract.contractType} onChange={(value) => {
                            updateSectionField('contract', 'contractType', value);
                            if (value !== 'أخرى') updateSectionField('contract', 'contractTypeOther', '');
                          }} options={['عقد عمل', 'عقد تعاون', 'عمل جزئي', 'عمل بالعمولة', 'تدريب', 'Freelancer / مستقل', 'بدون عقد مكتوب', 'أخرى']} getOptionLabel={(option) => trOption(option)} />
                          {formData.contract.contractType === 'أخرى' ? (
                            <Field label={text('يرجى التوضيح', 'Please specify')} value={formData.contract.contractTypeOther} onChange={(value) => updateSectionField('contract', 'contractTypeOther', value)} error={errors.contractTypeOther} />
                          ) : null}
                          <Field label={text('تاريخ توقيع العقد أو الاتفاق', 'Contract or agreement date')} type="date" value={formData.contract.contractDate} onChange={(value) => updateSectionField('contract', 'contractDate', value)} helperText={formData.contract.contractDate ? `${text('هجري', 'Hijri')}: ${toHijriDate(formData.contract.contractDate, isArabic)}` : ''} />
                          <Field required label={text('تاريخ مباشرة العمل', 'Work start date')} type="date" value={formData.contract.startDate} onChange={(value) => updateSectionField('contract', 'startDate', value)} error={errors.contractStartDate} helperText={formData.contract.startDate ? `${text('هجري', 'Hijri')}: ${toHijriDate(formData.contract.startDate, isArabic)}` : ''} />
                          <SelectField label={text('هل العقد محدد المدة أم غير محدد المدة؟', 'Is the contract fixed-term or indefinite?')} value={formData.contract.contractDurationType} onChange={(value) => {
                            updateSectionField('contract', 'contractDurationType', value);
                            if (value !== 'محدد المدة') updateSectionField('contract', 'contractEndDate', '');
                          }} options={['محدد المدة', 'غير محدد المدة', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                          {formData.contract.contractDurationType === 'محدد المدة' ? (
                            <Field label={text('تاريخ نهاية العقد', 'Contract end date')} type="date" value={formData.contract.contractEndDate} onChange={(value) => updateSectionField('contract', 'contractEndDate', value)} error={errors.contractEndDate} helperText={formData.contract.contractEndDate ? `${text('هجري', 'Hijri')}: ${toHijriDate(formData.contract.contractEndDate, isArabic)}` : ''} />
                          ) : null}
                          <SelectField label={text('هل كانت هناك فترة تجربة؟', 'Was there a probation period?')} value={formData.contract.trialPeriod} onChange={(value) => {
                            updateSectionField('contract', 'trialPeriod', value);
                            if (value !== 'نعم') {
                              updateSectionField('contract', 'trialPeriodLength', '');
                              updateSectionField('contract', 'trialPeriodExtended', 'لا أعلم');
                            }
                          }} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                          {formData.contract.trialPeriod === 'نعم' ? (
                            <>
                              <Field label={text('مدة التجربة', 'Probation period length')} value={formData.contract.trialPeriodLength} onChange={(value) => updateSectionField('contract', 'trialPeriodLength', value)} error={errors.contractTrialPeriodLength} />
                              <SelectField label={text('هل تم تمديدها؟', 'Was it extended?')} value={formData.contract.trialPeriodExtended} onChange={(value) => updateSectionField('contract', 'trialPeriodExtended', value)} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                            </>
                          ) : null}
                          <SelectField label={text('كيف كنت تستلم الراتب؟', 'How did you receive your salary?')} value={formData.contract.salaryReceiptMethod} onChange={(value) => {
                            updateSectionField('contract', 'salaryReceiptMethod', value);
                            if (value !== 'نقداً') {
                              updateSectionField('contract', 'cashPayrollSignoff', 'لا أعلم');
                              updateSectionField('contract', 'cashReceiptVoucher', 'لا أعلم');
                              updateSectionField('contract', 'cashWitnesses', 'لا أعلم');
                              updateSectionField('contract', 'cashMonthlyRegularity', 'لا أعلم');
                            }
                          }} options={['تحويل بنكي', 'نقداً', 'شيكات', 'جزء بنكي وجزء نقدي', 'لا يوجد راتب ثابت']} getOptionLabel={(option) => trOption(option)} />
                          <SelectField label={text('ما طبيعة الدوام؟', 'What was the work schedule?')} value={formData.contract.workPattern} onChange={(value) => {
                            updateSectionField('contract', 'workPattern', value);
                            if (!['دوام جزئي', 'عمل مرن', 'غير منتظم'].includes(value)) {
                              updateSectionField('contract', 'workPatternDetails', '');
                            }
                          }} options={['دوام كامل حضوري', 'دوام جزئي', 'عمل مرن', 'عمل عن بعد', 'شفتات', 'غير منتظم']} getOptionLabel={(option) => trOption(option)} />
                          {workPatternNeedsDetails ? (
                            <div className="xl:col-span-2 animate-in fade-in-0 slide-in-from-top-1 duration-300">
                              <TextAreaField
                                label={text('اشرح طبيعة الدوام وكيف كان العمل يتم فعلياً', 'Explain the work pattern and how the work was actually carried out')}
                                value={formData.contract.workPatternDetails}
                                onChange={(value) => updateSectionField('contract', 'workPatternDetails', value)}
                                error={errors.contractWorkPatternDetails}
                                rows={4}
                              />
                            </div>
                          ) : null}
                          {formData.contract.salaryReceiptMethod === 'نقداً' ? (
                            <div className="xl:col-span-4 animate-in fade-in-0 slide-in-from-top-1 duration-300">
                              <div className="rounded-[24px] border border-[#C8A870]/18 bg-white p-5">
                                <div className="mb-4 border-b border-[#C8A870]/12 pb-3">
                                  <h4 className="text-lg font-black text-[#1F3B64]">{text('تفاصيل استلام الراتب نقداً', 'Cash salary receipt details')}</h4>
                                </div>
                                <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
                                  <SelectField label={text('هل كنت توقّع على مسير رواتب؟', 'Did you sign payroll sheets?')} value={formData.contract.cashPayrollSignoff} onChange={(value) => updateSectionField('contract', 'cashPayrollSignoff', value)} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                                  <SelectField label={text('هل كنت تستلم إيصالاً أو سند قبض؟', 'Did you receive a receipt or payment voucher?')} value={formData.contract.cashReceiptVoucher} onChange={(value) => updateSectionField('contract', 'cashReceiptVoucher', value)} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                                  <SelectField label={text('هل يوجد شهود على الاستلام؟', 'Are there witnesses to the receipt?')} value={formData.contract.cashWitnesses} onChange={(value) => updateSectionField('contract', 'cashWitnesses', value)} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                                  <SelectField label={text('هل كان الاستلام منتظماً شهرياً؟', 'Was the receipt regular on a monthly basis?')} value={formData.contract.cashMonthlyRegularity} onChange={(value) => updateSectionField('contract', 'cashMonthlyRegularity', value)} options={['نعم', 'لا', 'أحياناً', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                                </div>
                              </div>
                            </div>
                          ) : null}
                        </div>
                      </div>

                      <div className="rounded-[28px] border border-[#C8A870]/18 bg-[#f8f6f1] p-6 sm:p-7">
                        <div className="mb-6 border-b border-[#C8A870]/14 pb-4">
                          <h3 className="text-xl font-black leading-9 text-[#1F3B64] sm:text-2xl">{text('تفاصيل العلاقة الفعلية وظروف العمل', 'Actual Relationship Details and Working Conditions')}</h3>
                          <p className="mt-2 text-sm leading-8 text-slate-600 sm:text-[15px]">
                            {text('يركز هذا القسم على كيفية ممارسة العمل فعلياً، وما إذا كانت توجد مخالفات أو وقائع مهمة تساعد على فهم النزاع وتقييم قوة المطالبات.', 'This section focuses on how work was actually carried out and whether there were violations or important facts that help assess the dispute and its strength.')}
                          </p>
                        </div>
                        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
                          <SelectField label={text('هل كنت تمارس مهام تختلف عن المسمى الوظيفي؟', 'Did you perform duties different from your job title?')} value={formData.contract.dutiesDifferent} onChange={(value) => {
                            updateSectionField('contract', 'dutiesDifferent', value);
                            if (value !== 'نعم') updateSectionField('contract', 'dutiesDifferenceDetails', '');
                          }} options={['نعم', 'لا']} getOptionLabel={(option) => trOption(option)} />
                          {formData.contract.dutiesDifferent === 'نعم' ? (
                            <div className="xl:col-span-2 animate-in fade-in-0 slide-in-from-top-1 duration-300">
                              <TextAreaField label={text('اشرح طبيعة المهام الفعلية', 'Explain the actual duties performed')} value={formData.contract.dutiesDifferenceDetails} onChange={(value) => updateSectionField('contract', 'dutiesDifferenceDetails', value)} error={errors.contractDutiesDifferenceDetails} rows={4} />
                            </div>
                          ) : null}
                          <SelectField label={text('هل تم تكليفك بأعمال إضافية خارج طبيعة عملك؟', 'Were you assigned additional work outside the nature of your role?')} value={formData.contract.outsideAssignments} onChange={(value) => {
                            updateSectionField('contract', 'outsideAssignments', value);
                            if (value !== 'نعم') {
                              updateSectionField('contract', 'outsideAssignmentsDetails', '');
                              updateSectionField('contract', 'outsideAssignmentsCompensated', 'لا أعلم');
                            }
                          }} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                          <SelectField label={text('هل يوجد تواصل واتساب أو إيميلات تخص العمل؟', 'Was there WhatsApp or email communication related to work?')} value={formData.contract.workCommunications} onChange={(value) => updateSectionField('contract', 'workCommunications', value)} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                          <SelectField label={text('هل كنت تعمل ساعات تتجاوز المتفق عليه؟', 'Did you work hours beyond what was agreed?')} value={formData.contract.overtimeBeyondAgreed} onChange={(value) => {
                            updateSectionField('contract', 'overtimeBeyondAgreed', value);
                            if (value !== 'نعم') updateSectionField('contract', 'overtimeCompensated', 'لا أعلم');
                          }} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                          {formData.contract.overtimeBeyondAgreed === 'نعم' ? (
                            <SelectField label={text('هل كان يتم احتسابها أو تعويضها؟', 'Was it calculated or compensated?')} value={formData.contract.overtimeCompensated} onChange={(value) => updateSectionField('contract', 'overtimeCompensated', value)} options={['نعم', 'لا', 'جزئياً', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                          ) : null}
                          <SelectField label={text('هل كان يوجد تأخير متكرر في صرف الرواتب؟', 'Was there repeated delay in paying salaries?')} value={formData.contract.salaryDelayRepeated} onChange={(value) => {
                            updateSectionField('contract', 'salaryDelayRepeated', value);
                            if (value !== 'نعم') updateSectionField('contract', 'salaryDelayAverage', '');
                          }} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                          {formData.contract.salaryDelayRepeated === 'نعم' ? (
                            <Field label={text('كم متوسط التأخير؟', 'What was the average delay?')} value={formData.contract.salaryDelayAverage} onChange={(value) => updateSectionField('contract', 'salaryDelayAverage', value)} className="xl:col-span-2" />
                          ) : null}
                          <SelectField label={text('هل سبق أن خُصم من راتبك؟', 'Were deductions ever made from your salary?')} value={formData.contract.salaryDeductionOccurred} onChange={(value) => {
                            updateSectionField('contract', 'salaryDeductionOccurred', value);
                            if (value !== 'نعم') updateSectionField('contract', 'salaryDeductionExplained', 'لا أعلم');
                          }} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                          {formData.contract.salaryDeductionOccurred === 'نعم' ? (
                            <SelectField label={text('هل تم توضيح سبب الخصم؟', 'Was the reason for the deduction explained?')} value={formData.contract.salaryDeductionExplained} onChange={(value) => updateSectionField('contract', 'salaryDeductionExplained', value)} options={['نعم', 'لا', 'جزئياً', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                          ) : null}
                          <SelectField label={text('هل كانت توجد عمولات أو حوافز أو بدلات لم تُصرف؟', 'Were there commissions, incentives, or allowances that were not paid?')} value={formData.contract.unpaidBenefitsOrCommissions} onChange={(value) => {
                            updateSectionField('contract', 'unpaidBenefitsOrCommissions', value);
                            if (value !== 'نعم') updateSectionField('contract', 'unpaidBenefitsDetails', '');
                          }} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                          {formData.contract.unpaidBenefitsOrCommissions === 'نعم' ? (
                            <div className="xl:col-span-2 animate-in fade-in-0 slide-in-from-top-1 duration-300">
                              <TextAreaField label={text('اشرح العمولات أو الحوافز أو البدلات غير المصروفة', 'Explain the unpaid commissions, incentives, or allowances')} value={formData.contract.unpaidBenefitsDetails} onChange={(value) => updateSectionField('contract', 'unpaidBenefitsDetails', value)} error={errors.contractUnpaidBenefitsDetails} rows={4} />
                            </div>
                          ) : null}
                          <SelectField label={text('هل تعرضت لأي تهديد بالفصل أو الضغط للاستقالة؟', 'Were you threatened with dismissal or pressured to resign?')} value={formData.contract.threatenedOrPressured} onChange={(value) => {
                            updateSectionField('contract', 'threatenedOrPressured', value);
                            if (value !== 'نعم') updateSectionField('contract', 'threatenedOrPressuredDetails', '');
                          }} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                          {formData.contract.threatenedOrPressured === 'نعم' ? (
                            <div className="xl:col-span-2 animate-in fade-in-0 slide-in-from-top-1 duration-300">
                              <TextAreaField label={text('وضّح واقعة التهديد أو الضغط', 'Explain the threat or pressure incident')} value={formData.contract.threatenedOrPressuredDetails} onChange={(value) => updateSectionField('contract', 'threatenedOrPressuredDetails', value)} error={errors.contractThreatenedOrPressuredDetails} rows={4} />
                            </div>
                          ) : null}
                          <SelectField label={text('هل سبق أن طُلب منك توقيع مستندات دون تسليم نسخة؟', 'Were you ever asked to sign documents without receiving a copy?')} value={formData.contract.signedWithoutCopy} onChange={(value) => {
                            updateSectionField('contract', 'signedWithoutCopy', value);
                            if (value !== 'نعم') updateSectionField('contract', 'signedWithoutCopyDetails', '');
                          }} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                          {formData.contract.signedWithoutCopy === 'نعم' ? (
                            <div className="xl:col-span-2 animate-in fade-in-0 slide-in-from-top-1 duration-300">
                              <TextAreaField label={text('اشرح المستندات التي طُلب منك توقيعها دون تسليم نسخة', 'Explain which documents you were asked to sign without receiving a copy')} value={formData.contract.signedWithoutCopyDetails} onChange={(value) => updateSectionField('contract', 'signedWithoutCopyDetails', value)} error={errors.contractSignedWithoutCopyDetails} rows={4} />
                            </div>
                          ) : null}
                          <SelectField label={text('هل كانت جهة العمل تلتزم بالعقد الموقّع؟', 'Did the employer comply with the signed contract?')} value={formData.contract.employerCommittedToContract} onChange={(value) => {
                            updateSectionField('contract', 'employerCommittedToContract', value);
                            if (value !== 'لا') updateSectionField('contract', 'employerBreachDetails', '');
                          }} options={['نعم', 'لا', 'جزئياً', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                          {formData.contract.employerCommittedToContract === 'لا' ? (
                            <div className="xl:col-span-2 animate-in fade-in-0 slide-in-from-top-1 duration-300">
                              <TextAreaField label={text('وضّح أوجه عدم الالتزام بالعقد', 'Explain the aspects of non-compliance with the contract')} value={formData.contract.employerBreachDetails} onChange={(value) => updateSectionField('contract', 'employerBreachDetails', value)} error={errors.contractEmployerBreachDetails} rows={4} />
                            </div>
                          ) : null}
                          {formData.contract.outsideAssignments === 'نعم' ? (
                            <>
                              <div className="xl:col-span-2 animate-in fade-in-0 slide-in-from-top-1 duration-300">
                                <TextAreaField
                                  label={text('اشرح طبيعة التكليفات أو الأعمال الإضافية', 'Explain the additional assignments or duties')}
                                  value={formData.contract.outsideAssignmentsDetails}
                                  onChange={(value) => updateSectionField('contract', 'outsideAssignmentsDetails', value)}
                                  error={errors.contractOutsideAssignmentsDetails}
                                  rows={4}
                                />
                              </div>
                              <SelectField label={text('هل كانت بمقابل إضافي؟', 'Was it compensated separately?')} value={formData.contract.outsideAssignmentsCompensated} onChange={(value) => updateSectionField('contract', 'outsideAssignmentsCompensated', value)} options={['نعم', 'لا', 'جزئياً', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                            </>
                          ) : null}
                        </div>
                      </div>

                      {contractNeedsLaborAssessment ? (
                        <div className="animate-in fade-in-0 slide-in-from-top-1 duration-300">
                          <ChecklistField title={text('مؤشرات تساعد على إثبات أن العلاقة في حقيقتها علاقة عمالية', 'Indicators that help establish the relationship as an employment relationship')} items={relationIndicators} selectedItems={formData.contract.laborIndicators} onToggle={(item) => updateChecklistValue('contract', 'laborIndicators', item)} getItemLabel={(item) => trOption(item, 'indicators')} />
                        </div>
                      ) : null}

                      <div className="rounded-[28px] border border-[#C8A870]/18 bg-[#f8f6f1] p-6 sm:p-7">
                        <div className="mb-6 border-b border-[#C8A870]/14 pb-4">
                          <h3 className="text-xl font-black leading-9 text-[#1F3B64] sm:text-2xl">{text('توثيق العقد وإثبات الاتفاق', 'Contract Documentation and Proof of Agreement')}</h3>
                          <p className="mt-2 text-sm leading-8 text-slate-600 sm:text-[15px]">
                            {text('يوضح هذا الجزء مدى توثيق العلاقة تعاقدياً، وما إذا كانت البيانات النظامية مطابقة للواقع أو توجد مستندات ورسائل بديلة للإثبات.', 'This section clarifies how the relationship was documented and whether official records match reality or require alternative proof.')}
                          </p>
                        </div>
                        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
                          <SelectField label={text('هل العقد موثق في قوى؟', 'Is the contract documented in Qiwa?')} value={formData.contract.qiwaContract} onChange={(value) => updateSectionField('contract', 'qiwaContract', value)} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                          {formData.contract.qiwaContract === 'نعم' ? (
                            <div className="contents animate-in fade-in-0 slide-in-from-top-1 duration-300">
                              <SelectField label={text('هل البيانات المسجلة في قوى مطابقة للواقع؟', 'Do the Qiwa details match reality?')} value={formData.contract.qiwaMatchesReality} onChange={(value) => updateSectionField('contract', 'qiwaMatchesReality', value)} options={['نعم', 'لا', 'جزئياً', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                              <SelectField label={text('هل الراتب المسجل صحيح؟', 'Is the recorded salary correct?')} value={formData.contract.qiwaSalaryAccurate} onChange={(value) => updateSectionField('contract', 'qiwaSalaryAccurate', value)} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                              <SelectField label={text('هل المسمى الوظيفي صحيح؟', 'Is the recorded job title correct?')} value={formData.contract.qiwaTitleAccurate} onChange={(value) => updateSectionField('contract', 'qiwaTitleAccurate', value)} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                            </div>
                          ) : null}
                          {formData.contract.qiwaContract === 'لا' ? (
                            <div className="contents animate-in fade-in-0 slide-in-from-top-1 duration-300">
                              <SelectField label={text('هل لديك نسخة من العقد؟', 'Do you have a copy of the contract?')} value={formData.contract.contractCopyAvailable} onChange={(value) => updateSectionField('contract', 'contractCopyAvailable', value)} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                              <SelectField label={text('هل استلمت نسخة عند التوقيع؟', 'Did you receive a signed copy?')} value={formData.contract.receivedSignedCopy} onChange={(value) => updateSectionField('contract', 'receivedSignedCopy', value)} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                              <SelectField label={text('هل كان التوقيع إلكترونياً أم ورقياً؟', 'Was the signing electronic or paper-based?')} value={formData.contract.signingMethod} onChange={(value) => updateSectionField('contract', 'signingMethod', value)} options={['إلكتروني', 'ورقي', 'كلاهما', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                              <SelectField label={text('هل توجد رسائل أو إيميلات تثبت الاتفاق؟', 'Are there messages or emails proving the agreement?')} value={formData.contract.messagesProofAvailable} onChange={(value) => updateSectionField('contract', 'messagesProofAvailable', value)} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                            </div>
                          ) : null}
                        </div>
                      </div>

                      <div className="rounded-[28px] border border-[#C8A870]/18 bg-[#f8f6f1] p-6 sm:p-7">
                        <div className="mb-6 border-b border-[#C8A870]/14 pb-4">
                          <h3 className="text-xl font-black leading-9 text-[#1F3B64] sm:text-2xl">{text('تغيّر شروط العمل أثناء العلاقة', 'Changes in Employment Terms During the Relationship')}</h3>
                          <p className="mt-2 text-sm leading-8 text-slate-600 sm:text-[15px]">
                            {text('إذا تغيرت شروط العمل أثناء العلاقة، ستظهر فقط الأسئلة اللازمة لفهم التغيير وتقييم أثره القانوني.', 'If the work terms changed during the relationship, only the relevant follow-up questions will appear to evaluate their legal impact.')}
                          </p>
                        </div>
                        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
                          <SelectField label={text('هل تغيرت شروط العمل أثناء العلاقة؟', 'Did the employment terms change during the relationship?')} value={formData.contract.conditionsChanged} onChange={(value) => {
                            updateSectionField('contract', 'conditionsChanged', value);
                            if (value !== 'نعم') {
                              updateSectionField('contract', 'salaryChanged', false);
                              updateSectionField('contract', 'titleChanged', false);
                              updateSectionField('contract', 'hoursChanged', false);
                              updateSectionField('contract', 'workplaceChanged', false);
                              updateSectionField('contract', 'dutiesChanged', false);
                              updateSectionField('contract', 'otherChanged', false);
                              updateSectionField('contract', 'otherChangedDetails', '');
                              updateSectionField('contract', 'changeOccurredAt', '');
                              updateSectionField('contract', 'changeWasWritten', 'لا أعلم');
                              updateSectionField('contract', 'changeApproved', 'لا أعلم');
                              updateSectionField('contract', 'changeProofAvailable', 'لا أعلم');
                            }
                          }} options={['نعم', 'لا']} getOptionLabel={(option) => trOption(option)} error={errors.contractConditionsChanged} />
                          <SelectField label={text('هل سبق تعديل أو تجديد العقد؟', 'Was the contract ever amended or renewed?')} value={formData.contract.contractRenewed} onChange={(value) => {
                            updateSectionField('contract', 'contractRenewed', value);
                            if (value !== 'نعم') updateSectionField('contract', 'contractRenewalDocument', 'لا أعلم');
                          }} options={['نعم', 'لا']} getOptionLabel={(option) => trOption(option)} />
                          {formData.contract.contractRenewed === 'نعم' ? (
                            <div className="animate-in fade-in-0 slide-in-from-top-1 duration-300">
                              <SelectField label={text('هل لديك نسخة من التعديل أو الملحق؟', 'Do you have a copy of the amendment or addendum?')} value={formData.contract.contractRenewalDocument} onChange={(value) => updateSectionField('contract', 'contractRenewalDocument', value)} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                            </div>
                          ) : null}
                        </div>

                        {formData.contract.conditionsChanged === 'نعم' ? (
                          <div className="mt-6 animate-in fade-in-0 slide-in-from-top-1 duration-300">
                            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                              <ToggleBox title={text('تغير الراتب', 'Salary changed')} checked={formData.contract.salaryChanged} onChange={(checked) => updateSectionField('contract', 'salaryChanged', checked)} />
                              <ToggleBox title={text('تغير المسمى الوظيفي', 'Job title changed')} checked={formData.contract.titleChanged} onChange={(checked) => updateSectionField('contract', 'titleChanged', checked)} />
                              <ToggleBox title={text('تغيرت ساعات العمل', 'Working hours changed')} checked={formData.contract.hoursChanged} onChange={(checked) => updateSectionField('contract', 'hoursChanged', checked)} />
                              <ToggleBox title={text('تغير مكان العمل', 'Workplace changed')} checked={formData.contract.workplaceChanged} onChange={(checked) => updateSectionField('contract', 'workplaceChanged', checked)} />
                              <ToggleBox title={text('تغيرت طبيعة المهام', 'Nature of duties changed')} checked={formData.contract.dutiesChanged} onChange={(checked) => updateSectionField('contract', 'dutiesChanged', checked)} />
                              <ToggleBox title={text('غير ذلك', 'Other')} checked={formData.contract.otherChanged} onChange={(checked) => updateSectionField('contract', 'otherChanged', checked)} />
                            </div>
                            {errors.contractConditionsChanged ? <p className="mt-3 text-sm font-medium text-red-500">{errors.contractConditionsChanged}</p> : null}
                            <div className="mt-5 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
                              <Field label={text('متى حدث التغيير؟', 'When did the change occur?')} type="date" value={formData.contract.changeOccurredAt} onChange={(value) => updateSectionField('contract', 'changeOccurredAt', value)} helperText={formData.contract.changeOccurredAt ? `${text('هجري', 'Hijri')}: ${toHijriDate(formData.contract.changeOccurredAt, isArabic)}` : ''} />
                              <SelectField label={text('هل كان التغيير مكتوباً؟', 'Was the change documented in writing?')} value={formData.contract.changeWasWritten} onChange={(value) => updateSectionField('contract', 'changeWasWritten', value)} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                              <SelectField label={text('هل وافقت على التغيير؟', 'Did you agree to the change?')} value={formData.contract.changeApproved} onChange={(value) => updateSectionField('contract', 'changeApproved', value)} options={['نعم', 'لا', 'جزئياً', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                              <SelectField label={text('هل لديك إثبات على التغيير؟', 'Do you have proof of the change?')} value={formData.contract.changeProofAvailable} onChange={(value) => updateSectionField('contract', 'changeProofAvailable', value)} options={['نعم', 'لا', 'جزئياً', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                              {formData.contract.otherChanged ? (
                                <div className="md:col-span-2 xl:col-span-4 animate-in fade-in-0 slide-in-from-top-1 duration-300">
                                  <TextAreaField label={text('اذكر طبيعة التغيير الآخر', 'Describe the other change')} value={formData.contract.otherChangedDetails} onChange={(value) => updateSectionField('contract', 'otherChangedDetails', value)} error={errors.contractOtherChangedDetails} rows={3} />
                                </div>
                              ) : null}
                            </div>
                          </div>
                        ) : null}
                      </div>

                      <div className="rounded-[28px] border border-[#C8A870]/18 bg-[#f8f6f1] p-6 sm:p-7">
                        <div className="mb-6 border-b border-[#C8A870]/14 pb-4">
                          <h3 className="text-xl font-black leading-9 text-[#1F3B64] sm:text-2xl">{text('انتهاء العلاقة العمالية', 'End of the Employment Relationship')}</h3>
                          <p className="mt-2 text-sm leading-8 text-slate-600 sm:text-[15px]">
                            {text('هذا القسم يرتبط بإجابتك السابقة في بيانات العميل، ويظهر فقط تفاصيل إنهاء العلاقة إذا كانت العلاقة قد انتهت فعلاً.', 'This section is linked to your answer in the client details step and only shows termination details if the relationship has actually ended.')}
                          </p>
                        </div>

                        <div className="space-y-6">
                          <div className="rounded-[22px] border border-[#C8A870]/18 bg-white px-5 py-5">
                            <p className="text-lg font-black text-[#1F3B64]">{text('حالة العلاقة الحالية', 'Current Relationship Status')}</p>
                            <p className="mt-3 text-[15px] leading-8 text-slate-600">
                              {relationshipEnded
                                ? (isArabic ? `تم تحديد أن العلاقة منتهية بناءً على إجابتكم في بيانات العميل${formData.client.lastWorkingDay ? `، وآخر يوم عمل: ${toDisplayDate(formData.client.lastWorkingDay, 'ar-SA')}` : ''}.` : `The relationship is marked as ended based on the client details step${formData.client.lastWorkingDay ? `, with the last working day recorded as ${toDisplayDate(formData.client.lastWorkingDay, 'en-US')}` : ''}.`)
                                : (text('تم تحديد أن العلاقة ما زالت قائمة بناءً على إجابتكم في بيانات العميل.', 'The relationship is marked as active based on the client details step.'))}
                            </p>
                          </div>

                          {relationshipEnded ? (
                            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4 animate-in fade-in-0 slide-in-from-top-1 duration-300">
                              <Field label={text('تاريخ آخر يوم عمل', 'Last working day')} type="date" value={formData.client.lastWorkingDay} onChange={(value) => updateSectionField('client', 'lastWorkingDay', value)} helperText={formData.client.lastWorkingDay ? `${text('هجري', 'Hijri')}: ${toHijriDate(formData.client.lastWorkingDay, isArabic)}` : ''} />
                              <SelectField required label={text('سبب انتهاء العلاقة', 'Reason for ending the relationship')} value={formData.contract.endReason} onChange={(value) => {
                                updateSectionField('contract', 'endReason', value);
                                if (value !== 'أخرى') updateSectionField('contract', 'endReasonOther', '');
                              }} error={errors.contractEndReason} options={['استقالة', 'إنهاء باتفاق الطرفين', 'فسخ العقد بموجب المادة 80', 'فسخ العقد بموجب المادة 81', 'أخرى']} getOptionLabel={(option) => trOption(option)} />
                              {formData.contract.endReason === 'أخرى' ? (
                                <Field required label={text('توضيح سبب انتهاء العلاقة', 'Specify the reason for ending the relationship')} value={formData.contract.endReasonOther} onChange={(value) => updateSectionField('contract', 'endReasonOther', value)} error={errors.contractEndReasonOther} />
                              ) : null}
                              <SelectField label={text('هل تم إشعارك؟', 'Were you notified?')} value={formData.contract.noticeProvided} onChange={(value) => {
                                updateSectionField('contract', 'noticeProvided', value);
                                if (value !== 'نعم') {
                                  updateSectionField('contract', 'noticeDeliveryMethod', '');
                                  updateSectionField('contract', 'noticeDate', '');
                                  updateSectionField('contract', 'noticePeriod', '');
                                  updateSectionField('contract', 'noticeCopyRetained', 'لا أعلم');
                                }
                              }} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                              <SelectField label={text('هل استلمت مخالصة؟', 'Did you receive a release or settlement?')} value={formData.contract.releaseReceived} onChange={(value) => {
                                updateSectionField('contract', 'releaseReceived', value);
                                if (value !== 'نعم') {
                                  updateSectionField('contract', 'releaseDate', '');
                                  updateSectionField('contract', 'releaseCopyReceived', 'لا أعلم');
                                  updateSectionField('contract', 'releaseSignedVoluntarily', 'لا أعلم');
                                  updateSectionField('contract', 'releaseSignedTiming', '');
                                  updateSectionField('contract', 'releaseFormat', '');
                                }
                              }} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                              <SelectField label={text('هل تم صرف جميع مستحقاتك؟', 'Were all your dues paid?')} value={formData.contract.allDuesPaid} onChange={(value) => updateSectionField('contract', 'allDuesPaid', value)} options={['نعم', 'لا', 'جزئياً', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                              <SelectField label={text('هل طُلب منك توقيع مستندات؟', 'Were you asked to sign documents?')} value={formData.contract.signedDocumentsRequested} onChange={(value) => updateSectionField('contract', 'signedDocumentsRequested', value)} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                              <SelectField label={text('هل تم تسليمك شهادة الخدمة؟', 'Were you given a service certificate?')} value={formData.contract.experienceCertificateReceived} onChange={(value) => {
                                updateSectionField('contract', 'experienceCertificateReceived', value);
                                if (value !== 'نعم') updateSectionField('contract', 'experienceCertificateDate', '');
                              }} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                              <SelectField label={text('هل تم عمل إخلاء طرف؟', 'Was a clearance completed?')} value={formData.contract.clearanceCompleted} onChange={(value) => {
                                updateSectionField('contract', 'clearanceCompleted', value);
                                if (value !== 'نعم') {
                                  updateSectionField('contract', 'clearanceCopyReceived', 'لا أعلم');
                                  updateSectionField('contract', 'clearanceDate', '');
                                }
                              }} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                            </div>
                          ) : null}

                          {relationshipEnded && formData.contract.noticeProvided === 'نعم' ? (
                            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4 animate-in fade-in-0 slide-in-from-top-1 duration-300">
                              <SelectField label={text('كيف تم تسليم الإشعار؟', 'How was the notice delivered?')} value={formData.contract.noticeDeliveryMethod} onChange={(value) => updateSectionField('contract', 'noticeDeliveryMethod', value)} error={errors.contractNoticeDeliveryMethod} options={['ورقي', 'بريد إلكتروني', 'رسالة واتساب', 'رسالة نصية', 'داخل النظام', 'شفهي', 'أخرى']} getOptionLabel={(option) => trOption(option)} />
                              <Field label={text('تاريخ الإشعار', 'Notice date')} type="date" value={formData.contract.noticeDate} onChange={(value) => updateSectionField('contract', 'noticeDate', value)} error={errors.contractNoticeDate} helperText={formData.contract.noticeDate ? `${text('هجري', 'Hijri')}: ${toHijriDate(formData.contract.noticeDate, isArabic)}` : ''} />
                              <Field label={text('مدة الإشعار المذكورة', 'Stated notice period')} value={formData.contract.noticePeriod} onChange={(value) => updateSectionField('contract', 'noticePeriod', value)} />
                              <SelectField label={text('هل احتفظت بنسخة من الإشعار؟', 'Did you keep a copy of the notice?')} value={formData.contract.noticeCopyRetained} onChange={(value) => updateSectionField('contract', 'noticeCopyRetained', value)} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                            </div>
                          ) : null}

                          {relationshipEnded && formData.contract.releaseReceived === 'نعم' ? (
                            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4 animate-in fade-in-0 slide-in-from-top-1 duration-300">
                              <Field label={text('تاريخ توقيع المخالصة', 'Release signing date')} type="date" value={formData.contract.releaseDate} onChange={(value) => updateSectionField('contract', 'releaseDate', value)} error={errors.contractReleaseDate} helperText={formData.contract.releaseDate ? `${text('هجري', 'Hijri')}: ${toHijriDate(formData.contract.releaseDate, isArabic)}` : ''} />
                              <SelectField label={text('هل استلمت نسخة منها؟', 'Did you receive a copy?')} value={formData.contract.releaseCopyReceived} onChange={(value) => updateSectionField('contract', 'releaseCopyReceived', value)} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                              <SelectField label={text('هل تم توقيعها برضاك الكامل؟', 'Was it signed with your full consent?')} value={formData.contract.releaseSignedVoluntarily} onChange={(value) => updateSectionField('contract', 'releaseSignedVoluntarily', value)} options={['نعم', 'لا', 'جزئياً', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                              <SelectField label={text('هل تم توقيعها بعد استلام المستحقات أم قبلها؟', 'Was it signed before or after receiving the dues?')} value={formData.contract.releaseSignedTiming} onChange={(value) => updateSectionField('contract', 'releaseSignedTiming', value)} error={errors.contractReleaseSignedTiming} options={['بعد استلام المستحقات', 'قبل استلام المستحقات', 'جزء قبل وجزء بعد', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                              <SelectField label={text('هل كانت المخالصة ورقية أم إلكترونية؟', 'Was the release paper-based or electronic?')} value={formData.contract.releaseFormat} onChange={(value) => updateSectionField('contract', 'releaseFormat', value)} error={errors.contractReleaseFormat} options={['ورقية', 'إلكترونية', 'كلاهما', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                            </div>
                          ) : null}

                          {relationshipEnded && formData.contract.experienceCertificateReceived === 'نعم' ? (
                            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4 animate-in fade-in-0 slide-in-from-top-1 duration-300">
                              <Field label={text('تاريخ استلام شهادة الخدمة', 'Service certificate receipt date')} type="date" value={formData.contract.experienceCertificateDate} onChange={(value) => updateSectionField('contract', 'experienceCertificateDate', value)} error={errors.contractExperienceCertificateDate} helperText={formData.contract.experienceCertificateDate ? `${text('هجري', 'Hijri')}: ${toHijriDate(formData.contract.experienceCertificateDate, isArabic)}` : ''} />
                            </div>
                          ) : null}

                          {relationshipEnded && formData.contract.clearanceCompleted === 'نعم' ? (
                            <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4 animate-in fade-in-0 slide-in-from-top-1 duration-300">
                              <SelectField label={text('هل استلمت نسخة من إخلاء الطرف؟', 'Did you receive a copy of the clearance?')} value={formData.contract.clearanceCopyReceived} onChange={(value) => updateSectionField('contract', 'clearanceCopyReceived', value)} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                              <Field label={text('تاريخ إخلاء الطرف', 'Clearance date')} type="date" value={formData.contract.clearanceDate} onChange={(value) => updateSectionField('contract', 'clearanceDate', value)} error={errors.contractClearanceDate} helperText={formData.contract.clearanceDate ? `${text('هجري', 'Hijri')}: ${toHijriDate(formData.contract.clearanceDate, isArabic)}` : ''} />
                            </div>
                          ) : null}


                          <div className="rounded-[24px] border border-[#C8A870]/18 bg-white p-5">
                            <div className="mb-4 border-b border-[#C8A870]/12 pb-3">
                              <h4 className="text-lg font-black text-[#1F3B64]">{text('الأدلة والإثباتات المتوفرة', 'Available evidence and proof')}</h4>
                            </div>
                            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                              {evidenceOptions.map((item) => {
                                const checked = formData.contract.evidenceSources.includes(item);
                                return (
                                  <label key={item} className={`flex cursor-pointer items-start gap-3 rounded-2xl border px-4 py-3 transition ${checked ? 'border-[#A27849]/50 bg-[#fcfbf8] text-[#1F3B64]' : 'border-[#C8A870]/14 bg-white text-slate-700'}`}>
                                    <Checkbox checked={checked} onCheckedChange={() => updateChecklistValue('contract', 'evidenceSources', item)} className="mt-1 border-[#A27849] data-[state=checked]:bg-[#A27849] data-[state=checked]:text-white" />
                                    <span className="text-[15px] leading-8 sm:text-base">{trOption(item, 'evidence')}</span>
                                  </label>
                                );
                              })}
                            </div>
                            {formData.contract.evidenceSources.includes('أخرى') ? (
                              <div className="mt-5 animate-in fade-in-0 slide-in-from-top-1 duration-300">
                                <TextAreaField label={text('اذكر نوع الدليل الآخر', 'Specify the other type of evidence')} value={formData.contract.evidenceOther} onChange={(value) => updateSectionField('contract', 'evidenceOther', value)} error={errors.contractEvidenceOther} rows={3} />
                              </div>
                            ) : null}
                          </div>

                          <div className="rounded-[24px] border border-[#C8A870]/18 bg-white p-5">
                            <div className="mb-4 border-b border-[#C8A870]/12 pb-3">
                              <h4 className="text-lg font-black text-[#1F3B64]">{text('تفاصيل النزاع الحالي', 'Current dispute details')}</h4>
                            </div>
                            <TextAreaField label={text('اشرح بشكل مختصر طبيعة المشكلة الحالية مع جهة العمل.', 'Briefly explain the current issue with the employer.')} value={formData.contract.problemSummary} onChange={(value) => updateSectionField('contract', 'problemSummary', value)} error={errors.contractProblemSummary} helperText={text('يفضل ذكر الوقائع بالتسلسل الزمني وشرح المشكلة بشكل مختصر وواضح.', 'Preferably describe the facts in chronological order and explain the issue briefly and clearly.')} rows={5} />
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {currentStep === 3 && (
                    <div className="space-y-8">
                      <div className="rounded-[28px] border border-[#C8A870]/18 bg-[#f8f6f1] p-6 sm:p-7">
                        <div className="mb-6 border-b border-[#C8A870]/14 pb-4">
                          <h3 className="text-xl font-black leading-9 text-[#1F3B64] sm:text-2xl">{text('الأجر والمزايا المالية', 'Wage and Financial Benefits')}</h3>
                        </div>
                        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
                          <SelectField label={text('نوع الأجر', 'Wage type')} value={formData.compensation.wageType} onChange={(value) => updateSectionField('compensation', 'wageType', value)} options={['', 'أجر أساسي فقط', 'أجر شامل', 'أجر أساسي + بدلات']} getOptionLabel={(option) => (!option ? (text('اختر النوع', 'Select type')) : trOption(option))} />
                          <Field label={text('الأجر الأساسي', 'Basic wage')} value={formData.compensation.basicSalary} onChange={(value) => updateSectionField('compensation', 'basicSalary', value)} />
                          <Field label={text('إجمالي الأجر', 'Total wage')} value={formData.compensation.totalSalary} onChange={(value) => updateSectionField('compensation', 'totalSalary', value)} />
                          <Field label={text('تاريخ صرف الأجر', 'Wage payment date')} type="date" value={formData.compensation.payDate} onChange={(value) => updateSectionField('compensation', 'payDate', value)} />
                          <SelectField label={text('هل كان الأجر ثابتاً؟', 'Was the wage fixed?')} value={formData.compensation.wageFixed} onChange={(value) => updateSectionField('compensation', 'wageFixed', value)} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                          <SelectField label={text('هل حصل تغيير بالأجر؟', 'Did the wage change?')} value={formData.compensation.wageChanged} onChange={(value) => updateSectionField('compensation', 'wageChanged', value)} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                          <SelectField label={text('هل توجد خصومات متكررة؟', 'Were there recurring deductions?')} value={formData.compensation.recurringDeductions} onChange={(value) => updateSectionField('compensation', 'recurringDeductions', value)} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                          <Field label={text('عدد الأجور غير المسددة', 'Number of unpaid wages')} value={formData.compensation.unpaidSalaries} onChange={(value) => updateSectionField('compensation', 'unpaidSalaries', value)} />
                        </div>

                        {formData.compensation.wageType === 'أجر أساسي + بدلات' ? (
                          <div className="mt-6 grid gap-6 md:grid-cols-2 xl:grid-cols-4 animate-in fade-in-0 slide-in-from-top-1 duration-300">
                            <Field label={text('بدل سكن', 'Housing allowance')} value={formData.compensation.housingAllowance} onChange={(value) => updateSectionField('compensation', 'housingAllowance', value)} />
                            <Field label={text('بدل نقل', 'Transport allowance')} value={formData.compensation.transportAllowance} onChange={(value) => updateSectionField('compensation', 'transportAllowance', value)} />
                            <Field label={text('بدل انتقال', 'Mobility allowance')} value={formData.compensation.mobilityAllowance} onChange={(value) => updateSectionField('compensation', 'mobilityAllowance', value)} />
                            <Field label={text('بدل اتصال', 'Communication allowance')} value={formData.compensation.communicationAllowance} onChange={(value) => updateSectionField('compensation', 'communicationAllowance', value)} />
                            <Field label={text('بدل طبيعة عمل', 'Nature-of-work allowance')} value={formData.compensation.natureAllowance} onChange={(value) => updateSectionField('compensation', 'natureAllowance', value)} />
                            <Field label={text('بدل انتداب', 'Assignment allowance')} value={formData.compensation.assignmentAllowance} onChange={(value) => updateSectionField('compensation', 'assignmentAllowance', value)} />
                            <Field label={text('عمولات', 'Commissions')} value={formData.compensation.commissionsValue} onChange={(value) => updateSectionField('compensation', 'commissionsValue', value)} />
                            <Field label={text('حوافز', 'Incentives')} value={formData.compensation.incentivesValue} onChange={(value) => updateSectionField('compensation', 'incentivesValue', value)} />
                            <Field label={text('علاوة سنوية', 'Annual increment')} value={formData.compensation.annualIncrease} onChange={(value) => updateSectionField('compensation', 'annualIncrease', value)} />
                            <Field label={text('مكافآت دورية', 'Periodic bonuses')} value={formData.compensation.periodicBonuses} onChange={(value) => updateSectionField('compensation', 'periodicBonuses', value)} />
                          </div>
                        ) : null}

                        <div className="mt-6 grid gap-6 md:grid-cols-2 xl:grid-cols-4">
                          <div className="xl:col-span-2">
                            <SelectField label={text('طريقة استلام الأجر', 'Wage payment method')} value={formData.contract.salaryReceiptMethod} onChange={(value) => updateSectionField('contract', 'salaryReceiptMethod', value)} options={['تحويل بنكي', 'نقداً', 'شيكات', 'جزء بنكي وجزء نقدي', 'لا يوجد أجر ثابت']} getOptionLabel={(option) => trOption(option)} />
                          </div>
                          <SelectField label={text('هل يوجد تأمين طبي؟', 'Is there medical insurance?')} value={formData.compensation.medicalInsurance} onChange={(value) => {
                            updateSectionField('compensation', 'medicalInsurance', value);
                            if (value !== 'نعم') {
                              updateSectionField('compensation', 'medicalInsuranceFamily', 'لا أعلم');
                              updateSectionField('compensation', 'medicalInsuranceStopped', 'لا أعلم');
                            }
                          }} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                          {formData.compensation.medicalInsurance === 'نعم' ? (
                            <>
                              <SelectField label={text('هل يشمل الأسرة؟', 'Did it cover the family?')} value={formData.compensation.medicalInsuranceFamily} onChange={(value) => updateSectionField('compensation', 'medicalInsuranceFamily', value)} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                              <SelectField label={text('هل تم إيقافه أثناء العلاقة؟', 'Was it stopped during the relationship?')} value={formData.compensation.medicalInsuranceStopped} onChange={(value) => updateSectionField('compensation', 'medicalInsuranceStopped', value)} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                            </>
                          ) : null}
                        </div>

                        <div className="mt-6 rounded-[24px] border border-[#C8A870]/18 bg-white p-5">
                          <div className="mb-4 border-b border-[#C8A870]/12 pb-3">
                            <h4 className="text-lg font-black text-[#1F3B64]">{text('مطابقة الأجر في المستندات الرسمية', 'Wage Matching in Official Documents')}</h4>
                            <p className="mt-2 text-sm leading-7 text-slate-600">
                              {text('يساعد هذا الجزء على توضيح ما إذا كان الأجر الفعلي مطابقاً لما ورد في العقد أو في التأمينات الاجتماعية، دون تقديم أي تحليل قانوني داخل البوابة.', 'This section clarifies whether the actual wage matched what was recorded in the contract or in social insurance records, without providing any legal analysis inside the portal.')}
                            </p>
                          </div>
                          <div className="grid gap-6 md:grid-cols-2">
                            <SelectField label={text('هل الأجر المدون في العقد مطابق للأجر الذي كنت تستلمه فعلياً؟', 'Did the wage stated in the contract match the wage you actually received?')} value={formData.compensation.contractWageMatchesActual} onChange={(value) => {
                              updateSectionField('compensation', 'contractWageMatchesActual', value);
                              if (value !== 'لا') updateSectionField('compensation', 'contractWageDifferenceDetails', '');
                            }} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                            <SelectField label={text('هل الأجر المسجل في التأمينات الاجتماعية مطابق للأجر الفعلي؟', 'Did the wage registered in GOSI match the actual wage?')} value={formData.compensation.gosiWageMatchesActual} onChange={(value) => {
                              updateSectionField('compensation', 'gosiWageMatchesActual', value);
                              if (value !== 'لا') updateSectionField('compensation', 'gosiWageDifferenceDetails', '');
                            }} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                            {formData.compensation.contractWageMatchesActual === 'لا' ? (
                              <div className="animate-in fade-in-0 slide-in-from-top-1 duration-300">
                                <TextAreaField label={text('وضح الفرق بين الأجر الفعلي والأجر المدون بالعقد', 'Explain the difference between the actual wage and the wage stated in the contract')} value={formData.compensation.contractWageDifferenceDetails} onChange={(value) => updateSectionField('compensation', 'contractWageDifferenceDetails', value)} error={errors.compensationContractWageDifferenceDetails} rows={4} />
                              </div>
                            ) : null}
                            {formData.compensation.gosiWageMatchesActual === 'لا' ? (
                              <div className="animate-in fade-in-0 slide-in-from-top-1 duration-300">
                                <TextAreaField label={text('وضح الفرق بين الأجر الفعلي والأجر المسجل في التأمينات', 'Explain the difference between the actual wage and the wage registered in GOSI')} value={formData.compensation.gosiWageDifferenceDetails} onChange={(value) => updateSectionField('compensation', 'gosiWageDifferenceDetails', value)} error={errors.compensationGosiWageDifferenceDetails} rows={4} />
                              </div>
                            ) : null}
                            {(formData.compensation.contractWageMatchesActual === 'لا' || formData.compensation.gosiWageMatchesActual === 'لا') ? (
                              <TextAreaField className="md:col-span-2 animate-in fade-in-0 slide-in-from-top-1 duration-300" label={text('إذا كان هناك اختلاف، ما السبب بحسب علمك؟', 'If there was a difference, what do you believe caused it?')} value={formData.compensation.wageDifferenceReason} onChange={(value) => updateSectionField('compensation', 'wageDifferenceReason', value)} rows={4} />
                            ) : null}
                          </div>
                        </div>

                        {formData.contract.salaryReceiptMethod === 'نقداً' ? (
                          <div className="mt-6 grid gap-6 md:grid-cols-2 xl:grid-cols-4 animate-in fade-in-0 slide-in-from-top-1 duration-300">
                            <SelectField label={text('هل كنت توقع على مسير استلام؟', 'Did you sign payroll receipt sheets?')} value={formData.contract.cashPayrollSignoff} onChange={(value) => updateSectionField('contract', 'cashPayrollSignoff', value)} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                            <SelectField label={text('هل لديك نسخ أو صور؟', 'Do you have copies or photos?')} value={formData.compensation.cashReceiptCopies} onChange={(value) => updateSectionField('compensation', 'cashReceiptCopies', value)} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                          </div>
                        ) : null}

                        {formData.contract.salaryReceiptMethod === 'شيكات' ? (
                          <div className="mt-6 grid gap-6 md:grid-cols-2 xl:grid-cols-4 animate-in fade-in-0 slide-in-from-top-1 duration-300">
                            <SelectField label={text('هل الشيكات باسمك؟', 'Were the cheques issued in your name?')} value={formData.compensation.chequeInYourName} onChange={(value) => updateSectionField('compensation', 'chequeInYourName', value)} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                            <SelectField label={text('هل صرفت بالكامل؟', 'Were they fully cashed?')} value={formData.compensation.chequeCashedFully} onChange={(value) => updateSectionField('compensation', 'chequeCashedFully', value)} options={['نعم', 'لا', 'جزئياً', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                            <SelectField label={text('هل تحتفظ بصور منها؟', 'Do you keep copies of them?')} value={formData.compensation.chequeCopiesAvailable} onChange={(value) => updateSectionField('compensation', 'chequeCopiesAvailable', value)} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                          </div>
                        ) : null}
                      </div>

                      <div className="rounded-[28px] border border-[#C8A870]/18 bg-[#f8f6f1] p-6 sm:p-7">
                        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 border-b border-[#C8A870]/14 pb-4">
                          <div>
                            <h3 className="text-xl font-black leading-9 text-[#1F3B64] sm:text-2xl">{text('العُهد والممتلكات المسلمة', 'Custodies and Employer Property')}</h3>
                            <p className="mt-1 text-sm leading-7 text-slate-600">{text('يشمل ذلك الأجهزة، السيارات، المعدات، أدوات العمل، أو المبالغ النقدية بعهدة الموظف.', 'Includes devices, vehicles, equipment, work tools, or cash entrusted to the employee.')}</p>
                          </div>
                        </div>
                        <div className="space-y-5">
                          <SelectField
                            label={text('هل استلم العامل أي عهدة أو ممتلكات من جهة العمل؟', 'Did the worker receive any custody or employer property?')}
                            value={formData.employment.hasCustodies || 'لا'}
                            onChange={(value) => {
                              updateSectionField('employment', 'hasCustodies', value);
                              if (value !== 'نعم') updateSectionField('employment', 'custodies', []);
                            }}
                            options={['لا', 'نعم']}
                            getOptionLabel={(option) => trOption(option)}
                          />
                          {formData.employment.hasCustodies === 'نعم' ? (
                            <>
                              <Button type="button" variant="outline" onClick={() => addEmploymentRow('custodies', createCustodyItem)} className="rounded-xl border-[#C8A870]/35 text-[#1F3B64]">
                                <Plus className="h-4 w-4" /> {text('إضافة عهدة', 'Add custody')}
                              </Button>
                              {(formData.employment.custodies || []).length ? (
                                <div className="space-y-4">
                                  {formData.employment.custodies.map((custody, index) => {
                                    const isCashCustody = custody.type === 'مبالغ نقدية';
                                    return (
                                      <div key={custody.id} className="rounded-[24px] border border-[#C8A870]/18 bg-white p-5">
                                        <div className="mb-4 flex items-center justify-between">
                                          <p className="font-black text-[#1F3B64]">{isArabic ? `العهدة ${index + 1}` : `Custody ${index + 1}`}</p>
                                          <button type="button" onClick={() => removeEmploymentRow('custodies', custody.id)} className="text-sm font-bold text-red-500">{text('حذف', 'Remove')}</button>
                                        </div>
                                        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                                          <SelectField label={text('نوع العهدة', 'Custody type')} value={custody.type} onChange={(value) => updateEmploymentRow('custodies', custody.id, { type: value })} options={['', 'جهاز حاسب', 'جوال', 'سيارة', 'معدات', 'أدوات عمل', 'مبالغ نقدية', 'أخرى']} getOptionLabel={(option) => option ? trOption(option) : (text('اختر نوع العهدة', 'Select custody type'))} />
                                          {custody.type === 'أخرى' ? <Field label={text('وصف العهدة', 'Custody description')} value={custody.otherDescription} onChange={(value) => updateEmploymentRow('custodies', custody.id, { otherDescription: value })} /> : null}
                                          <SelectField label={text('حالة العهدة عند الاستلام', 'Condition when received')} value={custody.conditionAtReceipt} onChange={(value) => updateEmploymentRow('custodies', custody.id, { conditionAtReceipt: value })} options={['جديدة', 'مستعملة', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                                          <Field label={text('القيمة التقديرية للعهدة', 'Estimated value')} inputMode="decimal" value={custody.estimatedValue} onChange={(value) => updateEmploymentRow('custodies', custody.id, { estimatedValue: value })} />
                                          <Field label={text('تاريخ الاستلام', 'Receipt date')} type="date" value={custody.receivedAt} onChange={(value) => updateEmploymentRow('custodies', custody.id, { receivedAt: value })} />
                                          <SelectField label={text('هل يوجد محضر أو نموذج استلام؟', 'Receipt record available?')} value={custody.hasReceipt} onChange={(value) => updateEmploymentRow('custodies', custody.id, { hasReceipt: value })} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                                          {custody.hasReceipt === 'نعم' ? (
                                            <>
                                              <Field label={text('رقم المحضر (اختياري)', 'Record number (optional)')} value={custody.receiptNumber} onChange={(value) => updateEmploymentRow('custodies', custody.id, { receiptNumber: value })} />
                                                <FileMiniField
                                                isArabic={isArabic}
                                                label={text('إرفاق المحضر (اختياري)', 'Attach receipt record (optional)')}
                                                count={(custody.receiptAttachments || []).length}
                                                onChange={(files) => handleEmploymentRowAttachment('custodies', custody.id, 'receiptAttachments', files, 'custody_receipt_record', 'محضر استلام عهدة')}
                                              />
                                            </>
                                          ) : null}
                                        </div>

                                        {isCashCustody ? (
                                          <div className="mt-5 rounded-[20px] bg-[#f8f6f1] p-4">
                                            <p className="mb-4 font-black text-[#1F3B64]">{text('تفاصيل العهدة النقدية', 'Cash custody details')}</p>
                                            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                                              <Field label={text('قيمة العهدة النقدية', 'Cash custody amount')} inputMode="decimal" value={custody.cashAmount} onChange={(value) => updateEmploymentRow('custodies', custody.id, { cashAmount: value })} />
                                              <Field label={text('الغرض من العهدة', 'Purpose of custody')} value={custody.cashPurpose} onChange={(value) => updateEmploymentRow('custodies', custody.id, { cashPurpose: value })} />
                                              <SelectField label={text('هل بقي أي مبلغ تحت عهدتك؟', 'Is any amount still with you?')} value={custody.hasRemainingCash} onChange={(value) => updateEmploymentRow('custodies', custody.id, { hasRemainingCash: value })} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                                              {custody.hasRemainingCash === 'نعم' ? <Field label={text('المبلغ المتبقي', 'Remaining amount')} inputMode="decimal" value={custody.remainingCashAmount} onChange={(value) => updateEmploymentRow('custodies', custody.id, { remainingCashAmount: value })} /> : null}
                                              <SelectField label={text('هل تم إعادة المبلغ لجهة العمل؟', 'Was the amount returned to the employer?')} value={custody.cashReturned} onChange={(value) => updateEmploymentRow('custodies', custody.id, { cashReturned: value })} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                                              {custody.cashReturned === 'نعم' ? (
                                                <>
                                                  <Field label={text('تاريخ التسليم', 'Return date')} type="date" value={custody.cashReturnedAt} onChange={(value) => updateEmploymentRow('custodies', custody.id, { cashReturnedAt: value })} />
                                                  <Field label={text('اسم المستلم', 'Receiver name')} value={custody.cashReceiverName} onChange={(value) => updateEmploymentRow('custodies', custody.id, { cashReceiverName: value })} />
                                                  <Field label={text('منصب المستلم', 'Receiver position')} value={custody.cashReceiverTitle} onChange={(value) => updateEmploymentRow('custodies', custody.id, { cashReceiverTitle: value })} />
                                                  <SelectField label={text('هل تم توقيع إقرار أو مخالصة أو سند استلام؟', 'Was an acknowledgement, release, or receipt signed?')} value={custody.hasCashReturnDocument} onChange={(value) => updateEmploymentRow('custodies', custody.id, { hasCashReturnDocument: value })} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                                                  {custody.hasCashReturnDocument === 'نعم' ? (
                                                    <>
                                                      <Field label={text('رقم المستند (اختياري)', 'Document number (optional)')} value={custody.cashReturnDocumentNumber} onChange={(value) => updateEmploymentRow('custodies', custody.id, { cashReturnDocumentNumber: value })} />
                                                      <FileMiniField isArabic={isArabic} label={text('إرفاق المستند (اختياري)', 'Attach document (optional)')} count={(custody.cashReturnAttachments || []).length} onChange={(files) => handleEmploymentRowAttachment('custodies', custody.id, 'cashReturnAttachments', files, 'cash_custody_return_document', 'مستند إعادة عهدة نقدية')} />
                                                    </>
                                                  ) : null}
                                                </>
                                              ) : null}
                                            </div>
                                          </div>
                                        ) : (
                                          <div className="mt-5 rounded-[20px] bg-[#f8f6f1] p-4">
                                            <p className="mb-4 font-black text-[#1F3B64]">{text('حالة تسليم العهدة العينية', 'Physical custody return status')}</p>
                                            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                                              <SelectField label={text('هل تم تسليم العهدة عند انتهاء العلاقة؟', 'Was the custody returned at the end?')} value={custody.returnedAtEnd} onChange={(value) => updateEmploymentRow('custodies', custody.id, { returnedAtEnd: value })} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                                              {custody.returnedAtEnd === 'نعم' ? (
                                                <>
                                                  <Field label={text('تاريخ التسليم', 'Return date')} type="date" value={custody.returnedAt} onChange={(value) => updateEmploymentRow('custodies', custody.id, { returnedAt: value })} />
                                                  <Field label={text('اسم المستلم', 'Receiver name')} value={custody.receiverName} onChange={(value) => updateEmploymentRow('custodies', custody.id, { receiverName: value })} />
                                                  <Field label={text('منصب المستلم', 'Receiver position')} value={custody.receiverTitle} onChange={(value) => updateEmploymentRow('custodies', custody.id, { receiverTitle: value })} />
                                                  <SelectField label={text('هل يوجد محضر تسليم؟', 'Return record available?')} value={custody.hasReturnRecord} onChange={(value) => updateEmploymentRow('custodies', custody.id, { hasReturnRecord: value })} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                                                  {custody.hasReturnRecord === 'نعم' ? (
                                                    <>
                                                      <Field label={text('رقم المحضر (اختياري)', 'Record number (optional)')} value={custody.returnRecordNumber} onChange={(value) => updateEmploymentRow('custodies', custody.id, { returnRecordNumber: value })} />
                                                      <FileMiniField isArabic={isArabic} label={text('إرفاق المحضر (اختياري)', 'Attach return record (optional)')} count={(custody.returnRecordAttachments || []).length} onChange={(files) => handleEmploymentRowAttachment('custodies', custody.id, 'returnRecordAttachments', files, 'custody_return_record', 'محضر تسليم عهدة')} />
                                                    </>
                                                  ) : null}
                                                </>
                                              ) : null}
                                            </div>
                                          </div>
                                        )}

                                        <div className="mt-5 grid gap-4 rounded-[20px] bg-[#fcfbf8] p-4 md:grid-cols-2">
                                          <SelectField label={text('هل سبق أن طالبتك جهة العمل بهذه العهدة أو خصمت بسببها أي مبلغ؟', 'Has the employer claimed this custody or deducted any amount for it?')} value={custody.employerClaimedOrDeducted} onChange={(value) => updateEmploymentRow('custodies', custody.id, { employerClaimedOrDeducted: value })} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                                          {custody.employerClaimedOrDeducted === 'نعم' ? <TextAreaField label={text('اشرح تفاصيل المطالبة أو الخصم.', 'Explain the claim or deduction details.')} value={custody.claimOrDeductionDetails} onChange={(value) => updateEmploymentRow('custodies', custody.id, { claimOrDeductionDetails: value })} rows={3} /> : null}
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              ) : <p className="rounded-2xl bg-white px-5 py-4 text-sm text-slate-500">{text('اضغط إضافة عهدة لإدخال تفاصيل كل عهدة على حدة.', 'Click add custody to enter each custody separately.')}</p>}
                            </>
                          ) : null}
                        </div>
                      </div>

                      <div className="rounded-[28px] border border-[#C8A870]/18 bg-[#f8f6f1] p-6 sm:p-7">
                        <div className="mb-6 flex flex-wrap items-center justify-between gap-3 border-b border-[#C8A870]/14 pb-4">
                          <div>
                            <h3 className="text-xl font-black leading-9 text-[#1F3B64] sm:text-2xl">{text('السلف والالتزامات المالية', 'Advances and Financial Obligations')}</h3>
                            <p className="mt-1 text-sm leading-7 text-slate-600">{text('أضف السلف التي حصل عليها العامل أثناء فترة العمل، إن وجدت.', 'Add employee advances received during employment, if any.')}</p>
                          </div>
                        </div>
                        <div className="space-y-5">
                          <SelectField
                            label={text('هل حصل العامل على سلفة أو أكثر خلال فترة العمل؟', 'Did the worker receive one or more advances during employment?')}
                            value={formData.employment.hasAdvances || 'لا'}
                            onChange={(value) => {
                              updateSectionField('employment', 'hasAdvances', value);
                              if (value !== 'نعم') updateSectionField('employment', 'advances', []);
                            }}
                            options={['لا', 'نعم']}
                            getOptionLabel={(option) => trOption(option)}
                          />
                          {formData.employment.hasAdvances === 'نعم' ? (
                            <>
                              <Button type="button" variant="outline" onClick={() => addEmploymentRow('advances', createAdvanceItem)} className="rounded-xl border-[#C8A870]/35 text-[#1F3B64]">
                                <Plus className="h-4 w-4" /> {text('إضافة سلفة', 'Add advance')}
                              </Button>
                              {(formData.employment.advances || []).length ? (
                                <div className="space-y-4">
                                  {formData.employment.advances.map((advance, index) => (
                                    <div key={advance.id} className="rounded-[24px] border border-[#C8A870]/18 bg-white p-5">
                                      <div className="mb-4 flex items-center justify-between">
                                        <p className="font-black text-[#1F3B64]">{isArabic ? `السلفة ${index + 1}` : `Advance ${index + 1}`}</p>
                                        <button type="button" onClick={() => removeEmploymentRow('advances', advance.id)} className="text-sm font-bold text-red-500">{text('حذف', 'Remove')}</button>
                                      </div>

                                      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                                        <Field label={text('قيمة السلفة', 'Advance amount')} inputMode="decimal" value={advance.amount} onChange={(value) => updateEmploymentRow('advances', advance.id, { amount: value })} />
                                        <Field label={text('تاريخ السلفة', 'Advance date')} type="date" value={advance.date} onChange={(value) => updateEmploymentRow('advances', advance.id, { date: value })} />
                                        <SelectField label={text('طريقة الاستلام', 'Receipt method')} value={advance.receiptMethod} onChange={(value) => updateEmploymentRow('advances', advance.id, { receiptMethod: value })} options={['', 'تحويل بنكي', 'نقداً', 'شيك', 'أخرى']} getOptionLabel={(option) => option ? trOption(option) : (text('اختر طريقة الاستلام', 'Select receipt method'))} />
                                        {advance.receiptMethod === 'أخرى' ? <Field label={text('وضح طريقة الاستلام', 'Explain receipt method')} value={advance.receiptMethodOther} onChange={(value) => updateEmploymentRow('advances', advance.id, { receiptMethodOther: value })} /> : null}
                                        {advance.receiptMethod === 'تحويل بنكي' ? (
                                          <>
                                            <Field label={text('اسم البنك', 'Bank name')} value={advance.receiptBankName} onChange={(value) => updateEmploymentRow('advances', advance.id, { receiptBankName: value })} />
                                            <Field label={text('تاريخ التحويل', 'Transfer date')} type="date" value={advance.transferDate} onChange={(value) => updateEmploymentRow('advances', advance.id, { transferDate: value })} />
                                          </>
                                        ) : null}
                                        {advance.receiptMethod === 'نقداً' ? (
                                          <>
                                            <Field label={text('اسم المسلم', 'Delivered by')} value={advance.cashGiverName} onChange={(value) => updateEmploymentRow('advances', advance.id, { cashGiverName: value })} />
                                            <Field label={text('منصب المسلم', 'Deliverer position')} value={advance.cashGiverTitle} onChange={(value) => updateEmploymentRow('advances', advance.id, { cashGiverTitle: value })} />
                                            <SelectField label={text('هل تم التوقيع على استلام السلفة؟', 'Was an advance receipt signed?')} value={advance.signedAdvanceReceipt} onChange={(value) => updateEmploymentRow('advances', advance.id, { signedAdvanceReceipt: value })} options={['نعم', 'لا']} getOptionLabel={(option) => trOption(option)} />
                                          </>
                                        ) : null}
                                      </div>

                                      <div className="mt-5 rounded-[20px] bg-[#f8f6f1] p-4">
                                        <p className="mb-4 font-black text-[#1F3B64]">{text('حالة السداد', 'Repayment status')}</p>
                                        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                                          <SelectField label={text('هل تم سداد السلفة بالكامل؟', 'Was the advance fully repaid?')} value={advance.repaid} onChange={(value) => updateEmploymentRow('advances', advance.id, { repaid: value })} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                                          {advance.repaid === 'لا' ? <Field label={text('المبلغ المتبقي', 'Remaining amount')} inputMode="decimal" value={advance.remainingBalance} onChange={(value) => updateEmploymentRow('advances', advance.id, { remainingBalance: value })} /> : null}
                                          <SelectField label={text('طريقة السداد', 'Repayment method')} value={advance.repaymentMethod} onChange={(value) => updateEmploymentRow('advances', advance.id, { repaymentMethod: value })} options={['', 'خصم من الراتب', 'تحويل بنكي', 'نقداً', 'أكثر من طريقة']} getOptionLabel={(option) => option ? trOption(option) : (text('اختر طريقة السداد', 'Select repayment method'))} />
                                          {['خصم من الراتب', 'أكثر من طريقة'].includes(advance.repaymentMethod) ? (
                                            <>
                                              <Field label={text('تاريخ أول خصم', 'First deduction date')} type="date" value={advance.firstDeductionDate} onChange={(value) => updateEmploymentRow('advances', advance.id, { firstDeductionDate: value })} />
                                              <Field label={text('تاريخ آخر خصم (اختياري)', 'Last deduction date (optional)')} type="date" value={advance.lastDeductionDate} onChange={(value) => updateEmploymentRow('advances', advance.id, { lastDeductionDate: value })} />
                                            </>
                                          ) : null}
                                          {['تحويل بنكي', 'أكثر من طريقة'].includes(advance.repaymentMethod) ? (
                                            <>
                                              <Field label={text('تاريخ التحويل', 'Transfer date')} type="date" value={advance.repaymentTransferDate} onChange={(value) => updateEmploymentRow('advances', advance.id, { repaymentTransferDate: value })} />
                                              <Field label={text('البنك', 'Bank')} value={advance.repaymentBankName} onChange={(value) => updateEmploymentRow('advances', advance.id, { repaymentBankName: value })} />
                                              <Field label={text('قيمة التحويل', 'Transfer amount')} inputMode="decimal" value={advance.repaymentTransferAmount} onChange={(value) => updateEmploymentRow('advances', advance.id, { repaymentTransferAmount: value })} />
                                              <FileMiniField isArabic={isArabic} label={text('إرفاق إشعار التحويل (اختياري)', 'Attach transfer notice (optional)')} count={(advance.repaymentTransferAttachments || []).length} onChange={(files) => handleEmploymentRowAttachment('advances', advance.id, 'repaymentTransferAttachments', files, 'advance_repayment_transfer_notice', 'إشعار تحويل سداد سلفة')} />
                                            </>
                                          ) : null}
                                          {['نقداً', 'أكثر من طريقة'].includes(advance.repaymentMethod) ? (
                                            <>
                                              <Field label={text('اسم المستلم', 'Receiver name')} value={advance.cashReceiverName} onChange={(value) => updateEmploymentRow('advances', advance.id, { cashReceiverName: value })} />
                                              <Field label={text('منصب المستلم', 'Receiver position')} value={advance.cashReceiverTitle} onChange={(value) => updateEmploymentRow('advances', advance.id, { cashReceiverTitle: value })} />
                                              <Field label={text('تاريخ السداد', 'Repayment date')} type="date" value={advance.cashRepaymentDate} onChange={(value) => updateEmploymentRow('advances', advance.id, { cashRepaymentDate: value })} />
                                              <SelectField label={text('هل تم استلام إيصال أو سند؟', 'Was a receipt or voucher received?')} value={advance.hasCashReceipt} onChange={(value) => updateEmploymentRow('advances', advance.id, { hasCashReceipt: value })} options={['نعم', 'لا']} getOptionLabel={(option) => trOption(option)} />
                                              {advance.hasCashReceipt === 'نعم' ? <FileMiniField isArabic={isArabic} label={text('إرفاق الإيصال (اختياري)', 'Attach receipt (optional)')} count={(advance.cashReceiptAttachments || []).length} onChange={(files) => handleEmploymentRowAttachment('advances', advance.id, 'cashReceiptAttachments', files, 'advance_cash_repayment_receipt', 'إيصال سداد سلفة')} /> : null}
                                            </>
                                          ) : null}
                                        </div>
                                      </div>

                                      <div className="mt-5 grid gap-4 rounded-[20px] bg-[#fcfbf8] p-4 md:grid-cols-2">
                                        <SelectField label={text('هل سبق أن طالبتك جهة العمل بهذه السلفة أو خصمت بسببها أي مبلغ؟', 'Has the employer claimed this advance or deducted any amount for it?')} value={advance.employerClaimedOrDeducted} onChange={(value) => updateEmploymentRow('advances', advance.id, { employerClaimedOrDeducted: value })} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                                        {advance.employerClaimedOrDeducted === 'نعم' ? <TextAreaField label={text('اشرح تفاصيل المطالبة أو الخصم.', 'Explain the claim or deduction details.')} value={advance.claimOrDeductionDetails} onChange={(value) => updateEmploymentRow('advances', advance.id, { claimOrDeductionDetails: value })} rows={3} /> : null}
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              ) : <p className="rounded-2xl bg-white px-5 py-4 text-sm text-slate-500">{text('اضغط إضافة سلفة لإدخال تفاصيل كل سلفة على حدة.', 'Click add advance to enter each advance separately.')}</p>}
                            </>
                          ) : null}
                          {errors.employmentAttachment ? <p className="text-sm font-medium text-red-500">{errors.employmentAttachment}</p> : null}
                        </div>
                      </div>

                      <div className="rounded-[28px] border border-[#C8A870]/18 bg-[#f8f6f1] p-6 sm:p-7">
                        <div className="mb-6 border-b border-[#C8A870]/14 pb-4">
                          <h3 className="text-xl font-black leading-9 text-[#1F3B64] sm:text-2xl">{text('الإجازات والراحة والعمل الإضافي', 'Leave, Rest Days, and Overtime')}</h3>
                        </div>
                        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
                          <SelectField label={text('هل كنت تحصل على إجازتك السنوية؟', 'Did you receive your annual leave?')} value={formData.benefits.annualLeaveTaken} onChange={(value) => updateSectionField('benefits', 'annualLeaveTaken', value)} options={['نعم', 'لا', 'أحياناً', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                          <Field label={text('هل يوجد رصيد إجازات متبقٍ؟', 'Is there remaining leave balance?')} value={formData.benefits.vacationBalance} onChange={(value) => updateSectionField('benefits', 'vacationBalance', value)} />
                          <SelectField label={text('هل تم تعويضك عنها؟', 'Were you compensated for it?')} value={formData.benefits.leaveCompensated} onChange={(value) => updateSectionField('benefits', 'leaveCompensated', value)} options={['نعم', 'لا', 'جزئياً', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                          <SelectField label={text('هل كانت الإجازات تعتمد رسمياً؟', 'Was leave officially approved?')} value={formData.benefits.leaveApprovedOfficially} onChange={(value) => updateSectionField('benefits', 'leaveApprovedOfficially', value)} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                          <SelectField label={text('هل كنت تعمل ساعات إضافية؟', 'Did you work overtime?')} value={formData.benefits.overtime} onChange={(value) => updateSectionField('benefits', 'overtime', value)} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                          {formData.benefits.overtime === 'نعم' ? (
                            <>
                              <Field label={text('متوسط عدد الساعات الإضافية', 'Average number of overtime hours')} value={formData.benefits.overtimeHours} onChange={(value) => updateSectionField('benefits', 'overtimeHours', value)} />
                              <SelectField label={text('هل تم دفع مقابلها؟', 'Were they paid?')} value={formData.benefits.overtimePaid} onChange={(value) => updateSectionField('benefits', 'overtimePaid', value)} options={['نعم', 'لا', 'جزئياً', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                              <SelectField label={text('هل يوجد إثبات؟', 'Is there proof?')} value={formData.benefits.overtimeEvidence} onChange={(value) => updateSectionField('benefits', 'overtimeEvidence', value)} options={['نعم', 'لا', 'جزئياً', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                              <div className="xl:col-span-4 rounded-[24px] border border-[#C8A870]/18 bg-white p-5">
                                <p className="mb-3 text-base font-black text-[#1F3B64]">{text('متى كان العمل الإضافي غالباً؟', 'When did overtime usually occur?')}</p>
                                <div className="grid gap-3 md:grid-cols-3">
                                  {['بعد الدوام', 'أيام الراحة', 'الأعياد'].map((item) => {
                                    const checked = formData.benefits.overtimeTiming.includes(item);
                                    return (
                                      <label key={item} className={`flex cursor-pointer items-start gap-3 rounded-2xl border px-4 py-3 transition ${checked ? 'border-[#A27849]/50 bg-[#fcfbf8] text-[#1F3B64]' : 'border-[#C8A870]/14 bg-white text-slate-700'}`}>
                                        <Checkbox checked={checked} onCheckedChange={() => updateChecklistValue('benefits', 'overtimeTiming', item)} className="mt-1 border-[#A27849] data-[state=checked]:bg-[#A27849] data-[state=checked]:text-white" />
                                        <span className="text-[15px] leading-8 sm:text-base">{trOption(item)}</span>
                                      </label>
                                    );
                                  })}
                                </div>
                              </div>
                            </>
                          ) : null}
                          <Field label={text('يوم الراحة المعتاد', 'Usual weekly rest day')} value={formData.benefits.weeklyRestDay} onChange={(value) => updateSectionField('benefits', 'weeklyRestDay', value)} />
                          <SelectField label={text('هل كنت تعمل في يوم الراحة؟', 'Did you work on the weekly rest day?')} value={formData.benefits.restDayWorked} onChange={(value) => updateSectionField('benefits', 'restDayWorked', value)} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                          <SelectField label={text('هل حصلت على تعويض؟', 'Were you compensated?')} value={formData.benefits.restDayCompensated} onChange={(value) => updateSectionField('benefits', 'restDayCompensated', value)} options={['نعم', 'لا', 'جزئياً', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                          <TextAreaField className="xl:col-span-4" label={text('أي ملاحظات مهمة حول الإجازات أو العمل الإضافي أو الراحة الأسبوعية', 'Any important notes about leave, overtime, or weekly rest')} value={formData.benefits.leaveNotes} onChange={(value) => updateSectionField('benefits', 'leaveNotes', value)} rows={4} />
                        </div>
                      </div>

                      <div className="rounded-[28px] border border-[#C8A870]/18 bg-[#f8f6f1] p-6 sm:p-7">
                        <div className="mb-6 border-b border-[#C8A870]/14 pb-4">
                          <h3 className="text-xl font-black leading-9 text-[#1F3B64] sm:text-2xl">{text('التغييرات الجوهرية أثناء العلاقة العمالية', 'Material Changes During the Employment Relationship')}</h3>
                        </div>
                        <div className="grid gap-6 md:grid-cols-2 xl:grid-cols-4">
                          <SelectField label={text('هل تغير المسمى الوظيفي؟', 'Did the job title change?')} value={formData.contract.titleChanged ? 'نعم' : 'لا'} onChange={(value) => updateSectionField('contract', 'titleChanged', value === 'نعم')} options={['لا', 'نعم']} getOptionLabel={(option) => trOption(option)} />
                          <SelectField label={text('هل تغير الأجر؟', 'Did the wage change?')} value={formData.contract.salaryChanged ? 'نعم' : 'لا'} onChange={(value) => updateSectionField('contract', 'salaryChanged', value === 'نعم')} options={['لا', 'نعم']} getOptionLabel={(option) => trOption(option)} />
                          <SelectField label={text('هل تغير مقر العمل؟', 'Did the workplace change?')} value={formData.contract.workplaceChanged ? 'نعم' : 'لا'} onChange={(value) => updateSectionField('contract', 'workplaceChanged', value === 'نعم')} options={['لا', 'نعم']} getOptionLabel={(option) => trOption(option)} />
                          <SelectField label={text('هل زادت المهام؟', 'Did the duties increase?')} value={formData.contract.dutiesChanged ? 'نعم' : 'لا'} onChange={(value) => updateSectionField('contract', 'dutiesChanged', value === 'نعم')} options={['لا', 'نعم']} getOptionLabel={(option) => trOption(option)} />
                          <SelectField label={text('هل تم نقلك؟', 'Were you transferred?')} value={formData.contract.hoursChanged ? 'نعم' : 'لا'} onChange={(value) => updateSectionField('contract', 'hoursChanged', value === 'نعم')} options={['لا', 'نعم']} getOptionLabel={(option) => trOption(option)} />
                          <SelectField label={text('هل تم تخفيض مزاياك؟', 'Were your benefits reduced?')} value={formData.contract.otherChanged ? 'نعم' : 'لا'} onChange={(value) => updateSectionField('contract', 'otherChanged', value === 'نعم')} options={['لا', 'نعم']} getOptionLabel={(option) => trOption(option)} />
                          {(formData.contract.titleChanged || formData.contract.salaryChanged || formData.contract.workplaceChanged || formData.contract.dutiesChanged || formData.contract.hoursChanged || formData.contract.otherChanged) ? (
                            <div className="xl:col-span-4 animate-in fade-in-0 slide-in-from-top-1 duration-300">
                              <TextAreaField label={text('اشرح بإيجاز أهم التغييرات الجوهرية أثناء العلاقة', 'Briefly explain the main material changes during the relationship')} value={formData.contract.otherChangedDetails} onChange={(value) => updateSectionField('contract', 'otherChangedDetails', value)} rows={4} />
                            </div>
                          ) : null}
                        </div>
                      </div>

                      <div className="rounded-[28px] border border-[#C8A870]/18 bg-[#f8f6f1] p-6 sm:p-7">
                        <div className="mb-6 border-b border-[#C8A870]/14 pb-4">
                          <h3 className="text-xl font-black leading-9 text-[#1F3B64] sm:text-2xl">{text('التحليل الأولي للمطالبات المحتملة', 'Initial Analysis of Potential Claims')}</h3>
                        </div>
                        {suggestedClaims.length > 0 ? (
                          <div className="mb-5 rounded-[22px] border border-[#A27849]/18 bg-white px-5 py-4">
                            <p className="text-base font-black text-[#1F3B64]">{text('مطالبات مقترحة بناءً على إجاباتك', 'Suggested claims based on your answers')}</p>
                            <div className="mt-4 flex flex-wrap gap-3">
                              {suggestedClaims.map((item) => (
                                <button key={item} type="button" onClick={() => {
                                  if (!formData.compensation.claims.includes(item)) toggleClaim(item);
                                }} className="rounded-full border border-[#A27849]/28 bg-[#fcfbf8] px-4 py-2 text-sm font-bold text-[#1F3B64] transition hover:border-[#A27849]/45 hover:bg-white">
                                  {trOption(item, 'claims')}
                                </button>
                              ))}
                            </div>
                          </div>
                        ) : null}
                        <ChecklistField title={text('المطالبات المحتملة', 'Potential claims')} items={dynamicClaimsOptions} selectedItems={formData.compensation.claims} onToggle={toggleClaim} getItemLabel={(item) => trOption(item, 'claims')} />
                        <ClaimBreakdownFields
                          isArabic={isArabic}
                          compensation={formData.compensation}
                          totals={financialTotals}
                          errors={errors}
                          updateSectionField={updateSectionField}
                          updateRow={updateCompensationRow}
                          addRow={addCompensationRow}
                          removeRow={removeCompensationRow}
                        />
                      </div>
                    </div>
                  )}

                  {currentStep === 4 && (
                    <div className="space-y-6">
                      <ChecklistField title={text('الإشعارات أو المراسلات المتوفرة', 'Available notices or correspondence')} items={noticesOptions} selectedItems={formData.notices.selectedNotices} onToggle={(item) => updateChecklistValue('notices', 'selectedNotices', item)} getItemLabel={(item) => trOption(item, 'notices')} />
                      <div className="grid gap-5 md:grid-cols-2">
                        <SelectField label={text('هل وقعت على مخالصة؟', 'Did you sign a release or settlement?')} value={formData.contract.releaseReceived} onChange={(value) => {
                          updateSectionField('contract', 'releaseReceived', value);
                          if (value !== 'نعم') {
                            updateSectionField('contract', 'releaseDate', '');
                            updateSectionField('contract', 'releaseCopyReceived', 'لا أعلم');
                            updateSectionField('contract', 'releaseSignedVoluntarily', 'لا أعلم');
                            updateSectionField('contract', 'releaseSignedTiming', '');
                            updateSectionField('contract', 'releaseFormat', '');
                          }
                        }} options={['لا', 'نعم', 'لا أذكر']} getOptionLabel={(option) => trOption(option)} />
                        <SelectField label={text('هل سبق أن أرسلت اعتراضاً أو مطالبة أو شكوى ضد جهة العمل؟', 'Have you previously sent any objection, claim, or complaint against the employer?')} value={formData.notices.sentObjection} onChange={(value) => {
                          updateSectionField('notices', 'sentObjection', value);
                          if (value !== 'نعم') {
                            updateSectionField('notices', 'objectionDate', '');
                            updateSectionField('notices', 'objectionMethod', '');
                            updateSectionField('notices', 'objectionCopyAvailable', 'لا أعلم');
                            updateSectionField('notices', 'employerResponseReceived', 'لا أعلم');
                            updateSectionField('notices', 'employerResponseCopyAvailable', 'لا أعلم');
                          }
                        }} options={['لا', 'نعم']} getOptionLabel={(option) => trOption(option)} />
                        {formData.contract.releaseReceived === 'نعم' ? (
                          <>
                            <Field className="md:col-span-2" label={text('تاريخ توقيع المخالصة', 'Release signing date')} type="date" value={formData.contract.releaseDate} onChange={(value) => updateSectionField('contract', 'releaseDate', value)} error={errors.contractReleaseDate} helperText={formData.contract.releaseDate ? `${text('هجري', 'Hijri')}: ${toHijriDate(formData.contract.releaseDate, isArabic)}` : ''} />
                            <SelectField label={text('هل كانت المخالصة ورقية أم إلكترونية؟', 'Was the release paper-based or electronic?')} value={formData.contract.releaseFormat} onChange={(value) => updateSectionField('contract', 'releaseFormat', value)} error={errors.contractReleaseFormat} options={['ورقية', 'إلكترونية']} getOptionLabel={(option) => trOption(option)} />
                            <SelectField label={text('هل استلمت نسخة منها؟', 'Did you receive a copy?')} value={formData.contract.releaseCopyReceived} onChange={(value) => updateSectionField('contract', 'releaseCopyReceived', value)} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                            <SelectField label={text('هل تم توقيعها بعد استلام المستحقات أم قبلها؟', 'Was it signed before or after receiving the dues?')} value={formData.contract.releaseSignedTiming} onChange={(value) => updateSectionField('contract', 'releaseSignedTiming', value)} error={errors.contractReleaseSignedTiming} options={['بعد استلام المستحقات', 'قبل استلام المستحقات', 'جزء قبل وجزء بعد', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                            <SelectField label={text('هل كان التوقيع باختيارك الكامل؟', 'Was it signed with your full consent?')} value={formData.contract.releaseSignedVoluntarily} onChange={(value) => updateSectionField('contract', 'releaseSignedVoluntarily', value)} options={['نعم', 'لا', 'جزئياً', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                          </>
                        ) : null}
                        {formData.notices.sentObjection === 'نعم' ? (
                          <>
                            <Field className="md:col-span-2" label={text('تاريخ الإرسال أو المطالبة', 'Date of the objection or claim')} type="date" value={formData.notices.objectionDate} onChange={(value) => updateSectionField('notices', 'objectionDate', value)} error={errors.noticesObjectionDate} helperText={formData.notices.objectionDate ? `${text('هجري', 'Hijri')}: ${toHijriDate(formData.notices.objectionDate, isArabic)}` : ''} />
                            <SelectField label={text('طريقة الإرسال', 'Submission method')} value={formData.notices.objectionMethod} onChange={(value) => updateSectionField('notices', 'objectionMethod', value)} error={errors.noticesObjectionMethod} options={['بريد إلكتروني', 'واتساب', 'خطاب رسمي', 'منصة قوى', 'منصة ودّي', 'مكتب العمل', 'رسالة نصية', 'أخرى']} getOptionLabel={(option) => trOption(option)} />
                            <SelectField label={text('هل لديك نسخة من الاعتراض أو المطالبة؟', 'Do you have a copy of the objection or claim?')} value={formData.notices.objectionCopyAvailable} onChange={(value) => updateSectionField('notices', 'objectionCopyAvailable', value)} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                            {formData.notices.objectionCopyAvailable === 'نعم' ? (
                              <div className="md:col-span-2 rounded-[18px] border border-[#C8A870]/18 bg-[#fcfbf8] px-4 py-3 text-sm leading-7 text-[#1F3B64]">
                                {text('يمكن رفع نسخة الاعتراض أو المطالبة في خطوة المرفقات ضمن نوع "اعتراض أو مطالبة سابقة".', 'You can upload the objection or claim copy in the attachments step under "Prior Objection or Claim".')}
                              </div>
                            ) : null}
                            <SelectField label={text('هل وصل رد من جهة العمل؟', 'Did the employer send a response?')} value={formData.notices.employerResponseReceived} onChange={(value) => {
                              updateSectionField('notices', 'employerResponseReceived', value);
                              if (value !== 'نعم') {
                                updateSectionField('notices', 'employerResponseCopyAvailable', 'لا أعلم');
                              }
                            }} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                            {formData.notices.employerResponseReceived === 'نعم' ? (
                              <>
                                <SelectField label={text('هل لديك نسخة من الرد؟', 'Do you have a copy of the response?')} value={formData.notices.employerResponseCopyAvailable} onChange={(value) => updateSectionField('notices', 'employerResponseCopyAvailable', value)} error={errors.noticesEmployerResponseCopyAvailable} options={['نعم', 'لا', 'لا أعلم']} getOptionLabel={(option) => trOption(option)} />
                                {formData.notices.employerResponseCopyAvailable === 'نعم' ? (
                                  <div className="md:col-span-2 rounded-[18px] border border-[#C8A870]/18 bg-[#fcfbf8] px-4 py-3 text-sm leading-7 text-[#1F3B64]">
                                    {text('يمكن رفع رد جهة العمل في خطوة المرفقات ضمن نوع "رد جهة العمل".', 'You can upload the employer response in the attachments step under "Employer Response".')}
                                  </div>
                                ) : null}
                              </>
                            ) : null}
                          </>
                        ) : null}
                        <TextAreaField className="md:col-span-2" label={text('اذكر التسلسل الزمني لأهم الإشعارات أو الرسائل', 'Describe the timeline of the main notices or messages')} value={formData.notices.noticeTimeline} onChange={(value) => updateSectionField('notices', 'noticeTimeline', value)} />
                      </div>
                    </div>
                  )}

                  {currentStep === 5 && (
                    <div className="space-y-6">
                      <Card className="rounded-[28px] border-[#C8A870]/20 bg-white/95 shadow-[0_14px_45px_rgba(15,23,42,0.05)]">
                        <CardHeader className="border-b border-[#C8A870]/12 pb-5">
                          <CardTitle className="text-2xl font-black text-[#1F3B64]">{text('ملخص البيانات والوقائع', 'Summary of Data and Facts')}</CardTitle>
                          <CardDescription className="mt-2 text-sm leading-7 text-slate-600">
                            {text('يعرض هذا القسم ملخصاً تنظيمياً للبيانات والوقائع التي أدخلتموها. لا يعد هذا الملخص رأياً قانونياً أو تقييماً لنتيجة القضية.', 'This section provides an organized summary of the data and facts you entered. It is not legal advice or an assessment of the case outcome.')}
                          </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-5 p-5 sm:p-6">
                          <div className="grid gap-3 md:grid-cols-3">
                            <SummaryBox icon={ClipboardList} title={text('حالة اكتمال الطلب', 'Request completion status')} value={`${completionPercentage}%`} />
                            <SummaryBox icon={CheckCircle2} title={text('الأقسام المكتملة', 'Completed sections')} value={`${completionItems.filter((item) => item.complete).length} / ${completionItems.length}`} />
                            <SummaryBox icon={FolderOpen} title={text('المرفقات الحالية', 'Current attachments')} value={`${(formData.attachments.items || []).length} ${text('مرفق', 'item(s)')}`} />
                          </div>

                          <div className="rounded-[22px] border border-[#C8A870]/14 bg-[#f8f6f1] p-4 text-sm leading-7 text-slate-700">
                            <p className="font-bold text-[#1F3B64]">{text('مؤشر اكتمال البيانات والمستندات', 'Data and Document Completion Indicator')}</p>
                            <p className="mt-2">{text('هذا المؤشر يوضح اكتمال البيانات والمستندات فقط، ولا يعد تقييماً قانونياً أو تقديراً لنتيجة القضية.', 'This indicator reflects only the completeness of the data and documents and does not constitute legal assessment or prediction of the case outcome.')}</p>
                            <div className="mt-4 space-y-2">
                              {completionItems.map((item) => (
                                <div key={item.key} className="flex items-center justify-between rounded-2xl bg-white px-4 py-3">
                                  <span className="text-sm font-bold text-[#1F3B64]">{item.title}</span>
                                  <span className={`text-sm font-bold ${item.complete ? 'text-[#1F3B64]' : 'text-[#A27849]'}`}>
                                    {item.complete ? (text('مكتمل', 'Complete')) : (text('بحاجة إلى استكمال', 'Needs completion'))}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>

                          <div className="space-y-4">
                            {summarySections.map((section) => (
                              <div key={section.title} className="rounded-[22px] border border-[#C8A870]/14 bg-white p-5">
                                <h3 className="text-lg font-black text-[#1F3B64]">{section.title}</h3>
                                <div className="mt-3 space-y-2">
                                  {section.items.map((item, index) => (
                                    <p key={`${section.title}-${index}`} className="text-sm leading-8 text-slate-700">{item}</p>
                                  ))}
                                </div>
                              </div>
                            ))}
                          </div>

                          <div className="rounded-[24px] border border-[#C8A870]/18 bg-[#1F3B64]/[0.03] p-5">
                            <p className="text-base font-black text-[#1F3B64]">{text('هل تصادق على صحة ملخص البيانات والوقائع أعلاه؟', 'Do you confirm the accuracy of the summary of data and facts above?')}</p>
                            <div className="mt-4 grid gap-3">
                              {[
                                { value: 'approved', labelAr: 'أصادق على صحة الملخص', labelEn: 'I confirm the accuracy of the summary' },
                                { value: 'partial', labelAr: 'أصادق جزئياً مع وجود ملاحظة أو تصحيح', labelEn: 'I partially confirm it with a note or correction' },
                                { value: 'rejected', labelAr: 'لا أصادق على الملخص', labelEn: 'I do not confirm the summary' },
                              ].map((option) => (
                                <label key={option.value} className={`flex cursor-pointer items-start gap-3 rounded-2xl border px-4 py-3 transition ${formData.summary.approvalStatus === option.value ? 'border-[#A27849]/50 bg-white text-[#1F3B64]' : 'border-[#C8A870]/14 bg-white text-slate-700'}`}>
                                  <input type="radio" name="summary-approval-status" value={option.value} checked={formData.summary.approvalStatus === option.value} onChange={() => updateSectionField('summary', 'approvalStatus', option.value)} className="mt-1 h-4 w-4 accent-[#A27849]" />
                                  <span className="text-[15px] leading-8 sm:text-base">{isArabic ? option.labelAr : option.labelEn}</span>
                                </label>
                              ))}
                            </div>
                            {errors.summaryApprovalStatus ? <p className="mt-3 text-sm font-medium text-red-500">{errors.summaryApprovalStatus}</p> : null}
                          </div>

                          {formData.summary.approvalStatus === 'partial' ? (
                            <TextAreaField label={text('اكتب المعلومة التي ترغب في تعديلها أو توضيحها', 'Write the information you want to correct or clarify')} value={formData.summary.approvalNote} onChange={(value) => updateSectionField('summary', 'approvalNote', value)} error={errors.summaryApprovalNote} rows={5} />
                          ) : null}

                          {formData.summary.approvalStatus === 'rejected' ? (
                            <div className="rounded-[22px] border border-red-200 bg-red-50 px-5 py-4 text-sm leading-7 text-red-700">
                              <p className="font-bold">
                                {text('لا يمكن إرسال الطلب دون المصادقة على ملخص البيانات والوقائع. إذا واجهت مشكلة في البيانات أو في عرض الملخص، يرجى التواصل مع فريق الدعم عبر البريد: ', 'The request cannot be submitted without confirming the summary of data and facts. If you face an issue with the data or summary display, please contact support at: ')}
                                <a href={`mailto:${supportEmail}`} className="text-[#A27849] underline underline-offset-4">{supportEmail}</a>
                              </p>
                            </div>
                          ) : null}
                        </CardContent>
                      </Card>
                    </div>
                  )}

                  {currentStep === 6 && (
                    <div className="space-y-6">
                      <Card className="rounded-[26px] border-[#C8A870]/20 bg-[#fcfbf8] shadow-[0_10px_30px_rgba(15,23,42,0.04)]">
                        <CardHeader className="border-b border-[#C8A870]/12 pb-5">
                          <div className="flex flex-col gap-4 md:flex-row md:items-start md:justify-between">
                            <div>
                              <CardTitle className="text-xl font-black text-[#1F3B64]">{text('المرفقات', 'Attachments')}</CardTitle>
                              <CardDescription className="mt-2 text-sm leading-7 text-slate-600">{text('أضف المستندات المتعلقة بالقضية ضمن قائمة منظمة، مع إمكانية رفع أكثر من ملف لنفس النوع عند الحاجة.', 'Add case-related documents through a structured list, with support for multiple files under the same attachment type when needed.')}</CardDescription>
                            </div>
                            <Button type="button" onClick={() => {
                              resetAttachmentDialog();
                              setIsAttachmentDialogOpen(true);
                            }} className="rounded-xl bg-[#1F3B64] px-5 text-white hover:bg-[#183553]">
                              <Plus className="h-4 w-4" />
                              {text('إضافة مرفق', 'Add Attachment')}
                            </Button>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-5 p-5 sm:p-6">
                          <div className="grid gap-3 md:grid-cols-3">
                            <SummaryBox icon={FolderOpen} title={text('عدد المرفقات الحالية', 'Current attachments')} value={`${(formData.attachments.items || []).length} ${text('مرفق', 'item(s)')}`} />
                            <SummaryBox icon={Upload} title={text('الصيغ المسموحة', 'Allowed formats')} value="PDF / JPG / PNG / DOCX" />
                            <SummaryBox icon={CheckCircle2} title={text('المرفقات الناقصة ظاهرياً', 'Visible missing attachments')} value={missingAttachmentOptions.length ? `${missingAttachmentOptions.length} ${text('عنصر', 'item(s)')}` : (text('لا توجد نواقص ظاهرة', 'No visible gaps'))} />
                          </div>

                          {availableAttachmentOptions.length > 0 ? (
                            <div className="rounded-[22px] border border-[#C8A870]/14 bg-white p-4">
                              <p className="text-sm font-bold text-[#1F3B64]">{text('مرفقات موصى بها بحسب البيانات الحالية', 'Suggested attachments based on your current answers')}</p>
                              <div className="mt-3 flex flex-wrap gap-2">
                                {availableAttachmentOptions.map((option) => (
                                  <span key={option.key} className="rounded-full border border-[#C8A870]/25 bg-[#f8f6f1] px-3 py-1.5 text-xs font-medium text-[#1F3B64]">{option.label}</span>
                                ))}
                              </div>
                            </div>
                          ) : null}

                          <div className="rounded-[22px] border border-[#C8A870]/14 bg-white">
                            <div className="hidden grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_120px_140px] gap-4 border-b border-[#C8A870]/10 px-5 py-4 text-xs font-bold uppercase tracking-[0.14em] text-slate-400 md:grid">
                              <span>{text('نوع المرفق', 'Attachment Type')}</span>
                              <span>{text('اسم الملف', 'File Name')}</span>
                              <span>{text('تاريخ الرفع', 'Upload Date')}</span>
                              <span>{text('الإجراءات', 'Actions')}</span>
                            </div>

                            {(formData.attachments.items || []).length > 0 ? (
                              (formData.attachments.items || []).map((item) => (
                                <div key={item.id} className="grid grid-cols-1 gap-4 border-b border-[#C8A870]/10 px-5 py-4 last:border-b-0 md:grid-cols-[minmax(0,1.2fr)_minmax(0,1fr)_120px_140px] md:items-center">
                                  <div>
                                    <p className="font-bold text-[#1F3B64]">{item.displayName || item.typeLabel}</p>
                                    <p className="mt-1 text-xs text-slate-500">{item.typeLabel}</p>
                                  </div>
                                  <div>
                                    <p className="text-sm font-medium text-slate-700">{item.fileName || item.name}</p>
                                    <p className="mt-1 text-xs text-slate-400">{formatBytes(item.size)}</p>
                                  </div>
                                  <p className="text-sm text-slate-500">{toDisplayDate(item.uploadedAt, text('ar-SA', 'en-US'))}</p>
                                  <div className="flex flex-wrap gap-2">
                                    <Button type="button" variant="outline" onClick={() => handlePreviewAttachment(item.id)} disabled={!attachmentPreviews[item.id]?.url} className="rounded-lg border-[#C8A870]/30 px-3 text-[#1F3B64] disabled:cursor-not-allowed disabled:opacity-50">
                                      <Eye className="h-4 w-4" />
                                      {text('معاينة', 'Preview')}
                                    </Button>
                                    <Button type="button" variant="outline" onClick={() => handleRemoveAttachment(item.id)} className="rounded-lg border-red-200 px-3 text-red-600 hover:bg-red-50 hover:text-red-700">
                                      <Trash2 className="h-4 w-4" />
                                      {text('حذف', 'Delete')}
                                    </Button>
                                  </div>
                                </div>
                              ))
                            ) : (
                              <div className="px-5 py-8 text-center">
                                <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-[#1F3B64]/7 text-[#1F3B64]">
                                  <FileUp className="h-6 w-6" />
                                </div>
                                <p className="mt-4 text-sm font-bold text-[#1F3B64]">{text('لا توجد مرفقات مضافة حتى الآن', 'No attachments have been added yet')}</p>
                                <p className="mt-2 text-sm leading-7 text-slate-500">{text('يمكنكم إضافة العقود، الإثباتات، الخطابات، أو أي مستندات داعمة للقضية من خلال زر "إضافة مرفق".', 'Use the "Add Attachment" button to upload contracts, evidence, notices, or any supporting documents related to the case.')}</p>
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>

                      <Dialog open={isAttachmentDialogOpen} onOpenChange={(open) => {
                        setIsAttachmentDialogOpen(open);
                        if (!open) resetAttachmentDialog();
                      }}>
                        <DialogContent className="max-w-2xl rounded-[28px] border border-[#C8A870]/20 bg-white p-0 shadow-[0_30px_80px_rgba(15,23,42,0.16)]">
                          <DialogHeader className="border-b border-[#C8A870]/12 px-6 py-5">
                            <DialogTitle className="text-2xl font-black text-[#1F3B64]">{text('إضافة مرفق', 'Add Attachment')}</DialogTitle>
                            <DialogDescription className="mt-2 text-sm leading-7 text-slate-600">{text('اختَر نوع المرفق، ثم أضف الملف أو الملفات المطلوبة ضمن نفس الفئة بشكل منظم.', 'Choose the attachment type, then add one or more files under the same category in a structured way.')}</DialogDescription>
                          </DialogHeader>

                          <div className="space-y-5 px-6 py-5">
                            <div>
                              <label className="mb-2 block text-sm font-bold text-[#1F3B64]">
                                {text('نوع المرفق', 'Attachment Type')}
                                <RequiredMark />
                              </label>
                              <select value={attachmentForm.typeKey} onChange={(event) => handleAttachmentTypeChange(event.target.value)} className={`flex h-12 w-full rounded-xl border bg-[#fcfbf8] px-3 py-2 text-sm text-slate-700 shadow-sm focus:outline-none focus:ring-2 ${attachmentErrors.typeKey ? 'border-red-300 focus:ring-red-300' : 'border-[#C8A870]/20 focus:ring-[#A27849]'}`}>
                                <option value="">{text('اختر نوع المرفق', 'Select attachment type')}</option>
                                {attachmentOptions.map((group) => (
                                  <optgroup key={group.id} label={group.label}>
                                    {group.options.map((option) => (
                                      <option key={option.key} value={option.key}>{option.label}</option>
                                    ))}
                                  </optgroup>
                                ))}
                              </select>
                              {attachmentForm.typeKey === 'gosi_wages_certificate' ? (
                                <p className="mt-2 text-sm leading-7 text-slate-500">
                                  {text('يمكن استخراجها من التأمينات الاجتماعية وتوضح مدد الاشتراك والأجر المسجل.', 'It can be obtained from GOSI and shows the contribution periods and the registered wage.')}
                                </p>
                              ) : null}
                              {attachmentErrors.typeKey ? <p className="mt-2 text-sm font-medium text-red-500">{attachmentErrors.typeKey}</p> : null}
                            </div>

                            <Field
                              label={text('اسم المرفق', 'Attachment Name')}
                              value={attachmentForm.displayName}
                              onChange={(value) => setAttachmentForm((prev) => ({ ...prev, displayName: value }))}
                              helperText={
                                attachmentForm.typeKey === 'gosi_wages_certificate'
                                  ? (text('يمكن أيضاً تسميته: مشهد مدد وأجور أو برنت التأمينات الاجتماعية.', 'It may also be referred to as: periods and wages statement or GOSI printout.'))
                                  : ''
                              }
                            />

                            <div>
                              <label className="mb-2 block text-sm font-bold text-[#1F3B64]">
                                {text('رفع الملف', 'Upload File')}
                                <RequiredMark />
                              </label>
                              <div
                                role="button"
                                tabIndex={0}
                                onDragOver={(event) => {
                                  event.preventDefault();
                                  setDragActive(true);
                                }}
                                onDragLeave={(event) => {
                                  event.preventDefault();
                                  setDragActive(false);
                                }}
                                onDrop={(event) => {
                                  event.preventDefault();
                                  setDragActive(false);
                                  assignAttachmentFiles(event.dataTransfer.files);
                                }}
                                onClick={() => document.getElementById('labor-attachment-input')?.click()}
                                onKeyDown={(event) => {
                                  if (event.key === 'Enter' || event.key === ' ') {
                                    event.preventDefault();
                                    document.getElementById('labor-attachment-input')?.click();
                                  }
                                }}
                                className={`rounded-[24px] border-2 border-dashed px-6 py-10 text-center transition ${dragActive ? 'border-[#1F3B64] bg-[#1F3B64]/[0.04]' : 'border-[#C8A870]/30 bg-[#fcfbf8] hover:border-[#A27849]/45 hover:bg-[#faf8f2]'}`}
                              >
                                <input id="labor-attachment-input" type="file" multiple accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx,.zip" onChange={(event) => assignAttachmentFiles(event.target.files)} className="hidden" />
                                <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-[22px] bg-[#1F3B64] text-white shadow-[0_15px_30px_rgba(31,59,100,0.18)]">
                                  <Upload className="h-7 w-7" />
                                </div>
                                <p className="mt-4 text-base font-bold text-[#1F3B64]">{text('اسحب الملف هنا أو اضغط للاختيار', 'Drag a file here or click to choose')}</p>
                                <p className="mt-2 text-sm text-slate-500">{text('الصيغ المسموحة: PDF / JPG / PNG / DOCX / XLSX / ZIP - الحد الأعلى 10 ميجابايت لكل ملف', 'Allowed formats: PDF / JPG / PNG / DOCX / XLSX / ZIP - Maximum 10 MB per file')}</p>
                              </div>
                              {attachmentErrors.files ? <p className="mt-2 text-sm font-medium text-red-500">{attachmentErrors.files}</p> : null}
                            </div>

                            {attachmentForm.files.length > 0 ? (
                              <div className="space-y-3">
                                {attachmentForm.files.map((file) => (
                                  <div key={`${file.name}-${file.size}`} className="flex flex-wrap items-center justify-between gap-3 rounded-[20px] border border-[#C8A870]/16 bg-[#fcfbf8] px-4 py-3">
                                    <div>
                                      <p className="font-bold text-[#1F3B64]">{file.name}</p>
                                      <p className="mt-1 text-xs text-slate-500">{formatBytes(file.size)}</p>
                                    </div>
                                    <div className="flex items-center gap-3">
                                      <span className="rounded-full bg-[#A27849]/10 px-3 py-1 text-xs font-bold text-[#A27849]">{text('جاهز للحفظ', 'Ready to save')}</span>
                                      <button type="button" onClick={() => setAttachmentForm((prev) => ({ ...prev, files: prev.files.filter((item) => item !== file) }))} className="rounded-full p-2 text-slate-400 transition hover:bg-slate-100 hover:text-slate-600">
                                        <X className="h-4 w-4" />
                                      </button>
                                    </div>
                                  </div>
                                ))}
                              </div>
                            ) : null}
                          </div>

                          <DialogFooter className="border-t border-[#C8A870]/12 px-6 py-5 sm:justify-between">
                            <Button type="button" variant="outline" onClick={() => {
                              setIsAttachmentDialogOpen(false);
                              resetAttachmentDialog();
                            }} className="rounded-xl border-[#C8A870]/30 px-5 text-[#1F3B64]">
                              {text('إلغاء', 'Cancel')}
                            </Button>
                            <Button type="button" onClick={handleSaveAttachment} disabled={isUploadingAttachment} className="rounded-xl bg-[#1F3B64] px-6 text-white hover:bg-[#183553] disabled:opacity-50">
                              {isUploadingAttachment ? (text('جارٍ رفع المرفق...', 'Uploading...')) : (text('حفظ المرفق', 'Save Attachment'))}
                            </Button>
                          </DialogFooter>
                        </DialogContent>
                      </Dialog>
                    </div>
                  )}

                  {currentStep === 7 && (
                    <div className="space-y-6">
                      <Card className="rounded-[28px] border-[#C8A870]/20 bg-white/95 shadow-[0_14px_45px_rgba(15,23,42,0.05)]">
                        <CardHeader className="border-b border-[#C8A870]/12 pb-5">
                          <CardTitle className="text-2xl font-black text-[#1F3B64]">{text('الإقرار النهائي والإرسال', 'Final Declaration and Submission')}</CardTitle>
                          <CardDescription className="mt-2 text-sm leading-7 text-slate-600">{text('بعد مراجعة الملخص ورفع المرفقات، يرجى تأكيد الإقرار التالي لإرسال الطلب إلى فريق وازن.', 'After reviewing the summary and uploading attachments, please confirm the following declaration to submit the request to the Wazin team.')}</CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-5 p-5 sm:p-6">
                          <div className="rounded-[24px] border border-[#C8A870]/18 bg-[#f8f6f1] p-5 text-sm leading-8 text-slate-700">
                            {text('أقر بأن جميع البيانات والمستندات المقدمة صحيحة بحسب علمي، وأنني اطلعت على ملخص البيانات والوقائع وصادقت عليه كلياً أو جزئياً بحسب اختياري، وأتحمل مسؤولية أي بيانات غير صحيحة أو ناقصة، كما أوافق على قيام شركة وازن للمحاماة والاستشارات القانونية بدراسة الطلب والمستندات والتواصل معي عند الحاجة.', 'I declare that all submitted data and documents are accurate to the best of my knowledge, that I reviewed the summary of data and facts and confirmed it fully or partially based on my selection, that I bear responsibility for any inaccurate or incomplete data, and that I agree to Wazin Law Firm and Legal Consultations reviewing the request and documents and contacting me when needed.')}
                          </div>
                          <label className="flex items-start gap-3 rounded-[24px] border border-[#C8A870]/18 bg-[#1F3B64]/[0.03] p-5">
                            <Checkbox checked={formData.declaration.accepted} onCheckedChange={(checked) => updateSectionField('declaration', 'accepted', !!checked)} className="mt-1 border-[#A27849] data-[state=checked]:bg-[#A27849] data-[state=checked]:text-white" />
                            <span className="text-sm leading-8 text-slate-700">{text('أوافق على الإقرار.', 'I agree to the declaration.')}</span>
                          </label>
                          {errors.declarationAccepted ? <p className="text-sm font-medium text-red-500">{errors.declarationAccepted}</p> : null}
                        </CardContent>
                      </Card>
                    </div>
                  )}
                </CardContent>
              </Card>

              <div className="mt-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm leading-7 text-slate-600">{text('يُحفظ تقدمك تلقائياً أثناء تعبئة الطلب.', 'Your progress is saved automatically while you complete the request.')}</p>
                  {draftSavedMessage ? <p className="mt-2 text-sm font-bold text-[#A27849]">{draftSavedMessage}</p> : null}
                  {errors.submit ? <p className="mt-2 text-sm font-bold text-red-600">{errors.submit}</p> : null}
                </div>
                <div className="flex flex-wrap items-center gap-3">
                  {currentStep > 0 ? (
                    <Button type="button" variant="outline" onClick={handlePrevious} className="rounded-xl border-[#C8A870]/40 px-6 py-6 text-[#183553]">
                      <ArrowRight className="h-4 w-4" />
                      {text('السابق', 'Previous')}
                    </Button>
                  ) : null}

                  <Button type="button" variant="outline" onClick={handleManualSaveDraft} className="rounded-xl border-[#C8A870]/40 px-6 py-6 text-[#A27849]">
                    {text('حفظ كمسودة', 'Save as Draft')}
                  </Button>

                  {currentStep < laborIntakeSteps.length - 1 ? (
                    <Button type="button" onClick={handleNext} className="rounded-xl bg-[#1F3B64] px-6 py-6 text-white hover:bg-[#183553]">
                      {text('التالي', 'Next')}
                      <ArrowLeft className="h-4 w-4" />
                    </Button>
                  ) : (
                    <Button type="submit" disabled={isSubmitting} className="rounded-xl bg-[#A27849] px-7 py-6 text-white hover:bg-[#8d673f]">
                      {isSubmitting ? (text('جارٍ الإرسال...', 'Submitting...')) : text('إرسال الطلب', 'Submit Request')}
                      <CheckCircle2 className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </form>
          </div>
        </section>
      </main>
    </LaborStandaloneLayout>
  );
};

const RequiredMark = () => <span className="mr-1 text-red-500">*</span>;

const Field = ({ label, icon: Icon, error, helperText, required = false, className = '', onChange, ...props }) => (
  <div className={className}>
    <label className="mb-2 block text-[15px] font-extrabold leading-7 text-[#1F3B64] sm:text-base">
      {label}
      {required ? <RequiredMark /> : null}
    </label>
    <div className="relative">
      {Icon ? <Icon className="pointer-events-none absolute right-3 top-3.5 h-4 w-4 text-slate-400" /> : null}
      <Input
        {...props}
        onChange={(event) => onChange(event.target.value)}
        className={`h-12 rounded-xl border-[#C8A870]/20 bg-[#fcfbf8] text-[15px] leading-7 ${Icon ? 'pr-10' : ''} ${error ? 'border-red-300 focus-visible:ring-red-300' : 'focus-visible:ring-[#A27849]'}`}
      />
    </div>
    {helperText && !error ? <p className="mt-2 text-sm leading-7 text-slate-500">{helperText}</p> : null}
    {error ? <p className="mt-2 text-sm font-medium text-red-500">{error}</p> : null}
  </div>
);

const TextAreaField = ({ label, error, helperText, required = false, className = '', onChange, rows = 5, ...props }) => (
  <div className={className}>
    <label className="mb-2 block text-[15px] font-extrabold leading-7 text-[#1F3B64] sm:text-base">
      {label}
      {required ? <RequiredMark /> : null}
    </label>
    <Textarea
      {...props}
      rows={rows}
      onChange={(event) => onChange(event.target.value)}
      className={`rounded-2xl border-[#C8A870]/20 bg-[#fcfbf8] text-[15px] leading-8 ${error ? 'border-red-300 focus-visible:ring-red-300' : 'focus-visible:ring-[#A27849]'}`}
    />
    {helperText && !error ? <p className="mt-2 text-sm leading-7 text-slate-500">{helperText}</p> : null}
    {error ? <p className="mt-2 text-sm font-medium text-red-500">{error}</p> : null}
  </div>
);

const SelectField = ({ label, options, value, onChange, error, required = false, className = '', getOptionLabel = (option) => option }) => (
  <div className={className}>
    <label className="mb-2 block text-[15px] font-extrabold leading-7 text-[#1F3B64] sm:text-base">
      {label}
      {required ? <RequiredMark /> : null}
    </label>
    <select
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className={`flex h-12 w-full rounded-xl border bg-[#fcfbf8] px-3 py-2 text-[15px] text-slate-700 shadow-sm focus:outline-none focus:ring-2 ${error ? 'border-red-300 focus:ring-red-300' : 'border-[#C8A870]/20 focus:ring-[#A27849]'}`}
    >
      {options.map((option) => (
        <option key={option} value={option}>
          {getOptionLabel(option)}
        </option>
      ))}
    </select>
    {error ? <p className="mt-2 text-sm font-medium text-red-500">{error}</p> : null}
  </div>
);

const FileMiniField = ({ label, count = 0, onChange, isArabic = true }) => (
  <div>
    <label className="mb-2 block text-[15px] font-extrabold leading-7 text-[#1F3B64] sm:text-base">{label}</label>
    <label className="flex min-h-12 cursor-pointer items-center justify-between gap-3 rounded-xl border border-[#C8A870]/20 bg-[#fcfbf8] px-3 py-2 text-[15px] text-slate-600 transition hover:border-[#A27849]/45 hover:bg-white">
      <span className="truncate">{count ? (isArabic ? `تم إرفاق ${count} ملف` : `${count} file(s) attached`) : (isArabic ? 'اختيار ملف' : 'Choose file')}</span>
      <Upload className="h-4 w-4 shrink-0 text-[#A27849]" />
      <input
        type="file"
        multiple
        accept=".pdf,.jpg,.jpeg,.png,.docx,.xlsx,.zip"
        className="hidden"
        onChange={(event) => {
          onChange(event.target.files);
          event.target.value = '';
        }}
      />
    </label>
    <p className="mt-2 text-xs leading-6 text-slate-500">PDF / JPG / PNG / DOCX / XLSX / ZIP</p>
  </div>
);

const ChecklistField = ({ title, items, selectedItems, onToggle, getItemLabel = (item) => item }) => (
  <div className="rounded-[28px] border border-[#C8A870]/18 bg-[#f8f6f1] p-6 sm:p-7">
    <div className="mb-5 border-b border-[#C8A870]/14 pb-4">
      <h3 className="text-xl font-black leading-9 text-[#1F3B64] sm:text-2xl">{title}</h3>
    </div>
    <div className="grid gap-3 md:grid-cols-2">
      {items.map((item) => {
        const checked = selectedItems.includes(item);
        return (
          <label key={item} className={`flex cursor-pointer items-start gap-3 rounded-2xl border px-4 py-3 transition ${checked ? 'border-[#A27849]/50 bg-white text-[#1F3B64]' : 'border-transparent bg-white/70 text-slate-700'}`}>
            <Checkbox checked={checked} onCheckedChange={() => onToggle(item)} className="mt-1 border-[#A27849] data-[state=checked]:bg-[#A27849] data-[state=checked]:text-white" />
            <span className="text-[15px] leading-8 sm:text-base">{getItemLabel(item)}</span>
          </label>
        );
      })}
    </div>
  </div>
);

const ClaimBreakdownFields = ({ isArabic, compensation, totals, errors, updateSectionField, updateRow, addRow, removeRow }) => {
  const claims = compensation.claims || [];
  const showFinancialTotal = claims.some((claim) => ['أجور متأخرة', 'رصيد إجازات', 'عمولات'].includes(claim));
  const addLabel = isArabic ? 'إضافة فترة أخرى' : 'Add another period';

  return (
    <div className="mt-5 space-y-5">
      {claims.includes('أجور متأخرة') ? (
        <div className="rounded-[24px] border border-[#C8A870]/18 bg-white p-5">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <h4 className="text-lg font-black text-[#1F3B64]">{isArabic ? 'تفاصيل الأجور المتأخرة' : 'Delayed Wage Details'}</h4>
            <Button type="button" variant="outline" onClick={() => addRow('unpaidWagePeriods', createUnpaidWagePeriod)} className="rounded-xl border-[#C8A870]/35 text-[#1F3B64]">
              <Plus className="h-4 w-4" /> {addLabel}
            </Button>
          </div>
          <div className="space-y-4">
            {(compensation.unpaidWagePeriods || []).map((period, index) => (
              <div key={period.id} className="rounded-[20px] bg-[#f8f6f1] p-4">
                <div className="mb-4 flex items-center justify-between">
                  <p className="text-sm font-bold text-[#1F3B64]">{isArabic ? `الفترة ${index + 1}` : `Period ${index + 1}`}</p>
                  {(compensation.unpaidWagePeriods || []).length > 1 ? (
                    <button type="button" onClick={() => removeRow('unpaidWagePeriods', period.id)} className="text-sm font-bold text-slate-500 transition hover:text-red-500">
                      {isArabic ? 'حذف' : 'Remove'}
                    </button>
                  ) : null}
                </div>
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
                  <SelectField label={isArabic ? 'نوع المطالبة' : 'Claim type'} value={period.periodType} onChange={(value) => updateRow('unpaidWagePeriods', period.id, { periodType: value, month: '', fromDate: '', toDate: '', monthlyWage: '', dailyWage: '' })} options={['شهر كامل', 'أيام محددة']} />
                  {period.periodType === 'شهر كامل' ? (
                    <>
                      <Field label={isArabic ? 'الشهر والسنة' : 'Month and year'} type="month" value={period.month} onChange={(value) => updateRow('unpaidWagePeriods', period.id, { month: value })} />
                      <Field label={isArabic ? 'قيمة الأجر الشهري' : 'Monthly wage'} inputMode="decimal" value={period.monthlyWage} onChange={(value) => updateRow('unpaidWagePeriods', period.id, { monthlyWage: value })} />
                    </>
                  ) : (
                    <>
                      <Field label={isArabic ? 'من تاريخ' : 'From date'} type="date" value={period.fromDate} onChange={(value) => updateRow('unpaidWagePeriods', period.id, { fromDate: value })} />
                      <Field label={isArabic ? 'إلى تاريخ' : 'To date'} type="date" value={period.toDate} onChange={(value) => updateRow('unpaidWagePeriods', period.id, { toDate: value })} />
                      <Field label={isArabic ? 'قيمة الأجر اليومي' : 'Daily wage'} inputMode="decimal" value={period.dailyWage} onChange={(value) => updateRow('unpaidWagePeriods', period.id, { dailyWage: value })} />
                    </>
                  )}
                  <div className="rounded-xl bg-white px-4 py-3 text-sm leading-7 text-slate-600">
                    <p>{isArabic ? 'العدد' : 'Quantity'}: <strong className="text-[#1F3B64]">{wagePeriodQuantity(period)} {period.periodType === 'شهر كامل' ? (isArabic ? 'شهر' : 'month') : (isArabic ? 'يوم' : 'day(s)')}</strong></p>
                    <p>{isArabic ? 'الإجمالي' : 'Total'}: <strong className="text-[#1F3B64]">{formatAmount(wagePeriodTotal(period), isArabic)}</strong></p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          {errors.unpaidWagePeriods ? <p className="mt-3 text-sm font-medium text-red-500">{errors.unpaidWagePeriods}</p> : null}
        </div>
      ) : null}

      {claims.includes('رصيد إجازات') ? (
        <div className="rounded-[24px] border border-[#C8A870]/18 bg-white p-5">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <h4 className="text-lg font-black text-[#1F3B64]">{isArabic ? 'تفاصيل رصيد الإجازات' : 'Leave Balance Details'}</h4>
            <Button type="button" variant="outline" onClick={() => addRow('leaveBalancePeriods', createLeaveBalancePeriod)} className="rounded-xl border-[#C8A870]/35 text-[#1F3B64]">
              <Plus className="h-4 w-4" /> {addLabel}
            </Button>
          </div>
          <div className="space-y-4">
            {(compensation.leaveBalancePeriods || []).map((period, index) => (
              <div key={period.id} className="grid gap-4 rounded-[20px] bg-[#f8f6f1] p-4 md:grid-cols-2 xl:grid-cols-4">
                <Field label={isArabic ? `عدد أيام الإجازة - الرصيد ${index + 1}` : `Leave days - Balance ${index + 1}`} inputMode="numeric" value={period.days} onChange={(value) => updateRow('leaveBalancePeriods', period.id, { days: value })} />
                <Field label={isArabic ? 'الأجر اليومي' : 'Daily wage'} inputMode="decimal" value={period.dailyWage} onChange={(value) => updateRow('leaveBalancePeriods', period.id, { dailyWage: value })} />
                <div className="rounded-xl bg-white px-4 py-3 text-sm leading-7 text-slate-600">
                  <p>{isArabic ? 'إجمالي بدل الإجازات' : 'Leave compensation total'}</p>
                  <strong className="text-[#1F3B64]">{formatAmount(leavePeriodTotal(period), isArabic)}</strong>
                </div>
                {(compensation.leaveBalancePeriods || []).length > 1 ? (
                  <button type="button" onClick={() => removeRow('leaveBalancePeriods', period.id)} className="self-center text-sm font-bold text-slate-500 transition hover:text-red-500">{isArabic ? 'حذف الرصيد' : 'Remove balance'}</button>
                ) : null}
              </div>
            ))}
          </div>
          {errors.leaveBalancePeriods ? <p className="mt-3 text-sm font-medium text-red-500">{errors.leaveBalancePeriods}</p> : null}
        </div>
      ) : null}

      {claims.includes('عمولات') ? (
        <div className="rounded-[24px] border border-[#C8A870]/18 bg-white p-5">
          <div className="mb-5 flex flex-wrap items-center justify-between gap-3">
            <h4 className="text-lg font-black text-[#1F3B64]">{isArabic ? 'تفاصيل العمولات' : 'Commission Details'}</h4>
            <Button type="button" variant="outline" onClick={() => addRow('commissionClaims', createCommissionClaim)} className="rounded-xl border-[#C8A870]/35 text-[#1F3B64]">
              <Plus className="h-4 w-4" /> {isArabic ? 'إضافة عمولة أخرى' : 'Add another commission'}
            </Button>
          </div>
          <div className="space-y-4">
            {(compensation.commissionClaims || []).map((claim, index) => (
              <div key={claim.id} className="grid gap-4 rounded-[20px] bg-[#f8f6f1] p-4 md:grid-cols-2 xl:grid-cols-4">
                <Field label={isArabic ? `وصف العمولة ${index + 1}` : `Commission description ${index + 1}`} value={claim.description} onChange={(value) => updateRow('commissionClaims', claim.id, { description: value })} />
                <Field label={isArabic ? 'فترة الاستحقاق' : 'Entitlement period'} value={claim.entitlementPeriod} onChange={(value) => updateRow('commissionClaims', claim.id, { entitlementPeriod: value })} />
                <Field label={isArabic ? 'مبلغ العمولة' : 'Commission amount'} inputMode="decimal" value={claim.amount} onChange={(value) => updateRow('commissionClaims', claim.id, { amount: value })} />
                <SelectField label={isArabic ? 'هل يوجد مستند مؤيد؟' : 'Supporting document available?'} value={claim.hasEvidence} onChange={(value) => updateRow('commissionClaims', claim.id, { hasEvidence: value })} options={['نعم', 'لا', 'لا أعلم']} />
                {(compensation.commissionClaims || []).length > 1 ? (
                  <button type="button" onClick={() => removeRow('commissionClaims', claim.id)} className="text-sm font-bold text-slate-500 transition hover:text-red-500">{isArabic ? 'حذف العمولة' : 'Remove commission'}</button>
                ) : null}
              </div>
            ))}
          </div>
          {errors.commissionClaims ? <p className="mt-3 text-sm font-medium text-red-500">{errors.commissionClaims}</p> : null}
        </div>
      ) : null}

      {claims.includes('التعويض عن الأضرار') ? (
        <div className="rounded-[24px] border border-[#C8A870]/18 bg-white p-5">
          <h4 className="mb-5 text-lg font-black text-[#1F3B64]">{isArabic ? 'تفاصيل التعويض عن الأضرار' : 'Compensation for Damages Details'}</h4>
          <div className="grid gap-4 md:grid-cols-2">
            <Field label={isArabic ? 'نوع الضرر' : 'Type of damage'} value={compensation.damageClaim.damageType} onChange={(value) => updateSectionField('compensation', 'damageClaim', { ...compensation.damageClaim, damageType: value })} />
            <SelectField label={isArabic ? 'هل توجد مستندات أو أدلة؟' : 'Are documents or evidence available?'} value={compensation.damageClaim.hasEvidence} onChange={(value) => updateSectionField('compensation', 'damageClaim', { ...compensation.damageClaim, hasEvidence: value })} options={['نعم', 'لا', 'لا أعلم']} />
            <TextAreaField className="md:col-span-2" label={isArabic ? 'وصف الضرر' : 'Description of damage'} value={compensation.damageClaim.description} onChange={(value) => updateSectionField('compensation', 'damageClaim', { ...compensation.damageClaim, description: value })} rows={3} />
            <Field label={isArabic ? 'المبلغ المطالب به إن وجد' : 'Claimed amount, if any'} inputMode="decimal" value={compensation.damageClaim.claimedAmount} onChange={(value) => updateSectionField('compensation', 'damageClaim', { ...compensation.damageClaim, claimedAmount: value })} />
          </div>
          <p className="mt-4 text-sm leading-7 text-slate-500">{isArabic ? 'لا يضاف هذا المبلغ إلى الإجمالي التلقائي ويخضع للمراجعة القانونية.' : 'This amount is not added to the automatic total and is subject to legal review.'}</p>
          {errors.damageClaim ? <p className="mt-3 text-sm font-medium text-red-500">{errors.damageClaim}</p> : null}
        </div>
      ) : null}

      {showFinancialTotal ? (
        <div className="rounded-[24px] border border-[#C8A870]/18 bg-[#f8f6f1] p-5">
          <h4 className="mb-4 text-lg font-black text-[#1F3B64]">{isArabic ? 'الملخص المالي الأولي' : 'Initial Financial Summary'}</h4>
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <p className="rounded-xl bg-white px-4 py-3 text-sm text-slate-600">{isArabic ? 'إجمالي الأجور المتأخرة' : 'Total delayed wages'}<strong className="mt-1 block text-[#1F3B64]">{formatAmount(totals.delayedWages, isArabic)}</strong></p>
            <p className="rounded-xl bg-white px-4 py-3 text-sm text-slate-600">{isArabic ? 'إجمالي بدل الإجازات' : 'Total leave compensation'}<strong className="mt-1 block text-[#1F3B64]">{formatAmount(totals.leaveCompensation, isArabic)}</strong></p>
            <p className="rounded-xl bg-white px-4 py-3 text-sm text-slate-600">{isArabic ? 'إجمالي العمولات' : 'Total commissions'}<strong className="mt-1 block text-[#1F3B64]">{formatAmount(totals.commissions, isArabic)}</strong></p>
            <p className="rounded-xl bg-white px-4 py-3 text-sm text-slate-600">{isArabic ? 'الإجمالي التقريبي' : 'Approximate total'}<strong className="mt-1 block text-[#1F3B64]">{formatAmount(totals.approximateTotal, isArabic)}</strong></p>
          </div>
          <p className="mt-4 text-sm leading-7 text-slate-500">{isArabic ? 'هذا الإجمالي تقريبي أولي وقابل للمراجعة القانونية.' : 'This is an initial estimate subject to legal review.'}</p>
        </div>
      ) : null}
    </div>
  );
};

const SummaryBox = ({ icon: Icon, title, value }) => (
  <div className="rounded-[22px] border border-[#C8A870]/14 bg-white p-4">
    <div className="mb-3 flex h-11 w-11 items-center justify-center rounded-2xl bg-[#1F3B64] text-white">
      <Icon className="h-5 w-5" />
    </div>
    <p className="text-sm font-bold text-[#1F3B64]">{title}</p>
    <p className="mt-2 text-sm leading-7 text-slate-600">{value}</p>
  </div>
);

const ToggleBox = ({ title, checked, onChange }) => (
  <label
    className={`flex cursor-pointer items-start gap-3 rounded-2xl border px-4 py-3 transition ${
      checked ? 'border-[#A27849]/50 bg-white text-[#1F3B64]' : 'border-[#C8A870]/14 bg-white text-slate-700'
    }`}
  >
    <Checkbox
      checked={checked}
      onCheckedChange={(value) => onChange(!!value)}
      className="mt-1 border-[#A27849] data-[state=checked]:bg-[#A27849] data-[state=checked]:text-white"
    />
    <span className="text-[15px] leading-8 sm:text-base">{title}</span>
  </label>
);

export default LaborIntakePage;
